/* eslint-disable no-unused-vars */
export type PersonType = 'artist' | 'staff' | 'guest' | 'support';

// User role within an organization
export type UserRole = 'owner' | 'admin' | 'programmer' | 'engineer' | 'host';

// Internal user (staff member who logs in)
export type User = {
    id: string;
    account_id: string;
    email: string;
    name: string;
    role: UserRole;
    is_active: boolean;
    last_login_at: string | null;
    created_at: string;
    updated_at: string;
};

export type PersonSocialLink = {
    label: string;
    url: string;
};

export type PersonSocials = {
    instagram?: string | null;
    soundcloud?: string | null;
    bandcamp?: string | null;
    custom?: PersonSocialLink[];
};

// Per-person scheduling constraints for staff assignment, stored in
// people.metadata.staffing.
export type StaffingConstraints = {
    max_consecutive_hours?: number;
    max_daily_hours?: number;
    min_break_minutes?: number;
    /** If set, this person may only be assigned to shifts at these places. */
    allowed_place_ids?: string[];
};

export type Person = {
    id: string;
    slug: string;
    name: string | null;
    type: PersonType;
    account_id?: string | null;
    pronouns?: string | null;
    email: string;
    phone?: string | null;
    bio?: string | null;
    picture_url?: string | null;
    image_focus?: string | null;
    socials?: PersonSocials;
    invite_token?: string | null;
    is_resident?: boolean;
    is_public?: boolean;
    is_collective?: boolean;
    is_active: boolean;
    staffing?: StaffingConstraints | null;
    profile_completed_at?: string | null;
    created_at: string;
    updated_at: string;
};

export type PersonCreate = {
    name?: string | null;
    type?: PersonType;
    slug?: string;
    pronouns?: string | null;
    email: string;
    phone?: string | null;
    bio?: string | null;
    picture_url?: string | null;
    socials?: PersonSocials;
    is_resident?: boolean;
    is_public?: boolean;
    is_collective?: boolean;
    staffing?: StaffingConstraints | null;
    generateInviteLink?: boolean;
};

export type PersonUpdate = Partial<Omit<Person, 'id' | 'created_at' | 'updated_at'>> & {
    slug?: string;
    generateInviteLink?: boolean;
};

export type ListPeopleFilters = {
    type?: PersonType;
    is_active?: boolean;
    is_public?: boolean;
    is_collective?: boolean;
    limit?: number;
    /**
     * Case-insensitive substring match against name, slug and email.
     * listPeople has always honoured this; it was just missing from the type,
     * so every caller passing it was a type error.
     */
    query?: string;
};

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskType = 'booking' | 'preparation' | 'follow_up' | 'technical';

export type Task = {
    id: string;
    title: string;
    type: TaskType;
    description: string | null;
    status: TaskStatus;
    priority: TaskPriority;
    due_date: string | null;
    parent_task_id: string | null;
    sort_order: number;
    template_task_key: string | null;
    related_happening_id: string | null;
    assignee_role: string | null;
    assigned_to_id: string | null;        // External person (artist/contractor)
    assigned_to_user_id: string | null;   // Internal staff user
    reminder_enabled?: boolean;           // Whether reminders are enabled for this task
    reminder_offset_hours?: number;       // Hours before/after due date to send reminder
    reminder_sent_at?: string | null;     // When the reminder was sent (deprecated - use task_reminders table)
    created_at: string;
    updated_at: string;
};

export type TaskCreate = {
    title: string;
    type: TaskType;
    description?: string | null;
    status?: TaskStatus;
    priority?: TaskPriority;
    due_date?: string | null;
    parent_task_id?: string | null;
    sort_order?: number;
    template_task_key?: string | null;
    related_happening_id?: string | null;
    assignee_role?: string | null;
    assigned_to_id?: string | null;        // External person
    assigned_to_user_id?: string | null;   // Internal staff
    reminder_enabled?: boolean;            // Whether reminders are enabled for this task
    reminder_offset_hours?: number;        // Hours before/after due date to send reminder
};

export type TaskUpdate = Partial<Omit<Task, 'id' | 'created_at' | 'updated_at'>>;

export type TaskFilters = {
    status?: TaskStatus;
    parent_task_id?: string | null;
    related_happening_id?: string;
    assigned_to_id?: string;
    assigned_to_user_id?: string;
    include_subtasks?: boolean;
    query?: string;
    due_date_gte?: string;
    due_date_lte?: string;
    limit?: number;
    offset?: number;
};

export type HappeningStatus = 'draft' | 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
export type HappeningType = 'show' | 'event' | 'recording' | 'festival' | 'mix' | 'performance' | 'tech';
export type RadioShowType = 'resident' | 'guest' | 'takeover';

export type HappeningArtist = {
    person_id: string;
    name: string | null; // Can be null for stub artists who haven't filled in their name yet
    slug: string;
    picture_url?: string | null;
    role?: string;
    sort_order?: number;
};

export type GearRequest = {
    thing_id: string;
    name: string;
    quantity: number;
    notes?: string | null;
};

export type ArchiveLink =
    | { type: 'soundcloud'; url: string }
    | { type: 'custom'; name: string; url: string; icon_slug?: string | null };

export type TracklistItem = {
    artist: string;
    title: string;
    timestamp?: string | null; // "HH:MM:SS" format, optional
};

export type HappeningMetadata = {
    show_type?: RadioShowType;
    genres?: string[];
    artists?: HappeningArtist[];
    gear_requests?: GearRequest[];
    duration_minutes?: number;
    episode_number?: number;
    stub_token?: string | null;
    place_id?: string | null;
    image_url?: string | null; // Stored in metadata until migration is applied
    archive_links?: ArchiveLink[];
    /** Running order inside the archived recording, for takeovers. See show-sets.ts. */
    sets?: ShowSet[];
    tracklist?: TracklistItem[];
    // Display flags
    is_auto_generated_title?: boolean;
    is_staff_pick?: boolean;
    image_fit?: string;
    image_focus?: string;
    // Artist submission
    rider_notes?: string | null;
    // Template-related
    template_id?: string;
    required_roles?: TemplateRole[];
    // Post-show
    post_show_feedback_url?: string;
    // Generated social announcement cards, keyed by template id (e.g. instagram, soundcloud)
    social_cards?: Record<string, string> | null;
    /**
     * Which invite-email strategy was last chosen for this show. Persisted so reopening
     * the edit dialog restores the choice instead of resetting it, and so the mix of
     * combined-vs-separate invites can be measured without an analytics vendor.
     */
    invite_email_strategy?: 'combined' | 'separate' | 'none';
    /** When that choice was made, ISO 8601. */
    invite_email_strategy_set_at?: string;
};

export type Happening = {
    id: string;
    slug: string;
    title: string;
    type: HappeningType;
    description: string | null;
    image_url: string | null;
    start_time: string | null;
    end_time: string | null;
    place_id: string | null;
    timezone: string | null;
    status: HappeningStatus;
    is_public: boolean;
    metadata: HappeningMetadata;
    series_id: string | null;
    series_instance_date: string | null; // ISO date (YYYY-MM-DD)
    program_id: string | null;
    account_id?: string | null;
    /**
     * Account-defined labels, joined at read time by the list/detail read paths.
     * Undefined means "not joined on this read", not "no tags" — don't persist it.
     */
    tags?: Tag[];
    created_at: string;
    updated_at: string;
};

/**
 * Happening with joined artist participants from the happening_participants table.
 * Used when querying happenings with includeArtists: true
 */
export type HappeningWithArtists = Happening & {
    happening_participants?: Array<{
        id: string;
        person_id: string;
        role: string;
        sort_order: number;
        people: { id: string; name: string | null; slug: string; image_url: string | null } | null;
    }>;
    /**
     * Every participant on the happening, not just the artists that
     * `happening_participants` is narrowed to. Populated by listHappenings so the
     * happenings list/calendar can show programmer initials and filter by
     * programmer without a second, separately-cached participants query — that
     * query keyed on nothing and went stale against the visible page, which is
     * why initials came and went between refreshes.
     */
    participants?: HappeningParticipantSummary[];
};

/**
 * The participant fields the happenings list joins in. A trimmed
 * `HappeningParticipant` — no metadata or created_at, since the embed does not
 * select them.
 */
export type HappeningParticipantSummary = {
    id: string;
    happening_id: string;
    person_id: string | null;
    user_id: string | null;
    role: string;
    sort_order: number;
    person_name: string | null;
    user_name: string | null;
};

/**
 * Helper to extract artists from a happening with joined participants.
 * Falls back to metadata.artists if no participants are joined (for backward compatibility).
 *
 * @param happening - A happening that may have joined happening_participants
 * @returns Array of HappeningArtist objects with fresh data from the people table
 */
export function getArtistsFromHappening(happening: HappeningWithArtists): HappeningArtist[] {
    // The fallback turns on when there is no ARTIST participant, not when there are no
    // participants at all. Keying it on the whole list meant a happening carrying any
    // other role — a template's engineer, or a rotating programmer — reported zero
    // artists and never consulted metadata, hiding artists that were really there.
    const artistParticipants = (happening.happening_participants ?? []).filter(
        p => p.role === 'artist' && p.people
    );
    if (artistParticipants.length > 0) {
        return artistParticipants
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map(p => ({
                person_id: p.person_id,
                name: p.people!.name,
                slug: p.people!.slug,
                picture_url: p.people!.image_url,
                sort_order: p.sort_order,
            }));
    }
    // Fallback to metadata during transition
    return happening.metadata?.artists || [];
}

export type HappeningCreate = {
    title?: string;
    type: HappeningType;
    slug?: string;
    description?: string | null;
    image_url?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    status?: HappeningStatus;
    is_public?: boolean;
    metadata?: HappeningMetadata;
    generateStubLink?: boolean;
    series_id?: string | null;
    series_instance_date?: string | null;
    program_id?: string | null;
};

export type HappeningUpdate = Partial<Omit<Happening, 'id' | 'created_at' | 'updated_at'>> & {
    generateStubLink?: boolean;
};

export type HappeningFilters = {
    status?: HappeningStatus;
    status_not?: HappeningStatus | string;
    type?: HappeningType;
    is_public?: boolean;
    start_time_gte?: string;
    start_time_lte?: string;
    program_id?: string | null;
    query?: string;
    limit?: number;
    offset?: number;
    order?: 'asc' | 'desc';
    /** When true, join happening_participants with role='artist' and people table for fresh artist data */
    includeArtists?: boolean;
    /**
     * Keep only happenings that have a matching participant. `participant_role`
     * may be used alone (any happening filling that role) or together with one
     * of the id filters (that specific person/user in that role).
     *
     * This has to run in the database. Filtering in the browser only ever saw
     * the current page, so a programmer whose shows sat on page 3 could not
     * find them by filtering.
     */
    participant_person_id?: string;
    participant_user_id?: string;
    participant_role?: string;
    /**
     * Set false to skip the participant/people/users join. Callers that only
     * need happening columns — global search, for one — avoid dragging the
     * whole cast of every happening across the wire. metadata.artists then
     * falls back to the stored snapshot instead of being rebuilt from people.
     */
    withParticipants?: boolean;
};

// Re-export Place and Thing types from types.ts to match database schema
export type { Place } from './types.js';
export type { Thing } from './types.js';
export type { ThingTransaction, ThingTransactionCreate, ThingInventoryState, ThingTrackingMode, ThingTransactionType } from './types.js';

// ============================================================================
// TEMPLATES & WORKFLOWS
// ============================================================================

export type RequirementSource = 'manual' | 'artist_form' | 'template';
export type RequirementStatus = 'requested' | 'confirmed' | 'prepared' | 'returned';

export type HappeningRequirement = {
    id: string;
    happening_id: string;
    thing_id: string;
    quantity: number;
    notes: string | null;
    requested_by_participant_id: string | null;
    request_source: RequirementSource;
    status: RequirementStatus;
    created_at: string;
};

export type HappeningRequirementCreate = {
    happening_id: string;
    thing_id: string;
    quantity?: number;
    notes?: string | null;
    requested_by_participant_id?: string | null;
    request_source?: RequirementSource;
    status?: RequirementStatus;
};

export type HappeningRequirementUpdate = Partial<Omit<HappeningRequirement, 'id' | 'created_at' | 'happening_id'>>;

// Template role definition
export type AssigneeSource = 'users' | 'people';

export type TemplateRole = {
    role: string;
    min_count: number;
    max_count: number | null;
    assignee_source?: AssigneeSource; // 'users' for staff, 'people' for artists
    person_type?: PersonType; // Deprecated: use assignee_source instead
};

// Template default requirement
export type TemplateDefaultRequirement = {
    thing_type: string;
    quantity: number;
};

export type HappeningTemplate = {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    happening_type: HappeningType;
    account_id: string | null;
    required_roles: TemplateRole[];
    default_duration_minutes: number;
    default_status: HappeningStatus;
    default_is_public: boolean;
    default_requirements: TemplateDefaultRequirement[];
    is_active: boolean;
    is_system: boolean;
    metadata: Record<string, unknown>;
    created_at: string;
    updated_at: string;
};

export type HappeningTemplateCreate = {
    slug: string;
    name: string;
    description?: string | null;
    happening_type: HappeningType;
    required_roles?: TemplateRole[];
    default_duration_minutes?: number;
    default_status?: HappeningStatus;
    default_is_public?: boolean;
    default_requirements?: TemplateDefaultRequirement[];
    metadata?: Record<string, unknown>;
};

export type HappeningTemplateUpdate = Partial<Omit<HappeningTemplate, 'id' | 'created_at' | 'updated_at' | 'is_system'>>;

export type HappeningTemplateFilters = {
    happening_type?: HappeningType;
    is_active?: boolean;
    include_system?: boolean;
};

// Task definition within a template
export type TaskDefinition = {
    id: string;
    template_id: string;
    task_key: string;
    title: string;
    description: string | null;
    type: TaskType;
    priority: TaskPriority;
    due_offset_days: number;
    due_offset_hours: number;
    assignee_role: string | null;
    depends_on: string[];
    auto_complete_on_happening_status: HappeningStatus | null;
    auto_create_on_happening_status: HappeningStatus;
    subtask_keys: string[];
    sort_order: number;
    is_required: boolean;
    reminder_enabled: boolean;
    reminder_offset_hours: number;
    metadata: Record<string, unknown>;
    created_at: string;
    updated_at: string;
};

export type TaskDefinitionCreate = {
    template_id: string;
    task_key: string;
    title: string;
    description?: string | null;
    type?: TaskType;
    priority?: TaskPriority;
    due_offset_days?: number;
    due_offset_hours?: number;
    assignee_role?: string | null;
    depends_on?: string[];
    auto_complete_on_happening_status?: HappeningStatus | null;
    auto_create_on_happening_status?: HappeningStatus;
    subtask_keys?: string[];
    sort_order?: number;
    is_required?: boolean;
    reminder_enabled?: boolean;
    reminder_offset_hours?: number;
    metadata?: Record<string, unknown>;
};

export type TaskDefinitionUpdate = Partial<Omit<TaskDefinition, 'id' | 'created_at' | 'updated_at' | 'template_id'>>;

// Participant submission (artist form response)
export type ParticipantGearRequest = {
    thing_type: string;
    quantity: number;
    notes?: string;
};

export type ParticipantSubmission = {
    id: string;
    happening_participant_id: string;
    account_id: string | null;
    gear_requests: ParticipantGearRequest[];
    rider_notes: string | null;
    dietary_restrictions: string | null;
    arrival_time: string | null;
    departure_time: string | null;
    submitted_at: string;
    metadata: Record<string, unknown>;
    created_at: string;
    updated_at: string;
};

export type ParticipantSubmissionCreate = {
    happening_participant_id: string;
    gear_requests?: ParticipantGearRequest[];
    rider_notes?: string | null;
    dietary_restrictions?: string | null;
    arrival_time?: string | null;
    departure_time?: string | null;
    metadata?: Record<string, unknown>;
};

export type ParticipantSubmissionUpdate = Partial<Omit<ParticipantSubmission, 'id' | 'created_at' | 'updated_at' | 'happening_participant_id' | 'submitted_at'>>;

// Happening participant (linking people/users to happenings with roles)
export type HappeningParticipant = {
    id: string;
    happening_id: string;
    person_id: string | null; // External person (artist/contractor)
    user_id: string | null;   // Internal user (staff)
    role: string;
    sort_order: number;
    metadata: Record<string, unknown>;
    created_at: string;
    // Joined fields for display
    person_name?: string | null;
    user_name?: string | null;
};

export type HappeningParticipantCreate = {
    happening_id: string;
    person_id?: string | null; // For external people (artists)
    user_id?: string | null;   // For internal users (staff)
    role: string;
    sort_order?: number;
    metadata?: Record<string, unknown>;
};

// Place availability check result
export type PlaceAvailabilityResult = {
    available: boolean;
    conflicts: {
        happening_id: string;
        happening_title: string;
        start_time: string;
        end_time: string;
    }[];
    capacity: number | null;
    concurrent_count: number;
};

// Apply template result
export type ApplyTemplateResult = {
    happening: Happening;
    tasks: Task[];
    requirements: HappeningRequirement[];
    participants: HappeningParticipant[];
};

// ============================================================================
// RECURRING HAPPENINGS (SERIES)
// ============================================================================

export type DayOfWeek = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';
export type WeekOfMonth = 1 | 2 | 3 | 4 | -1; // -1 = last week

/** Canonical ordering for weeks within a month. Last (-1) always sorts after 4th. */
export const WEEK_ORDER: readonly WeekOfMonth[] = [1, 2, 3, 4, -1];

type MonthlyWeeks = {
    /**
     * @deprecated Legacy single-week form. Still accepted on input because org presets
     * persisted in accounts.settings.recurrence_presets use it.
     */
    weekOfMonth?: WeekOfMonth;
    /** Multi-week form. Wins over `weekOfMonth` when non-empty. */
    weeksOfMonth?: WeekOfMonth[];
};

export type RecurrencePattern =
    | { frequency: 'weekly'; dayOfWeek: DayOfWeek }
    | ({ frequency: 'monthly'; dayOfWeek: DayOfWeek } & MonthlyWeeks)
    | ({ frequency: 'bi-monthly'; dayOfWeek: DayOfWeek } & MonthlyWeeks);

export type MonthlyRecurrencePattern = Extract<RecurrencePattern, { frequency: 'monthly' | 'bi-monthly' }>;

/**
 * Resolve either the legacy `weekOfMonth` or the new `weeksOfMonth` into a sorted,
 * deduped week list. Single source of truth for reading a monthly pattern.
 */
export const resolveWeeksOfMonth = (pattern: MonthlyRecurrencePattern): WeekOfMonth[] => {
    const raw: WeekOfMonth[] = pattern.weeksOfMonth?.length
        ? pattern.weeksOfMonth
        : pattern.weekOfMonth != null
            ? [pattern.weekOfMonth]
            : [];
    const seen = new Set<WeekOfMonth>(raw.filter((w): w is WeekOfMonth => WEEK_ORDER.includes(w)));
    return WEEK_ORDER.filter((w) => seen.has(w));
};

const WEEK_LABELS: Record<string, string> = { '1': '1st', '2': '2nd', '3': '3rd', '4': '4th', '-1': 'last' };

/** Render a week list as "2nd, 4th & last". Shared by the DAL and the admin picker. */
export const formatWeeksOfMonth = (weeks: WeekOfMonth[]): string => {
    const parts = weeks.map((w) => WEEK_LABELS[String(w)] ?? String(w));
    if (parts.length <= 1) return parts.join('');
    return `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1]}`;
};

export type SeriesParticipant = {
    person_id?: string | null; // For external people (artists)
    user_id?: string | null;   // For internal users (staff)
    role: string; // 'programmer', 'engineer', 'producer', etc.
};

/** One member of a round-robin pool. Exactly one of person_id / user_id is set. */
export type SeriesRotationMember = {
    person_id?: string | null; // External person
    user_id?: string | null;   // Internal user
};

/**
 * A role whose holder rotates across generated instances.
 * Array order is rotation order: occurrence i gets members[(i + start_offset) % members.length].
 *
 * start_offset exists so two series over the same dates (e.g. a 19:00 and a 20:00
 * slot sharing one pool) do not hand both slots to the same person every week.
 * Without it both runs start at index 0 and collide on every single date.
 */
export type SeriesRotation = {
    role: string;                    // e.g. 'programmer'
    members: SeriesRotationMember[];
    start_offset?: number;           // Whole steps to advance the pool before the first instance
};

export type HappeningSeries = {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    account_id: string | null;
    template_id: string | null;
    happening_type: HappeningType;
    default_title: string | null;
    default_description: string | null;
    default_duration_minutes: number;
    default_place_id: string | null;
    default_is_public: boolean;
    default_status: HappeningStatus;
    default_metadata: HappeningMetadata;
    default_show_type: RadioShowType | null;  // For radio shows
    starting_episode_number: number;          // Episode number to start from
    default_participants: SeriesParticipant[]; // Default role assignments to carry over
    default_artist_ids: string[];             // Default artist IDs to carry over
    rotating_participants: SeriesRotation[];  // Role pools cycled across generated instances
    /**
     * Whether editing one instance may be applied to this and all future instances.
     * Off by default: a guest-show series is independent shows on a shared schedule, and
     * "apply to future" there silently copies one guest's details over every later date.
     */
    allow_bulk_edits: boolean;
    rrule: string;
    recurrence_start_date: string; // ISO date (YYYY-MM-DD)
    recurrence_end_date: string;   // ISO date (YYYY-MM-DD)
    default_start_time: string;    // HH:MM:SS
    timezone: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
};

export type RecurrencePreset = {
    name: string;
    pattern: RecurrencePattern;
};

export type HappeningSeriesCreate = {
    name: string;
    slug?: string;
    description?: string | null;
    template_id?: string | null;
    happening_type: HappeningType;
    default_title?: string | null;
    default_description?: string | null;
    default_duration_minutes?: number;
    default_place_id?: string | null;
    default_is_public?: boolean;
    default_status?: HappeningStatus;
    default_metadata?: HappeningMetadata;
    default_show_type?: RadioShowType | null;  // For radio shows
    starting_episode_number?: number;          // Episode number to start from (defaults to 1)
    default_participants?: SeriesParticipant[]; // Default role assignments to carry over
    default_artist_ids?: string[];              // Default artist IDs to carry over
    rotating_participants?: SeriesRotation[];   // Role pools cycled across generated instances
    allow_bulk_edits?: boolean;                 // Defaults to false — edits apply to one instance
    recurrence_pattern: RecurrencePattern;
    recurrence_start_date: string;
    recurrence_end_date: string;
    default_start_time: string;
    timezone?: string;
    generate_instances?: boolean; // Whether to auto-generate happenings on creation
};

export type HappeningSeriesUpdate = Partial<Omit<HappeningSeries, 'id' | 'created_at' | 'updated_at' | 'account_id'>>;

export type HappeningSeriesFilters = {
    happening_type?: HappeningType;
    is_active?: boolean;
    template_id?: string;
};

export type GenerateSeriesInstancesOptions = {
    from_date?: string; // Generate from this date (defaults to today or series start)
    to_date?: string;   // Generate up to this date (defaults to series end_date)
};

export type BatchGenerateOptions = {
    recurrence_pattern: RecurrencePattern;
    start_date: string;
    end_date: string;
    start_time: string;
    timezone?: string;
    happening_data: HappeningCreate;
    role_assignments?: RoleAssignment[]; // For template-based batch creation
};

export type DalConfig = {
    supabaseUrl?: string;
    supabaseAnonKey?: string;
    schema?: string; // Database schema name (defaults to 'littlebooker')
    /** Which storage backend uploads use. */
    storageProvider?: 'r2' | 'memory';
    /** R2 presign endpoint base — required when storageProvider='r2' in the browser. */
    apiBaseUrl?: string;
    /** Returns the current auth token for the presign API call. */
    getAuthToken?: () => string | null;
};

export type MediaCategory = 'artist_photo' | 'happening_photo' | 'account_logo' | 'thing_photo' | 'place_layout' | 'social_card';

export type UploadMediaOptions = {
    category: MediaCategory;
    identifier?: string;
    fileName?: string;
    /** Account slug for multi-tenant storage path prefixing */
    accountSlug?: string;
    /**
     * When set, uploads are presigned via the token-scoped endpoint instead of
     * the JWT-gated one. Used by artist invite / show stub-link flows where the
     * uploader is anonymous but holds a valid invite_token or stub_token.
     */
    auth?: {
        token: string;
        entityId: string;
    };
};

export type UploadMediaResult = {
    url: string;
    path: string;
    provider: 'memory' | 'r2';
    contentType: string;
};

type SupabasePeopleRow = {
    id: string;
    slug: string;
    name: string;
    type: PersonType;
    account_id: string | null;
    email: string | null;
    phone: string | null;
    bio: string | null;
    image_url: string | null; // Database column name
    social_links: unknown; // Database column name
    profile_completion_token: string | null; // Database column name
    profile_completed_at: string | null;
    metadata: Record<string, unknown> | null;
    is_public: boolean;
    is_collective: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
};

type SupabaseHappeningsRow = {
    id: string;
    slug: string;
    title: string;
    type: HappeningType;
    description: string | null;
    image_url?: string | null; // Optional - may not exist in DB until migration is applied
    start_time: string | null;
    end_time: string | null;
    place_id: string | null;
    timezone: string | null;
    is_public: boolean;
    status: HappeningStatus;
    metadata: Record<string, unknown> | null;
    series_id?: string | null;
    series_instance_date?: string | null;
    program_id?: string | null;
    account_id?: string | null;
    created_at: string;
    updated_at: string;
};

// Supabase context - declared early so randomId can check it
type SupabaseContext = {
    restUrl: string;
    headers: Record<string, string>;
    schema: string;
    // Service-role key, when configured (API server only). Lets supabaseRequest
    // honour the per-request service-role preference — the index.ts equivalent of
    // getClient() returning the admin client (§5.9). Absent in the browser.
    adminApiKey?: string;
};
// Export supabaseContext setter for the new client system
export let supabaseContext: SupabaseContext | null = null;
export const setSupabaseContext = (context: SupabaseContext | null) => {
    supabaseContext = context;
};

// Generate a UUID v4 for Supabase, or a short ID for in-memory testing
const generateUUID = (): string => {
    // crypto.randomUUID is available in modern browsers and Node 19+
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback UUID v4 generator
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

// Normalize a timestamp to a whole minute (strip stray seconds/ms) so happening
// start/end times are always clean regardless of how they were produced. Keeps
// staff-shift boundaries exact.
const toMinuteIso = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return iso ?? null;
    return new Date(Math.floor(t / 60000) * 60000).toISOString();
};

const randomId = () => (supabaseContext ? generateUUID() : `id_${Math.random().toString(36).slice(2, 10)}`);
const randomToken = () => secureToken();

const slugify = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-');

/**
 * Slug for a resident show episode: show name (truncated) + episode number + date.
 * Shared by createHappening and renumberSeriesEpisodes so a renumbered episode gets
 * exactly the slug it would have been created with.
 */
export const buildResidentEpisodeSlug = (
    title: string,
    episodeNumber: number | null | undefined,
    startTime: string | null | undefined,
): string => {
    const truncatedTitle = title.length > 30 ? title.slice(0, 30) : title;
    const episodePart = episodeNumber ? `-ep${episodeNumber}` : '';
    const datePart = startTime
        ? `-${new Date(startTime).toISOString().slice(0, 10).replace(/-/g, '')}`
        : `-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
    return slugify(`${truncatedTitle}${episodePart}${datePart}`);
};

// Short, human-typeable lookup code for things. 28-symbol alphabet: digits 2-9
// + consonants — no vowels (avoids spelling accidental words) and no
// look-alikes (0/O, 1/I/L). Uniqueness is GUARANTEED by the DB unique index
// (account_id, code); this generator only needs to make collisions rare. See
// createThing for the generate-and-retry loop.
const THING_CODE_ALPHABET = '23456789BCDFGHJKMNPQRSTVWXYZ';

export const generateThingCode = (length = 5): string => {
    const alphabet = THING_CODE_ALPHABET;
    let out = '';
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(length);
        crypto.getRandomValues(bytes);
        for (let i = 0; i < length; i += 1) {
            out += alphabet[bytes[i] % alphabet.length];
        }
    } else {
        for (let i = 0; i < length; i += 1) {
            out += alphabet[Math.floor(Math.random() * alphabet.length)];
        }
    }
    return out;
};

// True only when a write failed because the thing `code` collided (not some
// other unique constraint such as slug), so we know to retry with a fresh code.
const isDuplicateThingCodeError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message : String(err);
    return /things_account_code_unique/i.test(msg)
        || ((/duplicate key|already exists|23505/i.test(msg)) && /\bcode\b/i.test(msg));
};

// In-memory equivalent of the DB uniqueness guarantee (used when Supabase is
// not configured, e.g. tests). Widens length if the local store saturates.
const generateUniqueThingCodeLocal = (): string => {
    const existing = new Set(store.things.map((t) => t.code));
    let length = 5;
    for (let attempt = 0; attempt < 200; attempt += 1) {
        const candidate = generateThingCode(length);
        if (!existing.has(candidate)) return candidate;
        if (attempt > 0 && attempt % 20 === 0) length += 1;
    }
    return generateThingCode(length + 1);
};

const now = () => new Date().toISOString();

const delay = (ms = 120) => new Promise((resolve) => setTimeout(resolve, ms));

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const store: {
    people: Person[];
    tasks: Task[];
    happenings: Happening[];
    happening_series: HappeningSeries[];
    places: Place[];
    things: Thing[];
    thing_transactions: ThingTransaction[];
} = {
    people: [],
    tasks: [],
    happenings: [],
    happening_series: [],
    places: [],
    things: [],
    thing_transactions: [],
};

/**
 * Reset the in-memory store (useful for testing)
 */
export const resetStore = () => {
    store.people = [];
    store.tasks = [];
    store.happenings = [];
    store.happening_series = [];
    store.places = [];
    store.things = [];
    store.thing_transactions = [];
};

const seedData = () => {
    if (store.people.length > 0) {
        return;
    }

    const created = now();

    store.people.push(
        {
            id: randomId(),
            slug: 'midori-takada',
            name: 'Midori Takada',
            type: 'artist',
            pronouns: 'she/her',
            email: 'booking@midoritakada.com',
            phone: null,
            bio: 'Minimal percussionist working between Tokyo and Berlin.',
            picture_url: 'https://images.littlebooker.local/midori-hero.jpg',
            socials: {
                instagram: 'midoritakada',
                soundcloud: 'midori-takada',
                bandcamp: null,
            },
            invite_token: null,
            is_active: true,
            created_at: created,
            updated_at: created,
        },
        {
            id: randomId(),
            slug: 'littlebooker-resident',
            name: 'Littlebooker Resident',
            type: 'staff',
            email: 'resident@littlebooker.fm',
            pronouns: 'they/them',
            phone: '+1 646 555 0145',
            bio: null,
            picture_url: null,
            socials: undefined,
            invite_token: null,
            is_active: true,
            created_at: created,
            updated_at: created,
        },
    );

    store.tasks.push(
        {
            id: randomId(),
            title: 'Confirm hospitality for touring artist',
            type: 'booking',
            description: 'Coordinate hotel and per diem for next Friday show.',
            status: 'todo',
            priority: 'high',
            due_date: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
            created_at: created,
            updated_at: created,
        },
        {
            id: randomId(),
            title: 'Collect stems for live mix',
            type: 'technical',
            description: null,
            status: 'in_progress',
            priority: 'medium',
            due_date: null,
            created_at: created,
            updated_at: created,
        },
    );

    store.happenings.push(
        {
            id: randomId(),
            slug: 'weekly-rinse-2024-02-18',
            title: 'Weekly Rinse',
            type: 'show',
            description: 'Live broadcast from the Brooklyn studio.',
            image_url: 'https://images.littlebooker.local/weekly-rinse.jpg',
            start_time: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toISOString(),
            end_time: null,
            status: 'confirmed',
            is_public: true,
            metadata: {
                show_type: 'resident',
                genres: ['House', 'Disco'],
                duration_minutes: 120,
            },
            series_id: null,
            series_instance_date: null,
            created_at: created,
            updated_at: created,
        },
        {
            id: randomId(),
            slug: 'archival-mix-march',
            title: 'Archival Mix Series',
            type: 'mix',
            description: null,
            image_url: null,
            start_time: null,
            end_time: null,
            status: 'draft',
            is_public: false,
            metadata: {},
            series_id: null,
            series_instance_date: null,
            created_at: created,
            updated_at: created,
        },
    );

    store.places.push(
        {
            id: randomId(),
            slug: 'studio-a',
            name: 'Studio A',
            type: 'room',
            metadata: { capacity: 6, gear: ['Xone 92', 'CDJ-3000', 'Modular rack'] },
        },
        {
            id: randomId(),
            slug: 'warehouse-stage',
            name: 'Warehouse Stage',
            type: 'stage',
            metadata: { capacity: 400, location: 'Industry City' },
        },
    );

    store.things.push(
        {
            id: randomId(),
            slug: 'cdj-3000-pair-a',
            name: 'CDJ 3000 Pair',
            category: 'equipment',
            status: 'available',
            metadata: { serials: ['XDJA2981', 'XDJA2982'] },
        },
        {
            id: randomId(),
            slug: 'ableton-suite-license',
            name: 'Ableton Suite License',
            category: 'software',
            status: 'in_use',
            metadata: { seats: 4 },
        },
    );
};

seedData();

let currentConfig: DalConfig | null = null;

// Account context for multi-tenant operations
// Import from account-context to avoid circular dependencies
import { setCurrentAccountId as setAccountContextId, getCurrentAccountId as getAccountContextId, setCurrentAccountToken, getCurrentAccountToken, getServiceRolePreference } from './account-context.js';
import { createPendingCompletionNotification } from './pending-completion-notifications.js';
import { secureToken } from './secure-token.js';
import type { Tag } from './types.js';
import { buildCombinedInvitePath } from './invite-links.js';
import { sanitizeShowSets, type ShowSet } from './show-sets.js';

// Re-export account context functions for external use
export { setCurrentAccountId, getCurrentAccountId, setCurrentAccountToken, setServiceRolePreference, getServiceRolePreference, setAccountContextProvider } from './account-context.js';
export type { AccountContext, AccountContextProvider } from './account-context.js';

/**
 * Set the account token and ID for authenticated requests
 * @param token - The account access token
 * @param accountId - The account UUID (from token validation)
 */
export const setAccountToken = (token: string | null, accountId?: string | null): void => {
    setCurrentAccountToken(token);
    setAccountContextId(accountId ?? null);
    if (typeof window !== 'undefined') {
        console.log('🔑 Account', token ? `set (${accountId})` : 'cleared');
    }

    // Sync webhook config from account settings (server-side only)
    if (typeof window === 'undefined' && accountId && token) {
        import('./webhook').then(({ setWebhookConfig }) => {
            import('./accounts.js').then(({ getAccount: getAcct }) => {
                getAcct(accountId).then((acct) => {
                    if (acct?.settings && typeof acct.settings === 'object') {
                        const settings = acct.settings as Record<string, unknown>;
                        const webhook = settings.webhook as { url?: string; secret?: string } | undefined;
                        setWebhookConfig(webhook?.url, webhook?.secret);
                    }
                }).catch(() => {
                    // Silently fail - webhook config is optional
                });
            });
        });
    } else if (!token) {
        // Clear webhook config when token is cleared
        import('./webhook').then(({ setWebhookConfig }) => {
            setWebhookConfig(undefined, undefined);
        }).catch(() => {});
    }
};

/**
 * Get the current account token
 */
export const getAccountToken = (): string | null => getCurrentAccountToken();

/**
 * Check if an account token is set
 */
export const hasAccountToken = (): boolean => {
    const t = getCurrentAccountToken();
    return t !== null && t.length > 0;
};

const getGlobalFetch = () => {
    const fetchFn = (globalThis as { fetch?: typeof fetch }).fetch;
    if (!fetchFn) {
        throw new Error('Global fetch is not available in this runtime');
    }
    return fetchFn;
};

type StorageProvider = {
    kind: UploadMediaResult['provider'];
    upload: (file: Blob, options: UploadMediaOptions) => Promise<UploadMediaResult>;
    delete?: (url: string) => Promise<void>;
};

const storageCategoryFolder = (category: MediaCategory) => {
    if (category === 'happening_photo') return 'happenings';
    if (category === 'account_logo') return 'logos';
    if (category === 'thing_photo') return 'things';
    if (category === 'place_layout') return 'places';
    if (category === 'social_card') return 'social-cards';
    return 'artists';
};

const normalizeStorageSegment = (value?: string) => {
    if (!value) {
        return randomId().replace('id_', 'media');
    }
    return slugify(value).slice(0, 60) || randomId().replace('id_', 'media');
};

const mimeExtensionMap: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/avif': 'avif',
    'image/svg+xml': 'svg',
};

const extractExtensionFromName = (fileName?: string) => {
    if (!fileName) {
        return '';
    }
    const parts = fileName.split('.');
    if (parts.length < 2) {
        return '';
    }
    return parts.pop()?.toLowerCase() ?? '';
};

const determineFileExtension = (file: Blob, overrideName?: string) => {
    const providedNameExtension = extractExtensionFromName(overrideName);
    if (providedNameExtension) {
        return providedNameExtension;
    }
    const fileWithName = file as Blob & { name?: string };
    const fileNameExtension = extractExtensionFromName(fileWithName.name);
    if (fileNameExtension) {
        return fileNameExtension;
    }
    if (file.type && mimeExtensionMap[file.type]) {
        return mimeExtensionMap[file.type];
    }
    return 'bin';
};

const buildStoragePath = (options: UploadMediaOptions, extension: string) => {
    const folder = storageCategoryFolder(options.category);
    const slug = normalizeStorageSegment(options.identifier);
    const uniqueSuffix = Math.random().toString(36).slice(2, 10);
    const fileName = `${Date.now()}-${uniqueSuffix}.${extension}`;

    // If accountSlug is provided, prefix the path for tenant isolation
    if (options.accountSlug) {
        const normalizedAccountSlug = slugify(options.accountSlug);
        return `${normalizedAccountSlug}/${folder}/${slug}/${fileName}`;
    }

    return `${folder}/${slug}/${fileName}`;
};

const encodeStoragePath = (path: string) =>
    path
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');

const encodeArrayBufferToBase64 = (arrayBuffer: ArrayBuffer) => {
    const bufferCtor = (globalThis as { Buffer?: { from(input: ArrayBuffer | ArrayBufferView): { toString(encoding: string): string } } }).Buffer;
    if (bufferCtor) {
        return bufferCtor.from(arrayBuffer).toString('base64');
    }
    const bytes = new Uint8Array(arrayBuffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    if (typeof btoa === 'function') {
        return btoa(binary);
    }
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let output = '';
    let idx = 0;
    while (idx < binary.length) {
        const chr1 = binary.charCodeAt(idx++);
        const chr2 = binary.charCodeAt(idx++);
        const chr3 = binary.charCodeAt(idx++);
        const enc1 = chr1 >> 2;
        const enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
        let enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
        let enc4 = chr3 & 63;
        if (Number.isNaN(chr2)) {
            enc3 = 64;
            enc4 = 64;
        } else if (Number.isNaN(chr3)) {
            enc4 = 64;
        }
        output += chars.charAt(enc1) + chars.charAt(enc2) + chars.charAt(enc3) + chars.charAt(enc4);
    }
    return output;
};

const blobToDataUrl = async (blob: Blob, contentType: string) => {
    const base64 = encodeArrayBufferToBase64(await blob.arrayBuffer());
    return `data:${contentType};base64,${base64}`;
};

const createMemoryStorageProvider = (): StorageProvider => ({
    kind: 'memory',
    async upload(file, options) {
        const contentType = file.type || 'application/octet-stream';
        const dataUrl = await blobToDataUrl(file, contentType);
        return {
            url: dataUrl,
            path: `memory://${options.category}/${randomId()}`,
            provider: 'memory',
            contentType,
        };
    },
    async delete(url: string) {
        // Memory storage uses data URLs - nothing to delete
        // This is a no-op for in-memory storage
    },
});


type R2BrowserConfig = {
    apiBaseUrl: string;
    getAuthToken: () => string | null;
};

const createR2BrowserPresignProvider = (config: R2BrowserConfig): StorageProvider => {
    const apiBaseUrl = config.apiBaseUrl.replace(/\/$/, '');
    const fetchFn = getGlobalFetch();

    const authHeaders = (): Record<string, string> => {
        const token = config.getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    return {
        kind: 'r2',
        async upload(file, options) {
            if (!options?.category) {
                throw new Error('Upload category is required');
            }
            const contentType = file.type || 'application/octet-stream';

            const useTokenAuth = Boolean(options.auth);
            const presignUrl = useTokenAuth
                ? `${apiBaseUrl}/api/v1/media/presign-token`
                : `${apiBaseUrl}/api/v1/media/presign`;
            const presignBody: Record<string, unknown> = {
                category: options.category,
                identifier: options.identifier,
                accountSlug: options.accountSlug,
                fileName: options.fileName,
                contentType,
            };
            if (options.auth) {
                presignBody.entityId = options.auth.entityId;
                presignBody.token = options.auth.token;
            }
            const presignResponse = await fetchFn(presignUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(useTokenAuth ? {} : authHeaders()),
                },
                credentials: useTokenAuth ? 'omit' : 'include',
                body: JSON.stringify(presignBody),
            });

            if (!presignResponse.ok) {
                const detail = await presignResponse.text().catch(() => '');
                throw new Error(`Presign request failed: ${detail || presignResponse.statusText}`);
            }

            const presign = await presignResponse.json() as {
                uploadUrl: string;
                publicUrl: string;
                path: string;
                method: string;
                headers: Record<string, string>;
            };

            const uploadResponse = await fetchFn(presign.uploadUrl, {
                method: presign.method,
                headers: presign.headers,
                body: file,
            });

            if (!uploadResponse.ok) {
                const detail = await uploadResponse.text().catch(() => '');
                throw new Error(`R2 upload failed: ${detail || uploadResponse.statusText}`);
            }

            return {
                url: presign.publicUrl,
                path: presign.path,
                provider: 'r2',
                contentType,
            };
        },
        async delete(url: string) {
            if (!url) return;
            try {
                const response = await fetchFn(`${apiBaseUrl}/api/v1/media`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        ...authHeaders(),
                    },
                    credentials: 'include',
                    body: JSON.stringify({ url }),
                });
                if (!response.ok && response.status !== 404) {
                    const detail = await response.text().catch(() => '');
                    console.warn(`Failed to delete R2 media ${url}:`, detail || response.statusText);
                }
            } catch (error) {
                console.warn('Error deleting R2 media:', url, error);
            }
        },
    };
};

let storageProvider: StorageProvider = createMemoryStorageProvider();

const updateStorageProvider = (config?: DalConfig | null) => {
    if (config?.storageProvider === 'r2' && config.apiBaseUrl && config.getAuthToken) {
        storageProvider = createR2BrowserPresignProvider({
            apiBaseUrl: config.apiBaseUrl,
            getAuthToken: config.getAuthToken,
        });
        return;
    }
    if (config?.storageProvider === 'r2') {
        console.warn('⚠️ R2 storage requires apiBaseUrl + getAuthToken. Falling back to in-memory uploads.');
    }
    storageProvider = createMemoryStorageProvider();
};

export const uploadMedia = async (file: Blob, options: UploadMediaOptions): Promise<UploadMediaResult> => {
    if (!file) {
        throw new Error('File is required for upload');
    }
    if (!options?.category) {
        throw new Error('Upload category is required');
    }
    return storageProvider.upload(file, options);
};

/**
 * Delete a media file from storage by its URL
 * This is a best-effort operation - failures are logged but don't throw errors
 */
export const deleteMedia = async (url: string | null | undefined): Promise<void> => {
    if (!url || !storageProvider.delete) {
        return;
    }

    // Only attempt deletion for Supabase storage URLs
    // Memory storage (data URLs) doesn't need cleanup
    if (url.startsWith('data:')) {
        return;
    }

    try {
        await storageProvider.delete(url);
    } catch (error) {
        // Don't throw - media deletion failure shouldn't block operations
        console.warn('Failed to delete media:', url, error);
    }
};

const initSupabaseContext = (config: DalConfig) => {
    if (config.supabaseUrl && config.supabaseAnonKey) {
        // Normalize URL - handle both with and without /rest/v1
        let normalizedBase = config.supabaseUrl.replace(/\/$/, '');
        // If URL doesn't already end with /rest/v1, add it
        if (!normalizedBase.endsWith('/rest/v1')) {
            // Remove any existing /rest/v1 if it's in the middle or end
            normalizedBase = normalizedBase.replace(/\/rest\/v1\/?$/, '');
            normalizedBase = `${normalizedBase}/rest/v1`;
        }

        const schema = config.schema || 'littlebooker';

        supabaseContext = {
            restUrl: normalizedBase,
            headers: {
                apikey: config.supabaseAnonKey,
                Authorization: `Bearer ${config.supabaseAnonKey}`,
                'Content-Type': 'application/json',
            },
            schema,
        };
        if (typeof window !== 'undefined') {
            console.log('✅ Supabase context initialized:', {
                restUrl: supabaseContext.restUrl,
                schema: supabaseContext.schema,
                originalUrl: config.supabaseUrl,
                hasHeaders: true,
            });
        }
    } else {
        supabaseContext = null;
        if (typeof window !== 'undefined') {
            console.warn('⚠️ Supabase context NOT initialized - missing URL or key. Using in-memory store only!', {
                hasUrl: !!config.supabaseUrl,
                hasKey: !!config.supabaseAnonKey,
                urlValue: config.supabaseUrl || 'MISSING',
                keyPreview: config.supabaseAnonKey ? `${config.supabaseAnonKey.substring(0, 10)}...` : 'MISSING',
            });
        }
    }
};

export const initClient = (config: DalConfig) => {
    currentConfig = config;
    // Reset context before initializing
    supabaseContext = null;
    initSupabaseContext(config);
    updateStorageProvider(config);
    return currentConfig;
};

export const getClientConfig = () => currentConfig;

const formatSupabaseError = (action: string, detail: string) => `Unable to ${action}: ${detail}`;

type SupabaseRequestOptions = {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
    body?: unknown;
    searchParams?: URLSearchParams;
    preferReturn?: boolean;
};

/**
 * Build a PostgREST `or=` filter matching `term` against any of `columns`.
 *
 * The values are wrapped in double quotes and escaped, which is not cosmetic.
 * Unquoted, a term containing a comma is read as a filter separator and the
 * request fails outright with a 400; parentheses are read as grouping and the
 * request quietly returns nothing. Both are ordinary characters in a show
 * title ("Rock, Paper & Scissors", "100% Live (Deluxe)"), so search used to
 * break on real data. Inside the quotes, a literal backslash needs doubling
 * twice — once for PostgREST's string parser and once for LIKE — and a double
 * quote needs a single escape.
 *
 * `%` and `_` in the term are still LIKE wildcards, so they match loosely
 * rather than literally. That is normal search-box behaviour and harmless.
 */
const buildTextSearchFilter = (
    columns: string[],
    term: string,
    prefixColumns: string[] = [],
): string => {
    const escaped = term
        .replace(/\\/g, '\\\\\\\\')
        .replace(/"/g, '\\"');
    const clauses = [
        ...columns.map((c) => `${c}.ilike."%${escaped}%"`),
        // Prefix-only: you type a short code from its start, and a substring
        // match would land it inside unrelated codes.
        ...prefixColumns.map((c) => `${c}.ilike."${escaped}%"`),
    ];
    return `(${clauses.join(',')})`;
};

const supabaseRequest = async <T>(
    action: string,
    endpoint: string,
    options: SupabaseRequestOptions = {},
): Promise<T> => {
    if (!supabaseContext) {
        throw new Error('Supabase is not configured');
    }

    const fetchFn = getGlobalFetch();
    // PostgREST: When schema is exposed, use Accept-Profile header and table name directly
    // Don't include schema in the path - just use the table name like "/people"
    // The Accept-Profile header tells PostgREST which schema to use

    const url = new URL(`${supabaseContext.restUrl}${endpoint}`);
    if (options.searchParams) {
        options.searchParams.forEach((value, key) => {
            url.searchParams.append(key, value);
        });
    }
    const finalUrl = url.toString();

    if (typeof window !== 'undefined') {
        console.log(`🌐 Supabase ${options.method || 'GET'} request:`, {
            url: finalUrl,
            action,
            hasBody: !!options.body,
        });
    }

    // Build headers - include Accept-Profile for schema selection
    const requestHeaders: Record<string, string> = {
        ...supabaseContext.headers,
        ...(options.preferReturn ? { Prefer: 'return=representation' } : {}),
    };

    // Forward the per-request identity to PostgREST so RLS can scope this request
    // — the supabaseRequest counterpart of getClient()'s authenticated fetch +
    // service-role selection (§5.9). Precedence mirrors that path:
    //   1. service-role preference (trusted, token-validated public endpoints
    //      with no account JWT) → service-role key for apikey + Authorization,
    //      but only when it's actually configured (API server);
    //   2. else the per-request account JWT (Supabase-valid) as the bearer;
    //   3. else the baked-in anon key (supabaseContext.headers).
    // With RLS now ON, an anon fallback (no service-role key AND no account token)
    // is denied by policy — it's the correct behaviour for an unidentified caller,
    // not a silent bypass.
    if (getServiceRolePreference() && supabaseContext.adminApiKey) {
        requestHeaders.apikey = supabaseContext.adminApiKey;
        requestHeaders.Authorization = `Bearer ${supabaseContext.adminApiKey}`;
    } else {
        const accountToken = getAccountToken();
        if (accountToken) {
            requestHeaders.Authorization = `Bearer ${accountToken}`;
        }
    }

    // Only add Accept-Profile/Content-Profile if schema is not 'public' (public is default)
    if (supabaseContext.schema !== 'public') {
        // Accept-Profile is for read operations (GET)
        requestHeaders['Accept-Profile'] = supabaseContext.schema;
        // Content-Profile is for write operations (POST, PATCH, DELETE)
        const isWriteOperation = options.method === 'POST' || options.method === 'PATCH' || options.method === 'DELETE';
        if (isWriteOperation) {
            requestHeaders['Content-Profile'] = supabaseContext.schema;
        }
    }

    const response = await fetchFn(finalUrl, {
        method: options.method ?? 'GET',
        headers: requestHeaders,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
        let detail = response.statusText || 'request failed';
        try {
            const data = await response.json();
            if (typeof data?.message === 'string' && data.message) {
                detail = data.message;
            } else if (typeof data?.error_description === 'string' && data.error_description) {
                detail = data.error_description;
            }
        } catch {
            const text = await response.text();
            if (text) {
                detail = text;
            }
        }
        if (typeof window !== 'undefined') {
            console.error(`❌ Supabase ${action} failed:`, {
                status: response.status,
                statusText: response.statusText,
                detail,
                url: url.toString(),
            });
        }
        throw new Error(formatSupabaseError(action, detail));
    }

    if (typeof window !== 'undefined') {
        console.log(`✅ Supabase ${action} succeeded:`, { status: response.status });
    }

    if (response.status === 204) {
        return null as T;
    }

    const text = await response.text();
    if (!text) {
        return null as T;
    }
    return JSON.parse(text) as T;
};

const safeJsonParse = (value: unknown) => {
    if (typeof value !== 'string') {
        return value;
    }
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

// Social links are anonymous-writable (the public artist form) and later
// rendered into `<a href>` in the admin — where React does NOT block a
// `javascript:`/`data:`/`vbscript:` scheme, so an unchecked value is a stored
// XSS that fires when an admin clicks the link. Strip dangerous schemes at the
// write boundary here, the single chokepoint both admin and public writes pass
// through. Non-URL values (bare handles, http(s) links) are untouched.
const DANGEROUS_URL_SCHEME = /^(javascript|data|vbscript):/i;
const stripDangerousUrl = (value: string): string => {
    // Compare against a copy with whitespace/control chars removed, so obfuscated
    // schemes like "java\tscript:" or " javascript:" can't slip past.
    const collapsed = value.replace(/[\u0000-\u0020]/g, '');
    return DANGEROUS_URL_SCHEME.test(collapsed) ? '' : value;
};

// Artists often type "instagram.com/name" or "www.soundcloud.com/name" without a
// scheme. Rendered as-is into `<a href>`, the browser treats that as a relative
// path and the public site 404s. Normalise to an absolute https:// URL. A bare
// handle ("name" / "@name") on a known platform is expanded to its profile URL.
const URL_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const SOCIAL_HANDLE_URL: Record<string, (handle: string) => string> = {
    instagram: (handle) => `https://instagram.com/${handle}`,
    soundcloud: (handle) => `https://soundcloud.com/${handle}`,
    bandcamp: (handle) => `https://${handle}.bandcamp.com`,
};
export const normalizeSocialUrl = (value: string | null | undefined, platform?: string): string => {
    const trimmed = stripDangerousUrl(value?.trim() ?? '');
    if (!trimmed || URL_SCHEME.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith('//')) {
        return `https:${trimmed}`;
    }
    const handle = trimmed.replace(/^@/, '');
    const expand = platform ? SOCIAL_HANDLE_URL[platform] : undefined;
    if (expand && /^[\w.-]+$/.test(handle) && !handle.includes('.')) {
        return expand(handle);
    }
    // Only prefix values that look like a host; a lone word isn't a usable URL.
    return trimmed.includes('.') ? `https://${trimmed}` : trimmed;
};

const sanitizeCustomLinks = (links?: PersonSocialLink[] | null) =>
    (links ?? [])
        .map((link) => ({
            label: link.label?.trim() ?? '',
            url: normalizeSocialUrl(link.url),
        }))
        .filter((link) => link.label && link.url);

const sanitizeSocialsPayload = (socials?: PersonSocials | null): PersonSocials | null => {
    if (!socials) {
        return null;
    }

    const instagram = normalizeSocialUrl(socials.instagram, 'instagram') || null;
    const soundcloud = normalizeSocialUrl(socials.soundcloud, 'soundcloud') || null;
    const bandcamp = normalizeSocialUrl(socials.bandcamp, 'bandcamp') || null;
    const custom = sanitizeCustomLinks(socials.custom);

    if (!instagram && !soundcloud && !bandcamp && custom.length === 0) {
        return null;
    }

    const payload: PersonSocials = {
        instagram,
        soundcloud,
        bandcamp,
    };

    if (custom.length > 0) {
        payload.custom = custom;
    }

    return payload;
};

const parseSocials = (value: unknown): PersonSocials | undefined => {
    if (!value) {
        return undefined;
    }
    const raw = safeJsonParse(value);
    if (!raw || typeof raw !== 'object') {
        return undefined;
    }

    const record = raw as Record<string, unknown>;
    // Normalise on read too, so rows saved before write-normalisation still
    // render as absolute links on the public site.
    const instagram =
        typeof record.instagram === 'string' ? normalizeSocialUrl(record.instagram, 'instagram') || null : null;
    const soundcloud =
        typeof record.soundcloud === 'string' ? normalizeSocialUrl(record.soundcloud, 'soundcloud') || null : null;
    const bandcamp =
        typeof record.bandcamp === 'string' ? normalizeSocialUrl(record.bandcamp, 'bandcamp') || null : null;

    const customSource = record.custom;
    const custom = Array.isArray(customSource)
        ? customSource
            .map((link) => {
                if (!link || typeof link !== 'object') {
                    return null;
                }
                const entry = link as Record<string, unknown>;
                const label = typeof entry.label === 'string' ? entry.label : '';
                const url = typeof entry.url === 'string' ? normalizeSocialUrl(entry.url) : '';
                return label && url ? { label, url } : null;
            })
            .filter((entry): entry is PersonSocialLink => Boolean(entry))
        : [];

    if (!instagram && !soundcloud && !bandcamp && custom.length === 0) {
        return undefined;
    }

    const result: PersonSocials = {
        instagram,
        soundcloud,
        bandcamp,
    };

    if (custom.length > 0) {
        result.custom = custom;
    }

    return result;
};

const mapSupabasePerson = (row: SupabasePeopleRow): Person => {
    // Extract pronouns from metadata since the database doesn't have a pronouns column yet
    const pronouns = row.metadata && typeof row.metadata === 'object' && 'pronouns' in row.metadata
        ? (typeof row.metadata.pronouns === 'string' ? row.metadata.pronouns : null)
        : null;

    // Extract is_resident from metadata
    const isResident = row.metadata && typeof row.metadata === 'object' && 'is_resident' in row.metadata
        ? (typeof row.metadata.is_resident === 'boolean' ? row.metadata.is_resident : false)
        : false;

    // Extract image_focus from metadata
    const imageFocus = row.metadata && typeof row.metadata === 'object' && 'image_focus' in row.metadata
        ? (typeof row.metadata.image_focus === 'string' ? row.metadata.image_focus : null)
        : null;

    // Extract staffing constraints from metadata
    const staffing = row.metadata && typeof row.metadata === 'object' && 'staffing' in row.metadata
        && row.metadata.staffing && typeof row.metadata.staffing === 'object'
        ? (row.metadata.staffing as StaffingConstraints)
        : null;

    return {
        id: row.id,
        slug: row.slug,
        name: row.name,
        type: row.type,
        account_id: row.account_id,
        pronouns,
        email: row.email,
        phone: row.phone,
        bio: row.bio,
        picture_url: row.image_url, // Map from DB column to Person type
        image_focus: imageFocus,
        socials: parseSocials(row.social_links), // Map from DB column to Person type
        invite_token: row.profile_completion_token, // Map from DB column to Person type
        is_resident: isResident,
        is_public: row.is_public,
        is_collective: row.is_collective ?? false,
        is_active: row.is_active,
        staffing,
        profile_completed_at: row.profile_completed_at,
        created_at: row.created_at,
        updated_at: row.updated_at,
    };
};

const ensureUniqueSlugLocal = (baseSlug: string, currentId?: string) => {
    let slug = baseSlug;
    let suffix = 1;
    while (
        store.people.some((person) => person.slug === slug && (!currentId || person.id !== currentId))
    ) {
        slug = `${baseSlug}-${suffix}`;
        suffix += 1;
    }
    return slug;
};

const doesSupabaseSlugExist = async (slug: string, currentId?: string) => {
    if (!supabaseContext) {
        return store.people.some((person) => person.slug === slug && person.id !== currentId);
    }

    const params = new URLSearchParams();
    params.set('select', 'id');
    params.set('slug', `eq.${slug}`);
    params.set('limit', '1');
    if (currentId) {
        params.set('id', `neq.${currentId}`);
    }

    const rows = await supabaseRequest<SupabasePeopleRow[]>('check slug', '/people', {
        searchParams: params,
    });
    if (Array.isArray(rows) && rows.length > 0) return true;

    // Also check slug aliases — don't claim a slug that's an alias for another entity
    const aliasParams = new URLSearchParams();
    aliasParams.set('select', 'entity_id');
    aliasParams.set('entity_type', 'eq.person');
    aliasParams.set('old_slug', `eq.${slug}`);
    aliasParams.set('limit', '1');
    if (currentId) {
        aliasParams.set('entity_id', `neq.${currentId}`);
    }
    const aliasRows = await supabaseRequest<{ entity_id: string }[]>('check slug alias', '/slug_aliases', {
        searchParams: aliasParams,
    });
    return Array.isArray(aliasRows) && aliasRows.length > 0;
};

const ensureUniqueSlug = async (baseSlug: string, currentId?: string) => {
    const normalizedBase = baseSlug || `person-${Math.random().toString(36).slice(2, 8)}`;
    if (!supabaseContext) {
        return ensureUniqueSlugLocal(normalizedBase, currentId);
    }
    let candidate = normalizedBase;
    let suffix = 1;
    while (await doesSupabaseSlugExist(candidate, currentId)) {
        candidate = `${normalizedBase}-${suffix}`;
        suffix += 1;
        if (suffix > 50) {
            throw new Error('Unable to allocate a unique slug');
        }
    }
    return candidate;
};

const resolveEnvObject = (env?: Record<string, string | undefined>) => {
    if (env) {
        return env;
    }

    // In browser/client context, try import.meta.env first (Vite's way)
    if (typeof window !== 'undefined' && typeof (globalThis as any).importMeta !== 'undefined') {
        try {
            // @ts-ignore - import.meta might be available
            const importMetaEnv = (globalThis as any).importMeta?.env || {};
            const merged: Record<string, string> = {};
            // Copy all env vars that might be available
            if (typeof import.meta !== 'undefined' && import.meta.env) {
                Object.entries(import.meta.env).forEach(([key, value]) => {
                    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                        merged[key] = String(value);
                    }
                });
            }
            // Also check process.env if available
            if (typeof process !== 'undefined' && process.env) {
                Object.assign(merged, process.env as Record<string, string>);
            }
            return merged;
        } catch (e) {
            // Fall through to process.env
        }
    }

    if (typeof process !== 'undefined' && process.env) {
        return process.env as Record<string, string>;
    }
    return {};
};

export const loadConfigFromEnv = (env?: Record<string, string | undefined>): DalConfig => {
    const source = resolveEnvObject(env);

    // Try multiple possible variable names
    const config = {
        supabaseUrl: source.VITE_SUPABASE_URL
            ?? source.SUPABASE_URL
            ?? source.VITE_POSTGREST_URL
            ?? source.POSTGREST_URL
            ?? undefined,
        supabaseAnonKey: source.VITE_SUPABASE_ANON_KEY
            ?? source.SUPABASE_ANON_KEY
            ?? source.VITE_POSTGREST_API_KEY
            ?? source.POSTGREST_API_KEY
            ?? undefined,
        schema: source.VITE_SUPABASE_SCHEMA
            ?? source.SUPABASE_SCHEMA
            ?? 'littlebooker',
    };

    if (typeof window !== 'undefined') {
        console.log('🔧 DAL Config loaded:', {
            hasUrl: !!config.supabaseUrl,
            hasKey: !!config.supabaseAnonKey,
            urlPreview: config.supabaseUrl ? `${config.supabaseUrl.substring(0, 30)}...` : 'MISSING',
            keyPreview: config.supabaseAnonKey ? `${config.supabaseAnonKey.substring(0, 10)}...` : 'MISSING',
            checkedVars: {
                hasViteSupabaseUrl: !!source.VITE_SUPABASE_URL,
                hasSupabaseUrl: !!source.SUPABASE_URL,
                hasViteAnonKey: !!source.VITE_SUPABASE_ANON_KEY,
                hasAnonKey: !!source.SUPABASE_ANON_KEY,
            },
            allKeys: Object.keys(source).filter(k => k.includes('SUPABASE') || k.includes('POSTGREST')),
        });
    }

    return config;
};

export const listPeople = async (filters: ListPeopleFilters = {}): Promise<Person[]> => {
    await delay();
    const { type, is_active, is_public, is_collective, query, limit } = filters;

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('order', 'name');

        // Filter by current account (multi-tenant isolation)
        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }

        if (type) {
            params.set('type', `eq.${type}`);
        }

        if (typeof is_active === 'boolean') {
            params.set('is_active', `eq.${is_active}`);
        }

        if (typeof is_public === 'boolean') {
            params.set('is_public', `eq.${is_public}`);
        }

        if (typeof is_collective === 'boolean') {
            params.set('is_collective', `eq.${is_collective}`);
        }

        if (query && query.trim()) {
            params.set('or', buildTextSearchFilter(['name', 'slug', 'email'], query.trim()));
        }

        if (limit) {
            params.set('limit', String(limit));
        }

        const rows = await supabaseRequest<SupabasePeopleRow[]>('list people', '/people', {
            searchParams: params,
        });
        return rows.map((row) => mapSupabasePerson(row));
    }

    return store.people
        .filter((person) => {
            if (type && person.type !== type) {
                return false;
            }
            if (typeof is_active === 'boolean' && person.is_active !== is_active) {
                return false;
            }
            if (typeof is_public === 'boolean' && person.is_public !== is_public) {
                return false;
            }
            if (typeof is_collective === 'boolean' && person.is_collective !== is_collective) {
                return false;
            }
            if (query) {
                const normalized = query.toLowerCase();
                return (
                    person.name?.toLowerCase().includes(normalized) ||
                    person.slug?.toLowerCase().includes(normalized) ||
                    (person.email?.toLowerCase().includes(normalized) ?? false)
                );
            }
            return true;
        })
        .map((person) => clone(person));
};

// ============================================================================
// USERS (Internal Staff)
// ============================================================================

export type ListUsersFilters = {
    is_active?: boolean;
    role?: UserRole;
    query?: string;
    limit?: number;
};

export const listUsers = async (filters: ListUsersFilters = {}): Promise<User[]> => {
    await delay();
    const { is_active, role, query, limit } = filters;

    if (supabaseContext) {
        const params = new URLSearchParams();
        // Don't select password_hash for security
        params.set('select', 'id,account_id,email,name,role,is_active,last_login_at,created_at,updated_at');
        params.set('order', 'name.asc');

        // Filter by current account (multi-tenant isolation)
        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }

        if (typeof is_active === 'boolean') {
            params.set('is_active', `eq.${is_active}`);
        }

        if (role) {
            params.set('role', `eq.${role}`);
        }

        if (query && query.trim()) {
            const searchTerm = query.trim().toLowerCase();
            params.set('or', `(name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%)`);
        }

        if (limit) {
            params.set('limit', String(limit));
        }

        return await supabaseRequest<User[]>('list users', '/users', {
            searchParams: params,
        });
    }

    // In-memory mock: return empty for now (users are managed via Supabase)
    return [];
};

export const getUserById = async (id: string): Promise<User | null> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', 'id,account_id,email,name,role,is_active,last_login_at,created_at,updated_at');
        params.set('id', `eq.${id}`);
        params.set('limit', '1');

        const rows = await supabaseRequest<User[]>('get user by id', '/users', {
            searchParams: params,
        });
        return rows.length > 0 ? rows[0] : null;
    }

    return null;
};

// ============================================================================
// PEOPLE (External Contacts)
// ============================================================================

export const getPersonBySlug = async (slug: string): Promise<Person | null> => {
    await delay();
    const normalizedSlug = slugify(slug || '');
    if (!normalizedSlug) {
        return null;
    }

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('slug', `eq.${normalizedSlug}`);
        params.set('limit', '1');

        const rows = await supabaseRequest<SupabasePeopleRow[]>('get person by slug', '/people', {
            searchParams: params,
        });
        if (!rows || rows.length === 0) {
            // Fallback: check slug aliases
            const aliasParams = new URLSearchParams();
            aliasParams.set('select', 'entity_id');
            aliasParams.set('entity_type', 'eq.person');
            aliasParams.set('old_slug', `eq.${normalizedSlug}`);
            aliasParams.set('limit', '1');
            const aliasRows = await supabaseRequest<{ entity_id: string }[]>('resolve person slug alias', '/slug_aliases', {
                searchParams: aliasParams,
            });
            if (aliasRows && aliasRows.length > 0) {
                const idParams = new URLSearchParams();
                idParams.set('select', '*');
                idParams.set('id', `eq.${aliasRows[0].entity_id}`);
                idParams.set('limit', '1');
                const personRows = await supabaseRequest<SupabasePeopleRow[]>('get person by alias id', '/people', {
                    searchParams: idParams,
                });
                if (personRows && personRows.length > 0) {
                    return mapSupabasePerson(personRows[0]);
                }
            }
            return null;
        }
        return mapSupabasePerson(rows[0]);
    }

    const person = store.people.find((p) => p.slug === normalizedSlug);
    return person ? clone(person) : null;
};

// Read a person by their public invite token (people.profile_completion_token),
// MAPPED to the app Person shape (invite_token, socials, picture_url, extracted
// metadata) via mapSupabasePerson — unlike people.ts's raw getPersonByProfileToken,
// which returns the DB row and so is missing invite_token/socials/etc. Used by
// the public artist-invite endpoint so the form receives the fields it expects.
// The token is the lookup key (closes §5.3 hole (a)).
export const getPersonByInviteToken = async (token: string): Promise<Person | null> => {
    const normalizedToken = token?.trim();
    if (!normalizedToken) {
        return null;
    }

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('profile_completion_token', `eq.${normalizedToken}`);
        params.set('limit', '1');
        const rows = await supabaseRequest<SupabasePeopleRow[]>('get person by invite token', '/people', {
            searchParams: params,
        });
        return rows && rows.length > 0 ? mapSupabasePerson(rows[0]) : null;
    }

    const person = store.people.find((p) => p.invite_token === normalizedToken);
    return person ? clone(person) : null;
};

export const createPerson = async (data: PersonCreate): Promise<Person> => {
    await delay();
    const email = data.email.trim();
    if (!email) {
        throw new Error('Email is required');
    }
    const name = data.name?.trim() || null;
    const type = data.type ?? 'artist';

    // Generate slug: use provided slug, or name if available, or email as fallback
    let baseSlug: string;
    if (data.slug) {
        baseSlug = slugify(data.slug);
    } else if (name) {
        baseSlug = slugify(name);
    } else {
        // Use email prefix (before @) as slug base
        baseSlug = slugify(email.split('@')[0]);
    }
    baseSlug = baseSlug || `person-${Math.random().toString(36).slice(2, 8)}`;
    const slug = await ensureUniqueSlug(baseSlug);
    const timestamp = now();
    const socialsPayload = sanitizeSocialsPayload(data.socials);

    if (supabaseContext) {
        const accountId = getAccountContextId() ?? data.account_id;
        if (!accountId) {
            throw new Error('Cannot create person: no account context set and no account_id provided.');
        }

        if (typeof window !== 'undefined') {
            console.log('📤 Creating person in Supabase:', { name, slug, type });
        }
        // Store pronouns in metadata since the database doesn't have a pronouns column yet
        const metadata: Record<string, unknown> = {};
        if (data.pronouns) {
            metadata.pronouns = data.pronouns;
        }
        if (data.is_resident !== undefined) {
            metadata.is_resident = data.is_resident;
        }
        if (data.staffing && Object.keys(data.staffing).length > 0) {
            metadata.staffing = data.staffing;
        }

        const insertPayload = {
            slug,
            name,
            type,
            account_id: accountId, // Multi-tenant: from context or explicitly provided
            email,
            phone: data.phone ?? null,
            bio: data.bio ?? null,
            image_url: data.picture_url ?? null, // Use DB column name
            social_links: socialsPayload, // Use DB column name
            profile_completion_token: data.generateInviteLink ? randomToken() : null, // Use DB column name
            metadata: Object.keys(metadata).length > 0 ? metadata : null,
            is_public: data.is_public ?? false,
            is_collective: data.is_collective ?? false,
            is_active: true,
            created_at: timestamp,
            updated_at: timestamp,
        };

        const rows = await supabaseRequest<SupabasePeopleRow[]>('create person', '/people', {
            method: 'POST',
            preferReturn: true,
            body: insertPayload,
        });

        if (!rows || rows.length === 0) {
            throw new Error('Unable to create person: empty response from Supabase');
        }

        if (typeof window !== 'undefined') {
            console.log('✅ Person created in Supabase:', { id: rows[0].id, name, slug });
        }

        const createdPerson = mapSupabasePerson(rows[0]);

        // Trigger webhook if this is an artist
        if (createdPerson.type === 'artist') {
            const { triggerWebsiteUpdate } = await import('./webhook')
            triggerWebsiteUpdate('artists-updated', {
                artistId: createdPerson.id,
                artistSlug: createdPerson.slug,
            })
        }

        return createdPerson;
    }

    if (typeof window !== 'undefined') {
        console.warn('⚠️ Using in-memory store - Supabase not configured. Data will be lost on refresh!');
    }
    const person: Person = {
        id: randomId(),
        slug,
        name,
        type,
        pronouns: data.pronouns ?? null,
        email,
        phone: data.phone ?? null,
        bio: data.bio ?? null,
        picture_url: data.picture_url ?? null,
        socials: socialsPayload ?? undefined,
        invite_token: data.generateInviteLink ? randomToken() : null,
        is_resident: data.is_resident ?? false,
        is_public: data.is_public ?? false,
        is_collective: data.is_collective ?? false,
        is_active: true,
        staffing: data.staffing ?? null,
        created_at: timestamp,
        updated_at: timestamp,
    };
    store.people.unshift(person);

    // Trigger webhook if this is an artist
    if (person.type === 'artist') {
        const { triggerWebsiteUpdate } = await import('./webhook')
        triggerWebsiteUpdate('artists-updated', {
            artistId: person.id,
            artistSlug: person.slug,
        })
    }

    return clone(person);
};

export const updatePerson = async (id: string, patch: PersonUpdate): Promise<Person> => {
    await delay();
    if (supabaseContext) {
        const loadParams = new URLSearchParams();
        loadParams.set('select', '*');
        loadParams.set('id', `eq.${id}`);
        loadParams.set('limit', '1');

        const rows = await supabaseRequest<SupabasePeopleRow[]>('load person', '/people', {
            searchParams: loadParams,
        });

        const currentRow = rows[0];
        if (!currentRow) {
            throw new Error(`Person ${id} not found`);
        }

        const currentPerson = mapSupabasePerson(currentRow);
        let slug = currentPerson.slug;

        // Regenerate slug if explicitly provided
        if (typeof patch.slug === 'string' && patch.slug.trim()) {
            const desiredSlug = slugify(patch.slug);
            if (desiredSlug !== currentPerson.slug) {
                slug = await ensureUniqueSlug(desiredSlug, id);
            }
        }
        // Auto-regenerate slug when name is added for the first time
        else if (patch.name && !currentPerson.name) {
            const nameSlug = slugify(patch.name);
            if (nameSlug && nameSlug !== currentPerson.slug) {
                slug = await ensureUniqueSlug(nameSlug, id);
            }
        }

        // Save old slug as alias so previously shared links still resolve
        if (slug !== currentPerson.slug) {
            await supabaseRequest('save person slug alias', '/slug_aliases', {
                method: 'POST',
                body: {
                    entity_type: 'person',
                    entity_id: id,
                    old_slug: currentPerson.slug,
                },
            }).catch((err: unknown) => {
                // Duplicate alias (unique index) is expected and fine to ignore
                console.warn('[updatePerson] Failed to save slug alias:', err);
            });
        }

        const nextSocials =
            patch.socials !== undefined
                ? sanitizeSocialsPayload(patch.socials)
                : sanitizeSocialsPayload(currentPerson.socials);

        let inviteToken: string | null = currentPerson.invite_token ?? null;
        if (typeof patch.invite_token === 'string') {
            inviteToken = patch.invite_token;
        } else if ('invite_token' in patch && patch.invite_token === null) {
            inviteToken = null;
        } else if (patch.generateInviteLink) {
            inviteToken = randomToken();
        }

        // Handle pronouns in metadata since the database doesn't have a pronouns column yet
        const currentPronouns = 'pronouns' in patch ? patch.pronouns ?? null : currentPerson.pronouns ?? null;
        const currentIsResident = 'is_resident' in patch ? patch.is_resident ?? false : currentPerson.is_resident ?? false;
        const currentMetadata = currentRow.metadata && typeof currentRow.metadata === 'object'
            ? (currentRow.metadata as Record<string, unknown>)
            : {};
        const updatedMetadata = { ...currentMetadata };
        if (currentPronouns) {
            updatedMetadata.pronouns = currentPronouns;
        } else if ('pronouns' in patch && patch.pronouns === null) {
            delete updatedMetadata.pronouns;
        }
        if (currentIsResident !== undefined) {
            updatedMetadata.is_resident = currentIsResident;
        } else if ('is_resident' in patch && patch.is_resident === false) {
            delete updatedMetadata.is_resident;
        }
        const currentImageFocus = 'image_focus' in patch ? patch.image_focus ?? null : currentPerson.image_focus ?? null;
        if (currentImageFocus && currentImageFocus !== 'center') {
            updatedMetadata.image_focus = currentImageFocus;
        } else {
            delete updatedMetadata.image_focus;
        }
        if ('staffing' in patch) {
            if (patch.staffing && Object.keys(patch.staffing).length > 0) {
                updatedMetadata.staffing = patch.staffing;
            } else {
                delete updatedMetadata.staffing;
            }
        }

        const updatedRecord = {
            slug,
            name: patch.name ?? currentPerson.name,
            type: patch.type ?? currentPerson.type,
            email: 'email' in patch ? patch.email ?? null : currentPerson.email ?? null,
            phone: 'phone' in patch ? patch.phone ?? null : currentPerson.phone ?? null,
            bio: 'bio' in patch ? patch.bio ?? null : currentPerson.bio ?? null,
            image_url:
                'picture_url' in patch ? patch.picture_url ?? null : currentPerson.picture_url ?? null, // Use DB column name
            social_links: nextSocials, // Use DB column name
            profile_completion_token: inviteToken, // Use DB column name
            metadata: Object.keys(updatedMetadata).length > 0 ? updatedMetadata : null,
            is_public:
                typeof patch.is_public === 'boolean' ? patch.is_public : currentPerson.is_public ?? false,
            is_collective:
                typeof patch.is_collective === 'boolean' ? patch.is_collective : currentPerson.is_collective ?? false,
            is_active:
                typeof patch.is_active === 'boolean' ? patch.is_active : currentPerson.is_active ?? true,
            updated_at: now(),
        };

        const updateParams = new URLSearchParams();
        updateParams.set('id', `eq.${id}`);

        const updatedRows = await supabaseRequest<SupabasePeopleRow[]>('update person', '/people', {
            method: 'PATCH',
            preferReturn: true,
            body: updatedRecord,
            searchParams: updateParams,
        });

        if (!updatedRows || updatedRows.length === 0) {
            throw new Error('Unable to update person: empty response from Supabase');
        }

        const updatedPerson = mapSupabasePerson(updatedRows[0]);

        // Trigger webhook if this is an artist
        if (updatedPerson.type === 'artist') {
            const { triggerWebsiteUpdate } = await import('./webhook')
            triggerWebsiteUpdate('artists-updated', {
                artistId: updatedPerson.id,
                artistSlug: updatedPerson.slug,
            })
        }

        return updatedPerson;
    }

    const personIndex = store.people.findIndex((p) => p.id === id);
    if (personIndex === -1) {
        throw new Error(`Person ${id} not found`);
    }
    const existing = store.people[personIndex];
    let slug = existing.slug;

    // Regenerate slug if explicitly provided
    if (typeof patch.slug === 'string' && patch.slug.trim()) {
        const desiredSlug = slugify(patch.slug);
        if (desiredSlug !== existing.slug) {
            slug = ensureUniqueSlugLocal(desiredSlug, id);
        }
    }
    // Auto-regenerate slug when name is added for the first time
    else if (patch.name && !existing.name) {
        const nameSlug = slugify(patch.name);
        if (nameSlug && nameSlug !== existing.slug) {
            slug = ensureUniqueSlugLocal(nameSlug, id);
        }
    }

    const socials =
        patch.socials !== undefined
            ? sanitizeSocialsPayload(patch.socials)
            : existing.socials ?? undefined;

    let inviteToken: string | null = existing.invite_token ?? null;
    if (typeof patch.invite_token === 'string') {
        inviteToken = patch.invite_token;
    } else if ('invite_token' in patch && patch.invite_token === null) {
        inviteToken = null;
    } else if (patch.generateInviteLink) {
        inviteToken = randomToken();
    }

    const updated: Person = {
        ...existing,
        ...patch,
        slug,
        socials: socials ?? undefined,
        invite_token: inviteToken,
        updated_at: now(),
    };

    store.people[personIndex] = updated;

    // Trigger webhook if this is an artist
    if (updated.type === 'artist') {
        const { triggerWebsiteUpdate } = await import('./webhook')
        triggerWebsiteUpdate('artists-updated', {
            artistId: updated.id,
            artistSlug: updated.slug,
        })
    }

    return clone(updated);
};

export const updatePersonByInviteToken = async (token: string, patch: PersonUpdate): Promise<Person> => {
    await delay();
    const normalizedToken = token?.trim();
    if (!normalizedToken) {
        throw new Error('Invite token is required');
    }

    const completedAt = new Date().toISOString();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', 'id,account_id,name,profile_completed_at');
        params.set('profile_completion_token', `eq.${normalizedToken}`);
        params.set('limit', '1');

        const rows = await supabaseRequest<SupabasePeopleRow[]>('load person by invite token', '/people', {
            searchParams: params,
        });

        const row = rows?.[0];
        if (!row) {
            throw new Error('Invite token is invalid or expired');
        }

        // Update the person with the patch
        const updatedPerson = await updatePerson(row.id, patch);
        const isFirstCompletion = !row.profile_completed_at;

        if (isFirstCompletion) {
            // Set profile_completed_at separately (since updatePerson doesn't handle this field)
            const completionParams = new URLSearchParams();
            completionParams.set('id', `eq.${row.id}`);
            await supabaseRequest('mark profile completed', '/people', {
                method: 'PATCH',
                body: { profile_completed_at: completedAt },
                searchParams: completionParams,
            });

            // Create a pending completion notification for the scheduler to process
            if (row.account_id) {
                try {
                    // Use name if available, otherwise fall back to email
                    const displayName = row.name || updatedPerson.name || updatedPerson.email;
                    await createPendingCompletionNotification({
                        entity_type: 'person',
                        entity_id: row.id,
                        entity_name: displayName,
                        completed_by_name: displayName,
                        completed_at: completedAt,
                        account_id: row.account_id,
                        notification_type: 'completion',
                        change_description: null,
                    });
                } catch (err) {
                    console.error('[updatePersonByInviteToken] Failed to create pending notification:', err);
                    // Don't fail the whole operation if notification creation fails
                }
            }
        }

        return { ...updatedPerson, profile_completed_at: isFirstCompletion ? completedAt : row.profile_completed_at };
    }

    const person = store.people.find((p) => p.invite_token === normalizedToken);
    if (!person) {
        throw new Error('Invite token is invalid or expired');
    }
    const updatedPerson = await updatePerson(person.id, patch);
    // Update profile_completed_at in memory store (only on first completion)
    if (!person.profile_completed_at) {
        const personIndex = store.people.findIndex((p) => p.id === person.id);
        if (personIndex !== -1) {
            store.people[personIndex] = { ...store.people[personIndex], profile_completed_at: completedAt };
        }
    }
    return { ...updatedPerson, profile_completed_at: person.profile_completed_at || completedAt };
};

export const deletePerson = async (id: string): Promise<void> => {
    await delay();

    // Fetch the person first to get their type and slug for webhook
    let isArtist = false;
    let personSlug: string | null = null;

    if (supabaseContext) {
        const getParams = new URLSearchParams();
        getParams.set('id', `eq.${id}`);
        getParams.set('select', 'type,slug');
        const persons = await supabaseRequest<Array<{ type: string; slug: string }>>('get person for deletion', '/people', {
            searchParams: getParams,
        });
        const p = persons?.[0];
        isArtist = p?.type === 'artist';
        personSlug = p?.slug ?? null;

        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `eq.${id}`);

        await supabaseRequest('delete person', '/people', {
            method: 'DELETE',
            searchParams: deleteParams,
        });

        // Trigger webhook if this was an artist
        if (isArtist) {
            const { triggerWebsiteUpdate } = await import('./webhook')
            triggerWebsiteUpdate('artists-updated', {
                artistId: id,
                artistSlug: personSlug,
                deleted: true,
            })
        }
    } else {
        const personIndex = store.people.findIndex((p) => p.id === id);
        if (personIndex !== -1) {
            const person = store.people[personIndex];
            isArtist = person.type === 'artist';
            personSlug = person.slug;

            store.people.splice(personIndex, 1);

            // Trigger webhook if this was an artist
            if (isArtist) {
                const { triggerWebsiteUpdate } = await import('./webhook')
                triggerWebsiteUpdate('artists-updated', {
                    artistId: id,
                    artistSlug: personSlug,
                    deleted: true,
                })
            }
        }
    }
};

export const deletePeople = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;
    await delay();

    if (supabaseContext) {
        // PostgREST in.() syntax: id=in.(uuid1,uuid2,uuid3)
        // No spaces after commas, and parentheses are part of the value
        const deleteParams = new URLSearchParams();
        const idsList = ids.join(',');
        deleteParams.set('id', `in.(${idsList})`);

        try {
            await supabaseRequest('delete people', '/people', {
                method: 'DELETE',
                searchParams: deleteParams,
            });
        } catch (error) {
            console.error('Failed to delete people:', { ids, error });
            throw error;
        }
    } else {
        ids.forEach((id) => {
            const personIndex = store.people.findIndex((p) => p.id === id);
            if (personIndex !== -1) {
                store.people.splice(personIndex, 1);
            }
        });
    }
};

export const listTasks = async (filters: TaskFilters = {}): Promise<Task[]> => {
    await delay();
    const { status, due_date_gte, due_date_lte, limit, offset, query } = filters;

    if (supabaseContext) {
        const params = new URLSearchParams();
        if (query && query.trim()) {
            params.set('or', buildTextSearchFilter(['title', 'description'], query.trim()));
        }
        if (status) {
            params.set('status', `eq.${status}`);
        }
        if (due_date_gte) {
            params.set('due_date', `gte.${due_date_gte}`);
        }
        if (due_date_lte) {
            params.set('due_date', `lte.${due_date_lte}`);
        }
        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }
        params.set('order', 'due_date.asc.nullslast');

        // Apply pagination
        if (limit !== undefined) {
            params.set('limit', String(limit));
            params.set('offset', String(offset ?? 0));
        }

        return await supabaseRequest<Task[]>('list tasks', '/tasks', { searchParams: params });
    }

    const lowerTaskQuery = query?.trim().toLowerCase();
    const filtered = store.tasks
        .filter((task) => {
            if (status && task.status !== status) {
                return false;
            }
            if (lowerTaskQuery) {
                const haystack = `${task.title ?? ''} ${task.description ?? ''}`.toLowerCase();
                if (!haystack.includes(lowerTaskQuery)) return false;
            }
            if (due_date_gte && task.due_date) {
                const dueDate = new Date(task.due_date);
                const filterDate = new Date(due_date_gte);
                if (dueDate < filterDate) return false;
            }
            if (due_date_lte && task.due_date) {
                const dueDate = new Date(task.due_date);
                const filterDate = new Date(due_date_lte);
                if (dueDate > filterDate) return false;
            }
            return true;
        })
        .sort((a, b) => {
            // Sort by due_date (closest first, nulls last)
            if (!a.due_date && !b.due_date) return 0;
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        })
        .map((task) => clone(task));

    // Apply pagination for in-memory store
    if (limit !== undefined) {
        const startIdx = offset ?? 0;
        return filtered.slice(startIdx, startIdx + limit);
    }

    return filtered;
};

export const countTasks = async (filters: Omit<TaskFilters, 'limit' | 'offset'> = {}): Promise<number> => {
    await delay();
    const { status, due_date_gte, due_date_lte } = filters;

    if (supabaseContext) {
        const params = new URLSearchParams();
        if (status) {
            params.set('status', `eq.${status}`);
        }
        if (due_date_gte) {
            params.set('due_date', `gte.${due_date_gte}`);
        }
        if (due_date_lte) {
            params.set('due_date', `lte.${due_date_lte}`);
        }
        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }

        const rows = await supabaseRequest<Task[]>('count tasks', '/tasks', {
            searchParams: params,
        });
        return rows.length;
    }

    // In-memory count
    return store.tasks.filter((task) => {
        if (status && task.status !== status) return false;
        if (due_date_gte && task.due_date) {
            const dueDate = new Date(task.due_date);
            const filterDate = new Date(due_date_gte);
            if (dueDate < filterDate) return false;
        }
        if (due_date_lte && task.due_date) {
            const dueDate = new Date(task.due_date);
            const filterDate = new Date(due_date_lte);
            if (dueDate > filterDate) return false;
        }
        return true;
    }).length;
};

export const createTask = async (data: TaskCreate): Promise<Task> => {
    await delay();
    if (!data.title.trim()) {
        throw new Error('Title is required');
    }

    if (supabaseContext) {
        const insertData = {
            title: data.title.trim(),
            type: data.type,
            description: data.description ?? null,
            status: data.status ?? 'todo',
            priority: data.priority ?? 'medium',
            due_date: data.due_date ?? null,
            parent_task_id: data.parent_task_id ?? null,
            template_task_key: data.template_task_key ?? null,
            related_happening_id: data.related_happening_id ?? null,
            sort_order: data.sort_order ?? 0,
            assignee_role: data.assignee_role ?? null,
            assigned_to_id: data.assigned_to_id ?? null,
            account_id: getAccountContextId(), // Multi-tenant: assign to current account
        };

        const rows = await supabaseRequest<Task[]>('create task', '/tasks', {
            method: 'POST',
            body: insertData,
            preferReturn: true,
        });

        if (!rows || rows.length === 0) {
            throw new Error('Unable to create task');
        }

        const task = rows[0];

        // Auto-create task reminder if enabled
        if (data.reminder_enabled && task.due_date) {
            const { createTaskReminder: createReminder } = await import('./task-reminders.js');
            await createReminder({
                task_id: task.id,
                offset_hours: data.reminder_offset_hours ?? 0,
            });
        }

        return task;
    }

    const timestamp = now();
    const task: Task = {
        id: randomId(),
        title: data.title.trim(),
        type: data.type,
        description: data.description ?? null,
        status: data.status ?? 'todo',
        priority: data.priority ?? 'medium',
        due_date: data.due_date ?? null,
        parent_task_id: data.parent_task_id ?? null,
        template_task_key: data.template_task_key ?? null,
        related_happening_id: data.related_happening_id ?? null,
        sort_order: data.sort_order ?? 0,
        assignee_role: data.assignee_role ?? null,
        assigned_to_id: data.assigned_to_id ?? null,
        assigned_to_user_id: data.assigned_to_user_id ?? null,
        created_at: timestamp,
        updated_at: timestamp,
    };
    store.tasks.unshift(task);

    // Auto-create task reminder if enabled (in-memory mode)
    if (data.reminder_enabled && task.due_date) {
        const { createTaskReminder: createReminder } = await import('./task-reminders.js');
        await createReminder({
            task_id: task.id,
            offset_hours: data.reminder_offset_hours ?? 0,
        });
    }

    return clone(task);
};

export const updateTask = async (id: string, data: TaskUpdate): Promise<Task> => {
    await delay();

    if (supabaseContext) {
        const updateData = {
            ...(data.title !== undefined && { title: data.title.trim() }),
            ...(data.description !== undefined && { description: data.description }),
            ...(data.type !== undefined && { type: data.type }),
            ...(data.status !== undefined && { status: data.status }),
            ...(data.priority !== undefined && { priority: data.priority }),
            ...(data.due_date !== undefined && { due_date: data.due_date }),
            ...(data.parent_task_id !== undefined && { parent_task_id: data.parent_task_id }),
            ...(data.sort_order !== undefined && { sort_order: data.sort_order }),
            ...(data.template_task_key !== undefined && { template_task_key: data.template_task_key }),
            ...(data.related_happening_id !== undefined && { related_happening_id: data.related_happening_id }),
            ...(data.assignee_role !== undefined && { assignee_role: data.assignee_role }),
            ...(data.assigned_to_id !== undefined && { assigned_to_id: data.assigned_to_id }),
            ...(data.assigned_to_user_id !== undefined && { assigned_to_user_id: data.assigned_to_user_id }),
            updated_at: new Date().toISOString(),
        };

        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);

        const rows = await supabaseRequest<Task[]>('update task', '/tasks', {
            method: 'PATCH',
            body: updateData,
            searchParams: params,
            preferReturn: true,
        });

        if (!rows || rows.length === 0) {
            throw new Error('Task not found');
        }
        return rows[0];
    }

    // In-memory store
    const index = store.tasks.findIndex((t) => t.id === id);
    if (index === -1) {
        throw new Error('Task not found');
    }

    const task = store.tasks[index];
    const updated: Task = {
        ...task,
        ...(data.title !== undefined && { title: data.title.trim() }),
        ...(data.description !== undefined && { description: data.description }),
        ...(data.type !== undefined && { type: data.type }),
        ...(data.status !== undefined && { status: data.status }),
        ...(data.priority !== undefined && { priority: data.priority }),
        ...(data.due_date !== undefined && { due_date: data.due_date }),
        ...(data.parent_task_id !== undefined && { parent_task_id: data.parent_task_id }),
        ...(data.sort_order !== undefined && { sort_order: data.sort_order }),
        ...(data.template_task_key !== undefined && { template_task_key: data.template_task_key }),
        ...(data.related_happening_id !== undefined && { related_happening_id: data.related_happening_id }),
        ...(data.assignee_role !== undefined && { assignee_role: data.assignee_role }),
        ...(data.assigned_to_id !== undefined && { assigned_to_id: data.assigned_to_id }),
        updated_at: now(),
    };
    store.tasks[index] = updated;
    return clone(updated);
};

export const deleteTask = async (id: string): Promise<void> => {
    await delay();

    if (supabaseContext) {
        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `eq.${id}`);

        await supabaseRequest('delete task', '/tasks', {
            method: 'DELETE',
            searchParams: deleteParams,
        });
    } else {
        const taskIndex = store.tasks.findIndex((t) => t.id === id);
        if (taskIndex !== -1) {
            store.tasks.splice(taskIndex, 1);
        }
    }
};

export const deleteTasks = async (ids: string[]): Promise<void> => {
    if (ids.length === 0) return;
    await delay();

    if (supabaseContext) {
        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `in.(${ids.join(',')})`);

        await supabaseRequest('delete tasks', '/tasks', {
            method: 'DELETE',
            searchParams: deleteParams,
        });
    } else {
        ids.forEach((id) => {
            const taskIndex = store.tasks.findIndex((t) => t.id === id);
            if (taskIndex !== -1) {
                store.tasks.splice(taskIndex, 1);
            }
        });
    }
};

// Happenings helper functions
const sanitizeStringArray = (arr: unknown): string[] => {
    if (!Array.isArray(arr)) return [];
    return arr
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map((item) => item.trim());
};

/**
 * Episode numbers only belong on resident shows. Metadata updates are merged over the
 * stored metadata and the form omits episode_number for non-resident shows, so a show
 * switched from resident to guest would otherwise keep its old number (and the public
 * site would show "Episode N" on a guest show).
 */
const dropEpisodeNumberIfNotResident = (type: string | undefined, metadata: HappeningMetadata): void => {
    if (type === 'show' && metadata.show_type && metadata.show_type !== 'resident') {
        delete metadata.episode_number;
    }
};

const sanitizeHappeningMetadata = (metadata?: HappeningMetadata | null): HappeningMetadata => {
    if (!metadata || typeof metadata !== 'object') {
        return {};
    }
    const record = clone(metadata as Record<string, unknown>);
    const genres = sanitizeStringArray(record.genres).slice(0, 4); // max 4 genres
    const showType = record.show_type === 'resident' || record.show_type === 'guest' || record.show_type === 'takeover' ? record.show_type : undefined;
    const durationMinutes = typeof record.duration_minutes === 'number' && record.duration_minutes > 0
        ? record.duration_minutes
        : undefined;
    const episodeNumber = typeof record.episode_number === 'number' && record.episode_number > 0
        ? record.episode_number
        : undefined;
    const stubToken =
        typeof record.stub_token === 'string' && record.stub_token.trim()
            ? record.stub_token.trim()
            : undefined;
    const placeId =
        typeof record.place_id === 'string' && record.place_id.trim()
            ? record.place_id.trim()
            : undefined;

    // Sanitize artists array. person_id is the only thing that genuinely identifies an
    // artist, so it is the only hard requirement: a missing name is normalised to null
    // (stub artists legitimately have none) rather than silently dropping the entry.
    // Discarding on a missing name emptied default_metadata.artists for every series
    // created from the admin UI, which sends {person_id, type} with no name.
    const artists = Array.isArray(record.artists)
        ? (record.artists as HappeningArtist[])
            .filter((a): a is HappeningArtist =>
                typeof a === 'object' && a !== null && typeof a.person_id === 'string'
            )
            .map((a) => ({
                ...a,
                name: typeof a.name === 'string' ? a.name : null,
                slug: typeof a.slug === 'string' ? a.slug : '',
            }))
        : undefined;

    // Sanitize gear requests array
    const gearRequests = Array.isArray(record.gear_requests)
        ? (record.gear_requests as GearRequest[]).filter(
            (g): g is GearRequest =>
                typeof g === 'object' &&
                g !== null &&
                typeof g.thing_id === 'string' &&
                typeof g.name === 'string'
        )
        : undefined;

    // Sanitize archive links array
    const archiveLinks = Array.isArray(record.archive_links)
        ? (record.archive_links as ArchiveLink[]).filter((link): link is ArchiveLink => {
            if (typeof link !== 'object' || link === null) return false;
            if ('type' in link && link.type === 'soundcloud') {
                return typeof link.url === 'string' && link.url.trim().length > 0;
            }
            if ('type' in link && link.type === 'custom') {
                return (
                    typeof link.name === 'string' &&
                    link.name.trim().length > 0 &&
                    typeof link.url === 'string' &&
                    link.url.trim().length > 0 &&
                    (link.icon_slug === null || link.icon_slug === undefined || typeof link.icon_slug === 'string')
                );
            }
            return false;
        }).map((link) => {
            if (link.type === 'soundcloud') {
                return { type: 'soundcloud' as const, url: link.url.trim() };
            } else {
                return {
                    type: 'custom' as const,
                    name: link.name.trim(),
                    url: link.url.trim(),
                    icon_slug: link.icon_slug?.trim() || null,
                };
            }
        })
        : undefined;

    const sets = sanitizeShowSets(record.sets);

    // Sanitize tracklist array
    const tracklist = Array.isArray(record.tracklist)
        ? (record.tracklist as TracklistItem[]).filter((item): item is TracklistItem => {
            if (typeof item !== 'object' || item === null) return false;
            return typeof item.artist === 'string' && typeof item.title === 'string' && item.title.trim().length > 0;
        }).map((item) => ({
            artist: item.artist.trim(),
            title: item.title.trim(),
            timestamp: typeof item.timestamp === 'string' && item.timestamp.trim() ? item.timestamp.trim() : null,
        }))
        : undefined;

    // Preserve template_id and required_roles (used for template-based happenings)
    const templateId = typeof record.template_id === 'string' && record.template_id.trim()
        ? record.template_id.trim()
        : undefined;
    const requiredRoles = Array.isArray(record.required_roles) && record.required_roles.length > 0
        ? (record.required_roles as TemplateRole[])
        : undefined;

    // Preserve display flags
    const isAutoGeneratedTitle = typeof record.is_auto_generated_title === 'boolean'
        ? record.is_auto_generated_title
        : undefined;
    const isStaffPick = typeof record.is_staff_pick === 'boolean'
        ? record.is_staff_pick
        : undefined;
    const imageFit = typeof record.image_fit === 'string' && record.image_fit.trim()
        ? record.image_fit.trim()
        : undefined;
    const imageFocus = typeof record.image_focus === 'string' && record.image_focus.trim()
        ? record.image_focus.trim()
        : undefined;
    const imageUrl = typeof record.image_url === 'string' && record.image_url.trim()
        ? record.image_url.trim()
        : undefined;

    // Sanitize rider notes
    const riderNotes = typeof record.rider_notes === 'string' && record.rider_notes.trim()
        ? record.rider_notes.trim()
        : undefined;

    const result: HappeningMetadata = {};
    if (showType) result.show_type = showType;
    if (genres.length > 0) result.genres = genres;
    // Always include artists array (even if empty) so it can clear existing artists
    if (artists !== undefined) result.artists = artists;
    if (gearRequests && gearRequests.length > 0) result.gear_requests = gearRequests;
    if (archiveLinks !== undefined) result.archive_links = archiveLinks;
    // Kept even when empty, so clearing the running order in the form clears it here.
    if (sets !== undefined) result.sets = sets;
    if (tracklist && tracklist.length > 0) result.tracklist = tracklist;
    if (durationMinutes) result.duration_minutes = durationMinutes;
    if (episodeNumber) result.episode_number = episodeNumber;
    if (stubToken) result.stub_token = stubToken;
    if (placeId) result.place_id = placeId;
    if (templateId) result.template_id = templateId;
    if (requiredRoles) result.required_roles = requiredRoles;
    // Include display flags
    if (isAutoGeneratedTitle !== undefined) result.is_auto_generated_title = isAutoGeneratedTitle;
    if (isStaffPick !== undefined) result.is_staff_pick = isStaffPick;
    if (imageFit) result.image_fit = imageFit;
    if (imageFocus) result.image_focus = imageFocus;
    if (imageUrl) result.image_url = imageUrl;
    if (riderNotes) result.rider_notes = riderNotes;
    // Which invite email the programmer chose for this show, and when.
    if (
        record.invite_email_strategy === 'combined' ||
        record.invite_email_strategy === 'separate' ||
        record.invite_email_strategy === 'none'
    ) {
        result.invite_email_strategy = record.invite_email_strategy;
    }
    if (typeof record.invite_email_strategy_set_at === 'string' && record.invite_email_strategy_set_at.trim()) {
        result.invite_email_strategy_set_at = record.invite_email_strategy_set_at.trim();
    }
    // Post-show feedback URL
    const postShowFeedbackUrl = typeof record.post_show_feedback_url === 'string' && record.post_show_feedback_url.trim()
        ? record.post_show_feedback_url.trim()
        : undefined;
    if (postShowFeedbackUrl) result.post_show_feedback_url = postShowFeedbackUrl;
    // Generated social cards, keyed by template id -> URL
    if (record.social_cards && typeof record.social_cards === 'object' && !Array.isArray(record.social_cards)) {
        const cards: Record<string, string> = {};
        for (const [key, val] of Object.entries(record.social_cards as Record<string, unknown>)) {
            if (typeof key === 'string' && typeof val === 'string' && val.trim()) {
                cards[key] = val.trim();
            }
        }
        // Set unconditionally (even when empty) so an admin removing the last card
        // can clear the key — metadata is merged shallowly, so a dropped key would
        // leave the old map in place.
        result.social_cards = cards;
    }

    return result;
};

const parseHappeningMetadata = (value: unknown): HappeningMetadata => {
    if (!value) return {};
    const raw = safeJsonParse(value);
    if (!raw || typeof raw !== 'object') return {};
    return sanitizeHappeningMetadata(raw as HappeningMetadata);
};

type JoinedParticipant = {
    id: string;
    person_id: string;
    user_id?: string | null;
    role: string;
    sort_order: number;
    people: { id: string; name: string | null; slug: string; image_url: string | null } | null;
    users?: { id: string; name: string | null } | null;
};

/**
 * PostgREST embed that brings a happening's tags along with the row. Shared by the list
 * and detail read paths so a tag badge never depends on which one loaded the happening.
 */
const TAG_EMBED =
    ',happening_tags(tags(id,account_id,slug,label,color,description,created_at,updated_at))';

/** Shape of a `happening_tags(tags(...))` embed. */
type JoinedHappeningTag = {
    tags: Tag | null;
};

const mapSupabaseHappening = (
    row: SupabaseHappeningsRow,
    participants?: JoinedParticipant[],
    joinedTags?: JoinedHappeningTag[],
): Happening => {
    const metadata = parseHappeningMetadata(row.metadata);
    // image_url can come from column (if migration applied) or metadata (fallback)
    const imageUrl = row.image_url ?? metadata.image_url ?? null;

    // When the caller joined happening_participants(people(...)), overwrite the
    // persisted artist snapshot in metadata.artists with fresh data from people.
    // The stored snapshot can go stale when an artist updates their photo, slug,
    // or name — joining at read time is the single source of truth.
    if (participants && participants.length > 0) {
        metadata.artists = participants
            .filter((p) => p.role === 'artist' && p.people)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map((p) => ({
                person_id: p.person_id,
                name: p.people!.name,
                slug: p.people!.slug,
                picture_url: p.people!.image_url,
                sort_order: p.sort_order,
            }));
    }

    return {
        id: row.id,
        slug: row.slug,
        title: row.title,
        type: row.type,
        description: row.description,
        image_url: imageUrl,
        start_time: row.start_time,
        end_time: row.end_time,
        place_id: row.place_id,
        timezone: row.timezone,
        status: row.status,
        is_public: row.is_public,
        metadata,
        series_id: row.series_id ?? null,
        series_instance_date: row.series_instance_date ?? null,
        program_id: row.program_id ?? null,
        account_id: row.account_id ?? null,
        // Only set when the caller joined happening_tags; left undefined otherwise so a
        // write path built from this object cannot mistake "not joined" for "no tags".
        ...(joinedTags
            ? { tags: joinedTags.map((t) => t.tags).filter((t): t is Tag => !!t) }
            : {}),
        created_at: row.created_at,
        updated_at: row.updated_at,
    };
};

const ensureUniqueHappeningSlugLocal = (baseSlug: string, currentId?: string) => {
    let slug = baseSlug;
    let suffix = 1;
    while (
        store.happenings.some((h) => h.slug === slug && (!currentId || h.id !== currentId))
    ) {
        slug = `${baseSlug}-${suffix}`;
        suffix += 1;
    }
    return slug;
};

const doesSupabaseHappeningSlugExist = async (slug: string, currentId?: string) => {
    if (!supabaseContext) {
        return store.happenings.some((h) => h.slug === slug && h.id !== currentId);
    }

    const params = new URLSearchParams();
    params.set('select', 'id');
    params.set('slug', `eq.${slug}`);
    params.set('limit', '1');
    if (currentId) {
        params.set('id', `neq.${currentId}`);
    }

    const rows = await supabaseRequest<SupabaseHappeningsRow[]>('check happening slug', '/happenings', {
        searchParams: params,
    });
    if (Array.isArray(rows) && rows.length > 0) return true;

    // Also check slug aliases — don't claim a slug that's an alias for another entity
    const aliasParams = new URLSearchParams();
    aliasParams.set('select', 'entity_id');
    aliasParams.set('entity_type', 'eq.happening');
    aliasParams.set('old_slug', `eq.${slug}`);
    aliasParams.set('limit', '1');
    if (currentId) {
        aliasParams.set('entity_id', `neq.${currentId}`);
    }
    const aliasRows = await supabaseRequest<{ entity_id: string }[]>('check happening slug alias', '/slug_aliases', {
        searchParams: aliasParams,
    });
    return Array.isArray(aliasRows) && aliasRows.length > 0;
};

const ensureUniqueHappeningSlug = async (baseSlug: string, currentId?: string) => {
    const normalizedBase = baseSlug || `show-${Math.random().toString(36).slice(2, 8)}`;
    if (!supabaseContext) {
        return ensureUniqueHappeningSlugLocal(normalizedBase, currentId);
    }
    let candidate = normalizedBase;
    let suffix = 1;
    while (await doesSupabaseHappeningSlugExist(candidate, currentId)) {
        candidate = `${normalizedBase}-${suffix}`;
        suffix += 1;
        if (suffix > 50) {
            throw new Error('Unable to allocate a unique happening slug');
        }
    }
    return candidate;
};

/**
 * Add the participant filter to a happenings query as an aliased `!inner` embed.
 *
 * The alias matters. Filtering the main `happening_participants` embed directly
 * would narrow that array to the matching row, and the list needs the whole
 * cast: the artists rebuild metadata.artists and the programmer draws the
 * initials. A second, aliased embed constrains which happenings come back while
 * leaving the main embed untouched. PostgREST does not duplicate a happening
 * that has several matching participants.
 *
 * Returns the select fragment to append, or '' when no participant filter is set.
 */
const applyParticipantFilter = (
    params: URLSearchParams,
    filters: Pick<HappeningFilters, 'participant_person_id' | 'participant_user_id' | 'participant_role'>,
): string => {
    const { participant_person_id, participant_user_id, participant_role } = filters;
    if (!participant_person_id && !participant_user_id && !participant_role) {
        return '';
    }
    if (participant_person_id) {
        params.set('pfilter.person_id', `eq.${participant_person_id}`);
    }
    if (participant_user_id) {
        params.set('pfilter.user_id', `eq.${participant_user_id}`);
    }
    if (participant_role) {
        params.set('pfilter.role', `eq.${participant_role}`);
    }
    return ',pfilter:happening_participants!inner(happening_id)';
};

/** In-memory counterpart of applyParticipantFilter. */
const matchesParticipantFilter = (
    happeningId: string,
    filters: Pick<HappeningFilters, 'participant_person_id' | 'participant_user_id' | 'participant_role'>,
): boolean => {
    const { participant_person_id, participant_user_id, participant_role } = filters;
    if (!participant_person_id && !participant_user_id && !participant_role) {
        return true;
    }
    return templateStore.participants.some((p) => {
        if (p.happening_id !== happeningId) return false;
        if (participant_role && p.role !== participant_role) return false;
        if (participant_person_id && p.person_id !== participant_person_id) return false;
        if (participant_user_id && p.user_id !== participant_user_id) return false;
        return true;
    });
};

export const listHappenings = async (filters: HappeningFilters = {}): Promise<Happening[]> => {
    await delay();
    const { status, status_not, type, is_public, start_time_gte, start_time_lte, program_id, query, limit, offset, order, includeArtists } = filters;

    if (supabaseContext) {
        const params = new URLSearchParams();
        const participantJoin = applyParticipantFilter(params, filters);
        // Always join happening_participants(people(...)) so mapSupabaseHappening
        // can rebuild metadata.artists with fresh data. The includeArtists option
        // is now informational — kept for backward compatibility of HappeningWithArtists.
        // user_id/users(...) come along too: a programmer can be an internal user
        // rather than an external person, and the list view renders whichever is set.
        const participantEmbed = filters.withParticipants === false
            ? ''
            : ',happening_participants(id,happening_id,person_id,user_id,role,sort_order,people(id,name,slug,image_url),users(id,name))';
        params.set('select', `*${participantEmbed}${TAG_EMBED}${participantJoin}`);
        // Use order parameter if provided, default to ascending (closest to today first)
        const sortDirection = order === 'desc' ? 'desc' : 'asc';
        params.set('order', `start_time.${sortDirection}.nullslast,created_at.desc`);

        // Filter by current account (multi-tenant isolation)
        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }

        if (status) {
            params.set('status', `eq.${status}`);
        }
        if (status_not) {
            params.set('status', `neq.${status_not}`);
        }
        if (type) {
            params.set('type', `eq.${type}`);
        }
        if (is_public !== undefined) {
            params.set('is_public', `eq.${is_public}`);
        }
        if (start_time_gte) {
            params.set('start_time', `gte.${start_time_gte}`);
        }
        if (start_time_lte) {
            params.set('start_time', `lte.${start_time_lte}`);
        }
        if (program_id !== undefined) {
            params.set('program_id', program_id === null ? 'is.null' : `eq.${program_id}`);
        }
        if (query && query.trim()) {
            params.set('or', buildTextSearchFilter(['title', 'slug', 'description'], query.trim()));
        }

        // Apply pagination
        if (limit !== undefined) {
            params.set('limit', String(limit));
            params.set('offset', String(offset ?? 0));
        }

        // Type for rows that may include joined participants
        type HappeningRowWithParticipants = SupabaseHappeningsRow & {
            happening_participants?: Array<{
                id: string;
                happening_id?: string;
                person_id: string;
                user_id?: string | null;
                role: string;
                sort_order: number;
                people: { id: string; name: string | null; slug: string; image_url: string | null } | null;
                users?: { id: string; name: string | null } | null;
            }>;
            happening_tags?: JoinedHappeningTag[];
        };

        const rows = await supabaseRequest<HappeningRowWithParticipants[]>('list happenings', '/happenings', {
            searchParams: params,
        });

        // Map rows; the mapper uses the joined participants to refresh metadata.artists.
        // We still attach happening_participants on the result when callers opted in via
        // includeArtists, since HappeningWithArtists consumers may walk that array.
        const happenings = rows.map((row) => {
            const happening = mapSupabaseHappening(row, row.happening_participants, row.happening_tags) as HappeningWithArtists;
            if (includeArtists && row.happening_participants) {
                happening.happening_participants = row.happening_participants.filter(
                    (p) => p.role === 'artist'
                );
            }
            // Every role, not just artists. The list and calendar read the
            // programmer off this so the initials always match the rows on screen.
            happening.participants = (row.happening_participants ?? []).map((p) => ({
                id: p.id,
                happening_id: p.happening_id ?? row.id,
                person_id: p.person_id ?? null,
                user_id: p.user_id ?? null,
                role: p.role,
                sort_order: p.sort_order ?? 0,
                person_name: p.people?.name ?? null,
                user_name: p.users?.name ?? null,
            }));
            return happening;
        });

        // Server-side filtering as safeguard (database filter may not work correctly)
        if (start_time_gte) {
            const filterDate = new Date(start_time_gte);
            const filtered = happenings.filter((happening) => {
                if (!happening.start_time) return false;
                const startTime = new Date(happening.start_time);
                return startTime >= filterDate;
            });

            if (filtered.length !== happenings.length) {
                console.warn(`[listHappenings] Database filter returned ${happenings.length} results, server-side filter reduced to ${filtered.length} future shows.`);
            }

            return filtered;
        }

        return happenings;
    }

    const filtered = store.happenings
        .filter((happening) => {
            if (status && happening.status !== status) {
                return false;
            }
            if (status_not && happening.status === status_not) {
                return false;
            }
            if (type && happening.type !== type) {
                return false;
            }
            if (is_public !== undefined && happening.is_public !== is_public) {
                return false;
            }
            if (start_time_gte && happening.start_time) {
                const startTime = new Date(happening.start_time);
                const filterDate = new Date(start_time_gte);
                if (startTime < filterDate) {
                    return false;
                }
            }
            if (start_time_lte && happening.start_time) {
                const startTime = new Date(happening.start_time);
                const filterDate = new Date(start_time_lte);
                if (startTime > filterDate) {
                    return false;
                }
            }
            if (query && query.trim()) {
                const searchTerm = query.trim().toLowerCase();
                const title = happening.title?.toLowerCase() || '';
                const slug = happening.slug?.toLowerCase() || '';
                const description = happening.description?.toLowerCase() || '';
                if (!title.includes(searchTerm) && !slug.includes(searchTerm) && !description.includes(searchTerm)) {
                    return false;
                }
            }
            if (!matchesParticipantFilter(happening.id, filters)) {
                return false;
            }
            return true;
        })
        .map((happening) => {
            // Mirror the Supabase branch: attach every participant so callers can
            // read the programmer without a second query.
            const cloned = clone(happening) as HappeningWithArtists;
            cloned.participants = templateStore.participants
                .filter((p) => p.happening_id === happening.id)
                .map((p) => ({
                    id: p.id,
                    happening_id: p.happening_id,
                    person_id: p.person_id ?? null,
                    user_id: p.user_id ?? null,
                    role: p.role,
                    sort_order: p.sort_order ?? 0,
                    person_name: p.person_name ?? null,
                    user_name: p.user_name ?? null,
                }));
            return cloned;
        })
        .sort((a, b) => {
            // Sort by start_time (default ascending)
            const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
            const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
            return order === 'desc' ? bTime - aTime : aTime - bTime;
        });

    // Apply pagination for in-memory store
    if (limit !== undefined) {
        const startIdx = offset ?? 0;
        return filtered.slice(startIdx, startIdx + limit);
    }

    return filtered;
};

export const countHappenings = async (filters: Omit<HappeningFilters, 'limit' | 'offset'> = {}): Promise<number> => {
    await delay();
    const { status, status_not, type, is_public, start_time_gte, start_time_lte, program_id, query } = filters;

    if (supabaseContext) {
        const params = new URLSearchParams();
        // The participant join has to be in the select for its filters to bind,
        // so the count matches the list exactly and pagination stays honest.
        const participantJoin = applyParticipantFilter(params, filters);
        params.set('select', `*${participantJoin}`);

        // Filter by current account (multi-tenant isolation)
        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }

        if (status) {
            params.set('status', `eq.${status}`);
        }
        if (status_not) {
            params.set('status', `neq.${status_not}`);
        }
        if (type) {
            params.set('type', `eq.${type}`);
        }
        if (is_public !== undefined) {
            params.set('is_public', `eq.${is_public}`);
        }
        if (start_time_gte) {
            params.set('start_time', `gte.${start_time_gte}`);
        }
        if (start_time_lte) {
            params.set('start_time', `lte.${start_time_lte}`);
        }
        if (program_id !== undefined) {
            params.set('program_id', program_id === null ? 'is.null' : `eq.${program_id}`);
        }
        if (query && query.trim()) {
            params.set('or', buildTextSearchFilter(['title', 'slug', 'description'], query.trim()));
        }

        const rows = await supabaseRequest<SupabaseHappeningsRow[]>('count happenings', '/happenings', {
            searchParams: params,
            preferCount: 'exact',
            preferReturn: false,
        });
        // When using preferCount, the response includes the count in headers
        // For now, just return the length of results (we'll optimize with head request later if needed)
        return rows.length;
    }

    // In-memory count
    return store.happenings.filter((happening) => {
        if (status && happening.status !== status) return false;
        if (status_not && happening.status === status_not) return false;
        if (type && happening.type !== type) return false;
        if (is_public !== undefined && happening.is_public !== is_public) return false;
        if (start_time_gte && happening.start_time) {
            const startTime = new Date(happening.start_time);
            const filterDate = new Date(start_time_gte);
            if (startTime < filterDate) return false;
        }
        if (start_time_lte && happening.start_time) {
            const startTime = new Date(happening.start_time);
            const filterDate = new Date(start_time_lte);
            if (startTime > filterDate) return false;
        }
        if (query && query.trim()) {
            const searchTerm = query.trim().toLowerCase();
            const title = happening.title?.toLowerCase() || '';
            const slug = happening.slug?.toLowerCase() || '';
            const description = happening.description?.toLowerCase() || '';
            if (!title.includes(searchTerm) && !slug.includes(searchTerm) && !description.includes(searchTerm)) return false;
        }
        if (!matchesParticipantFilter(happening.id, filters)) return false;
        return true;
    }).length;
};

export const getHappeningById = async (
    id: string,
    options?: { includeArtists?: boolean }
): Promise<Happening | null> => {
    await delay();
    if (!id) return null;

    if (supabaseContext) {
        const params = new URLSearchParams();
        // Always join happening_participants(people(...)) so mapSupabaseHappening
        // rebuilds metadata.artists with fresh data. includeArtists is kept only
        // to gate attaching happening_participants on the result for legacy callers.
        params.set(
            'select',
            `*,happening_participants(id,person_id,role,sort_order,people(id,name,slug,image_url))${TAG_EMBED}`,
        );
        params.set('id', `eq.${id}`);
        params.set('limit', '1');

        // Type for rows that may include joined participants
        type HappeningRowWithParticipants = SupabaseHappeningsRow & {
            happening_participants?: Array<{
                id: string;
                happening_id?: string;
                person_id: string;
                user_id?: string | null;
                role: string;
                sort_order: number;
                people: { id: string; name: string | null; slug: string; image_url: string | null } | null;
                users?: { id: string; name: string | null } | null;
            }>;
            happening_tags?: JoinedHappeningTag[];
        };

        const rows = await supabaseRequest<HappeningRowWithParticipants[]>('get happening by id', '/happenings', {
            searchParams: params,
        });
        if (!rows || rows.length === 0) {
            return null;
        }

        const row = rows[0];
        const happening = mapSupabaseHappening(row, row.happening_participants, row.happening_tags) as HappeningWithArtists;
        if (options?.includeArtists && row.happening_participants) {
            happening.happening_participants = row.happening_participants.filter(
                (p) => p.role === 'artist'
            );
        }
        return happening;
    }

    const happening = store.happenings.find((h) => h.id === id);
    return happening ? clone(happening) : null;
};

// Read a happening by its public stub_token (happenings.metadata->>stub_token) —
// the read counterpart of updateHappeningByStubToken. The public Show-invite flow
// must look up BY TOKEN, not slug (closes §5.3 hole (a)); the token is the lookup
// key, so there's no IDOR via a separate id param. Returns null when the token
// doesn't match, mirroring the other by-token readers.
export const getHappeningByStubToken = async (
    token: string,
    options?: { includeArtists?: boolean }
): Promise<Happening | null> => {
    const normalizedToken = token?.trim();
    if (!normalizedToken) {
        return null;
    }

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', 'id');
        params.set('metadata->>stub_token', `eq.${normalizedToken}`);
        params.set('limit', '1');
        const rows = await supabaseRequest<Array<{ id: string }>>(
            'load happening by stub token',
            '/happenings',
            { searchParams: params },
        );
        const id = rows?.[0]?.id;
        return id ? getHappeningById(id, options) : null;
    }

    const match = store.happenings.find(
        (h) => (h.metadata as Record<string, unknown> | undefined)?.stub_token === normalizedToken,
    );
    return match ? getHappeningById(match.id, options) : null;
};

export const getHappeningBySlug = async (
    slug: string,
    options?: { includeArtists?: boolean }
): Promise<Happening | null> => {
    await delay();
    const normalizedSlug = slugify(slug || '');
    if (!normalizedSlug) {
        return null;
    }

    if (supabaseContext) {
        const params = new URLSearchParams();
        // Always join — mapSupabaseHappening uses the result to rebuild metadata.artists.
        params.set(
            'select',
            `*,happening_participants(id,person_id,role,sort_order,people(id,name,slug,image_url))${TAG_EMBED}`,
        );
        params.set('slug', `eq.${normalizedSlug}`);
        params.set('limit', '1');

        // Type for rows that may include joined participants
        type HappeningRowWithParticipants = SupabaseHappeningsRow & {
            happening_participants?: Array<{
                id: string;
                happening_id?: string;
                person_id: string;
                user_id?: string | null;
                role: string;
                sort_order: number;
                people: { id: string; name: string | null; slug: string; image_url: string | null } | null;
                users?: { id: string; name: string | null } | null;
            }>;
            happening_tags?: JoinedHappeningTag[];
        };

        const rows = await supabaseRequest<HappeningRowWithParticipants[]>('get happening by slug', '/happenings', {
            searchParams: params,
        });
        if (!rows || rows.length === 0) {
            // Fallback: check slug aliases
            const aliasParams = new URLSearchParams();
            aliasParams.set('select', 'entity_id');
            aliasParams.set('entity_type', 'eq.happening');
            aliasParams.set('old_slug', `eq.${normalizedSlug}`);
            aliasParams.set('limit', '1');
            const aliasRows = await supabaseRequest<{ entity_id: string }[]>('resolve happening slug alias', '/slug_aliases', {
                searchParams: aliasParams,
            });
            if (aliasRows && aliasRows.length > 0) {
                return getHappeningById(aliasRows[0].entity_id, options);
            }
            return null;
        }

        const row = rows[0];
        const happening = mapSupabaseHappening(row, row.happening_participants, row.happening_tags) as HappeningWithArtists;
        if (options?.includeArtists && row.happening_participants) {
            happening.happening_participants = row.happening_participants.filter(
                (p) => p.role === 'artist'
            );
        }
        return happening;
    }

    const happening = store.happenings.find((h) => h.slug === normalizedSlug);
    return happening ? clone(happening) : null;
};

export const createHappening = async (data: HappeningCreate): Promise<Happening> => {
    await delay();
    const metadata = sanitizeHappeningMetadata(data.metadata);
    const isResidentShow = data.type === 'show' && metadata.show_type === 'resident';
    const isGuestShow = data.type === 'show' && metadata.show_type === 'guest';

    // Validate title based on show type
    const title = data.title?.trim() || '';
    if (isResidentShow && !title) {
        throw new Error('Title is required for resident shows');
    }
    if (isResidentShow && data.series_id && !metadata.episode_number) {
        throw new Error('Episode number is required for resident shows in a series');
    }

    // Generate slug based on show type
    let baseSlug: string;
    if (data.slug) {
        baseSlug = slugify(data.slug);
    } else if (isGuestShow) {
        // Guest shows: artist name + timestamp for uniqueness
        const artistName = metadata.artists && metadata.artists.length > 0
            ? metadata.artists[0].name
            : 'guest';
        let timestamp: string;
        if (data.start_time) {
            const date = new Date(data.start_time);
            // Format: YYYYMMDD-HHMMSS for better readability
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            timestamp = `${year}${month}${day}-${hours}${minutes}${seconds}`;
        } else {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            timestamp = `${year}${month}${day}-${hours}${minutes}${seconds}`;
        }
        baseSlug = slugify(`${artistName}-${timestamp}`);
    } else if (isResidentShow && title) {
        baseSlug = buildResidentEpisodeSlug(title, metadata.episode_number, data.start_time);
    } else if (title) {
        baseSlug = slugify(title);
    } else {
        baseSlug = `show-${Math.random().toString(36).slice(2, 8)}`;
    }

    const slug = await ensureUniqueHappeningSlug(baseSlug);
    const timestamp = now();

    // Handle metadata with optional stub token
    if (data.generateStubLink) {
        metadata.stub_token = randomToken();
    }

    if (supabaseContext) {
        if (typeof window !== 'undefined') {
            console.log('📤 Creating happening in Supabase:', { title, slug, type: data.type });
        }

        // Write to both the top-level column and metadata so reads on either
        // path see a consistent value (updateHappening already does the same).
        if (data.image_url) {
            metadata.image_url = data.image_url;
        }

        // For guest shows without title, use artist name
        let finalTitle: string;
        if (isGuestShow && !title) {
            const artistName = metadata.artists && metadata.artists.length > 0
                ? metadata.artists[0].name
                : 'Guest Show';
            finalTitle = artistName;
        } else {
            finalTitle = title || 'Untitled Show';
        }

        // For shows, performances, and tech events, calculate end_time from start_time + duration if not provided
        let finalEndTime: string | null = data.end_time ?? null;
        if ((data.type === 'show' || data.type === 'performance' || data.type === 'tech') && data.start_time && metadata.duration_minutes && !data.end_time) {
            const start = new Date(data.start_time);
            const end = new Date(start.getTime() + metadata.duration_minutes * 60 * 1000);
            finalEndTime = end.toISOString();
        }

        const accountId = data.account_id ?? getAccountContextId();
        if (!accountId) {
            throw new Error('Missing account context: account_id is required to create a happening');
        }

        const insertPayload = {
            slug,
            title: finalTitle,
            type: data.type,
            account_id: accountId, // Multi-tenant: assign to current account
            description: data.description ?? null,
            image_url: data.image_url ?? null,
            start_time: toMinuteIso(data.start_time),
            end_time: toMinuteIso(finalEndTime),
            place_id: metadata.place_id ?? null,
            is_public: data.is_public ?? false,
            status: data.status ?? 'draft',
            metadata,
            series_id: data.series_id ?? null,
            series_instance_date: data.series_instance_date ?? null,
            program_id: data.program_id ?? null,
            created_at: timestamp,
            updated_at: timestamp,
        };

        const rows = await supabaseRequest<SupabaseHappeningsRow[]>('create happening', '/happenings', {
            method: 'POST',
            preferReturn: true,
            body: insertPayload,
        });

        if (!rows || rows.length === 0) {
            throw new Error('Unable to create happening: empty response from Supabase');
        }

        if (typeof window !== 'undefined') {
            console.log('✅ Happening created in Supabase:', { id: rows[0].id, title, slug });
        }

        const createdHappening = mapSupabaseHappening(rows[0])

        // Trigger webhook if this is a public show
        if (createdHappening.type === 'show' && createdHappening.is_public) {
            const { triggerWebsiteUpdate } = await import('./webhook')
            triggerWebsiteUpdate('shows-updated', {
                happeningId: createdHappening.id,
                showSlug: createdHappening.slug,
            })
        }

        // Trigger webhook for program timetable updates
        if (createdHappening.program_id) {
            const { triggerWebsiteUpdate } = await import('./webhook')
            triggerWebsiteUpdate('program-updated', {
                programId: createdHappening.program_id,
                happeningId: createdHappening.id,
                reason: 'happening-created',
            })
        }

        return createdHappening
    }

    if (typeof window !== 'undefined') {
        console.warn('⚠️ Using in-memory store - Supabase not configured. Data will be lost on refresh!');
    }
    // For guest shows without title, use artist name
    let finalTitle: string;
    if (isGuestShow && !title) {
        const artistName = metadata.artists && metadata.artists.length > 0
            ? metadata.artists[0].name
            : 'Guest Show';
        finalTitle = artistName;
    } else {
        finalTitle = title || 'Untitled Show';
    }

    // For shows, calculate end_time from start_time + duration if not provided
    let finalEndTime: string | null = data.end_time ?? null;
    if (data.type === 'show' && data.start_time && metadata.duration_minutes && !data.end_time) {
        const start = new Date(data.start_time);
        const end = new Date(start.getTime() + metadata.duration_minutes * 60 * 1000);
        finalEndTime = end.toISOString();
    }
    const happening: Happening = {
        id: randomId(),
        slug,
        title: finalTitle,
        type: data.type,
        description: data.description ?? null,
        image_url: data.image_url ?? null,
        start_time: toMinuteIso(data.start_time),
        end_time: toMinuteIso(finalEndTime),
        status: data.status ?? 'draft',
        is_public: data.is_public ?? false,
        metadata,
        series_id: data.series_id ?? null,
        series_instance_date: data.series_instance_date ?? null,
        program_id: null,
        created_at: timestamp,
        updated_at: timestamp,
    };
    store.happenings.unshift(happening);
    return clone(happening);
};

export const updateHappening = async (id: string, patch: HappeningUpdate): Promise<Happening> => {
    await delay();

    if (supabaseContext) {
        const loadParams = new URLSearchParams();
        // Only select the columns we actually need for the update
        loadParams.set('select', 'id,slug,title,type,description,image_url,start_time,end_time,status,is_public,metadata,account_id');
        loadParams.set('id', `eq.${id}`);
        loadParams.set('limit', '1');

        const rows = await supabaseRequest<SupabaseHappeningsRow[]>('load happening for update', '/happenings', {
            searchParams: loadParams,
        });

        const currentRow = rows[0];
        if (!currentRow) {
            throw new Error(`Happening ${id} not found`);
        }

        const currentHappening = mapSupabaseHappening(currentRow);
        let slug = currentHappening.slug;
        if (typeof patch.slug === 'string' && patch.slug.trim()) {
            const desiredSlug = slugify(patch.slug);
            if (desiredSlug !== currentHappening.slug) {
                slug = await ensureUniqueHappeningSlug(desiredSlug, id);
            }
        }

        // Save old slug as alias so previously shared links still resolve
        if (slug !== currentHappening.slug) {
            await supabaseRequest('save happening slug alias', '/slug_aliases', {
                method: 'POST',
                body: {
                    entity_type: 'happening',
                    entity_id: id,
                    old_slug: currentHappening.slug,
                },
            }).catch((err: unknown) => {
                // Duplicate alias (unique index) is expected and fine to ignore
                console.warn('[updateHappening] Failed to save slug alias:', err);
            });
        }

        // Merge metadata
        const existingMetadata = currentHappening.metadata || {};
        const patchMetadata = patch.metadata ? sanitizeHappeningMetadata(patch.metadata) : {};
        const mergedMetadata: HappeningMetadata = { ...existingMetadata, ...patchMetadata };
        dropEpisodeNumberIfNotResident(patch.type ?? currentHappening.type, mergedMetadata);

        // Handle stub token generation
        if (patch.generateStubLink) {
            mergedMetadata.stub_token = randomToken();
        }

        // Store image_url in both database column (if exists) and metadata (fallback)
        let finalImageUrl: string | null = null;
        if ('image_url' in patch) {
            finalImageUrl = patch.image_url ?? null;
            mergedMetadata.image_url = finalImageUrl; // Also store in metadata as fallback
        } else if (currentHappening.image_url) {
            finalImageUrl = currentHappening.image_url;
            // Preserve existing image_url in metadata if not already set
            if (!mergedMetadata.image_url) {
                mergedMetadata.image_url = currentHappening.image_url;
            }
        } else if (mergedMetadata.image_url) {
            // If image_url is in metadata but not in current happening, use metadata value
            finalImageUrl = mergedMetadata.image_url as string | null;
        }

        // Determine final start_time and duration
        const finalStartTime = 'start_time' in patch ? patch.start_time ?? null : currentHappening.start_time;
        const finalDuration = mergedMetadata.duration_minutes ?? (currentHappening.metadata?.duration_minutes as number | undefined);
        const effectiveType = patch.type ?? currentHappening.type;
        const isShow = effectiveType === 'show' || effectiveType === 'performance' || effectiveType === 'tech';

        // For shows and performances, calculate end_time from start_time + duration if not explicitly provided
        let finalEndTime: string | null;
        if ('end_time' in patch) {
            finalEndTime = patch.end_time ?? null;
        } else if (isShow && finalStartTime && finalDuration) {
            // Recalculate end_time for shows when start_time or duration changes
            const start = new Date(finalStartTime);
            const end = new Date(start.getTime() + finalDuration * 60 * 1000);
            finalEndTime = end.toISOString();
        } else {
            finalEndTime = currentHappening.end_time;
        }

        const updatedRecord: any = {
            slug,
            title: patch.title ?? currentHappening.title,
            type: patch.type ?? currentHappening.type,
            description: 'description' in patch ? patch.description ?? null : currentHappening.description,
            start_time: toMinuteIso(finalStartTime),
            end_time: toMinuteIso(finalEndTime),
            place_id: mergedMetadata.place_id ?? null,
            is_public: typeof patch.is_public === 'boolean' ? patch.is_public : currentHappening.is_public,
            status: patch.status ?? currentHappening.status,
            metadata: mergedMetadata,
            updated_at: now(),
        };

        if ('program_id' in patch) {
            updatedRecord.program_id = patch.program_id ?? null;
        }

        // Include image_url in the update if the database column exists
        // This ensures it's persisted even if metadata gets cleared
        if (finalImageUrl !== null || ('image_url' in patch && patch.image_url === null)) {
            updatedRecord.image_url = finalImageUrl;
        }

        const updateParams = new URLSearchParams();
        updateParams.set('id', `eq.${id}`);

        let updatedRows: SupabaseHappeningsRow[];
        try {
            updatedRows = await supabaseRequest<SupabaseHappeningsRow[]>('update happening', '/happenings', {
                method: 'PATCH',
                preferReturn: true,
                body: updatedRecord,
                searchParams: updateParams,
            });

            if (!updatedRows || updatedRows.length === 0) {
                throw new Error('Unable to update happening: empty response from Supabase');
            }
        } catch (error: any) {
            // Provide more context if this is a timeout error
            if (error?.message?.includes('timeout') || error?.code === 'PGRST301') {
                throw new Error(
                    `Happening update timed out. This may be due to database load. ` +
                    `Please try again in a moment. If the issue persists, contact support. ` +
                    `(Original error: ${error.message})`
                );
            }
            throw error;
        }

        const updatedHappening = mapSupabaseHappening(updatedRows[0])

        // Trigger webhook if this is a public show
        if (updatedHappening.type === 'show' && updatedHappening.is_public) {
            const { triggerWebsiteUpdate } = await import('./webhook')
            triggerWebsiteUpdate('shows-updated', {
                happeningId: updatedHappening.id,
                showSlug: updatedHappening.slug,
            })
        }

        // Enqueue an archive_added notification when archive links are added for the
        // first time on archivable happening types (radio shows, mixes, recordings).
        // The completion-reminder scheduler picks this up and emails the artists.
        {
            const archivableTypes = new Set(['show', 'mix', 'recording'])
            const previousLinks = (currentHappening.metadata?.archive_links as ArchiveLink[] | undefined) ?? []
            const newLinks = (updatedHappening.metadata?.archive_links as ArchiveLink[] | undefined) ?? []
            const wentFromEmptyToNonEmpty = previousLinks.length === 0 && newLinks.length > 0
            if (
                archivableTypes.has(updatedHappening.type) &&
                wentFromEmptyToNonEmpty &&
                updatedHappening.account_id
            ) {
                try {
                    await createPendingCompletionNotification({
                        entity_type: 'happening',
                        entity_id: updatedHappening.id,
                        entity_name: updatedHappening.title,
                        completed_by_name: 'System',
                        completed_at: new Date().toISOString(),
                        account_id: updatedHappening.account_id,
                        notification_type: 'archive_added',
                        change_description: null,
                    })
                } catch (err) {
                    console.error('[updateHappening] Failed to enqueue archive_added notification:', err)
                    // Don't fail the update if enqueue fails — log and continue.
                }
            }
        }

        // Trigger webhook for program timetable updates. Fire if either the
        // current or previous program_id is set, so moving a happening between
        // programs revalidates both timetables.
        const previousProgramId = currentHappening.program_id ?? null
        const newProgramId = updatedHappening.program_id ?? null
        if (previousProgramId || newProgramId) {
            const { triggerWebsiteUpdate } = await import('./webhook')
            const ids = Array.from(new Set([previousProgramId, newProgramId].filter(Boolean) as string[]))
            for (const programId of ids) {
                triggerWebsiteUpdate('program-updated', {
                    programId,
                    happeningId: updatedHappening.id,
                    reason: 'happening-updated',
                })
            }
        }

        return updatedHappening
    }

    const happeningIndex = store.happenings.findIndex((h) => h.id === id);
    if (happeningIndex === -1) {
        throw new Error(`Happening ${id} not found`);
    }
    const existing = store.happenings[happeningIndex];
    let slug = existing.slug;
    if (typeof patch.slug === 'string' && patch.slug.trim()) {
        const desiredSlug = slugify(patch.slug);
        if (desiredSlug !== existing.slug) {
            slug = ensureUniqueHappeningSlugLocal(desiredSlug, id);
        }
    }

    // Merge metadata
    const existingMetadata = existing.metadata || {};
    const patchMetadata = patch.metadata ? sanitizeHappeningMetadata(patch.metadata) : {};
    const mergedMetadata: HappeningMetadata = { ...existingMetadata, ...patchMetadata };
    dropEpisodeNumberIfNotResident(patch.type ?? existing.type, mergedMetadata);

    // Handle stub token generation
    if (patch.generateStubLink) {
        mergedMetadata.stub_token = randomToken();
    }

    // Determine final start_time and duration
    const finalStartTime = 'start_time' in patch ? patch.start_time ?? null : existing.start_time;
    const finalDuration = mergedMetadata.duration_minutes ?? (existing.metadata?.duration_minutes as number | undefined);
    const isShow = (patch.type ?? existing.type) === 'show';

    // For shows, calculate end_time from start_time + duration if not explicitly provided
    let finalEndTime: string | null;
    if ('end_time' in patch) {
        finalEndTime = patch.end_time ?? null;
    } else if (isShow && finalStartTime && finalDuration) {
        // Recalculate end_time for shows when start_time or duration changes
        const start = new Date(finalStartTime);
        const end = new Date(start.getTime() + finalDuration * 60 * 1000);
        finalEndTime = end.toISOString();
    } else {
        finalEndTime = existing.end_time;
    }

    const updated: Happening = {
        ...existing,
        ...patch,
        slug,
        start_time: toMinuteIso(finalStartTime),
        end_time: toMinuteIso(finalEndTime),
        metadata: mergedMetadata,
        updated_at: now(),
    };

    store.happenings[happeningIndex] = updated;
    const cloned = clone(updated)

    // Trigger webhook if this is a public show
    if (cloned.type === 'show' && cloned.is_public) {
        const { triggerWebsiteUpdate } = await import('./webhook')
        triggerWebsiteUpdate('shows-updated', {
            happeningId: cloned.id,
            showSlug: cloned.slug,
        })
    }

    return cloned
};

/**
 * What an engineer actually needs to hear about, comparing a show's metadata
 * before and after an artist's save.
 *
 * The artist-facing form posts `gear_requests` and `rider_notes` on every save,
 * empty or not, so keying the notification off "the field was in the patch" mailed
 * the engineer every time anyone pressed save — usually to say nothing had been
 * requested. Compare the values instead: an empty form saved twice is not news.
 *
 * Returns the labels of what changed, empty when nothing did.
 */
export const technicalUpdateChanges = (
    previous: HappeningMetadata | undefined,
    next: HappeningMetadata | undefined,
): string[] => {
    // Order-insensitive, and blank-vs-missing notes count as the same request.
    const gearSignature = (requests?: GearRequest[] | null) =>
        JSON.stringify(
            (requests ?? [])
                .map((r) => ({
                    thing_id: r.thing_id ?? '',
                    name: (r.name ?? '').trim(),
                    quantity: r.quantity ?? 1,
                    notes: (r.notes ?? '').trim(),
                }))
                .sort((a, b) =>
                    `${a.thing_id}${a.name}`.localeCompare(`${b.thing_id}${b.name}`),
                ),
        );
    const notesSignature = (notes?: string | null) => (notes ?? '').trim();

    const changes: string[] = [];
    if (gearSignature(previous?.gear_requests) !== gearSignature(next?.gear_requests)) {
        changes.push('equipment requests');
    }
    if (notesSignature(previous?.rider_notes) !== notesSignature(next?.rider_notes)) {
        changes.push('technical notes');
    }
    return changes;
};

export const updateHappeningByStubToken = async (
    token: string,
    patch: HappeningUpdate,
    options?: { applyToFuture?: boolean }
): Promise<Happening> => {
    await delay();
    const normalizedToken = token?.trim();
    if (!normalizedToken) {
        throw new Error('Stub token is required');
    }

    let updatedHappening: Happening;
    let happeningId: string;
    // Snapshot for the engineer-notification diff below.
    let previousMetadata: HappeningMetadata | undefined;

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', 'id,account_id,title,series_id,start_time,metadata');
        params.set('metadata->>stub_token', `eq.${normalizedToken}`);
        params.set('limit', '1');

        const rows = await supabaseRequest<SupabaseHappeningsRow[]>('load happening by stub token', '/happenings', {
            searchParams: params,
        });

        const row = rows?.[0];
        if (!row) {
            throw new Error('Stub token is invalid or expired');
        }
        happeningId = row.id;
        const isFirstCompletion = !row.metadata?.stub_completed_at;
        previousMetadata = parseHappeningMetadata(row.metadata);
        updatedHappening = await updateHappening(row.id, patch);

        if (isFirstCompletion) {
            // Mark as completed by setting stub_completed_at in metadata (only on first completion)
            const completedAt = new Date().toISOString();
            const existingMetadata = updatedHappening.metadata || {};
            const completionParams = new URLSearchParams();
            completionParams.set('id', `eq.${happeningId}`);
            await supabaseRequest('mark happening stub completed', '/happenings', {
                method: 'PATCH',
                body: {
                    metadata: {
                        ...existingMetadata,
                        stub_completed_at: completedAt,
                        stub_completed_by_name: 'Artist', // Could be enhanced to get actual artist name
                    },
                },
                searchParams: completionParams,
            });

            // Create a pending completion notification for the scheduler to process
            if (row.account_id) {
                try {
                    await createPendingCompletionNotification({
                        entity_type: 'happening',
                        entity_id: row.id,
                        entity_name: row.title || updatedHappening.title,
                        completed_by_name: 'Artist', // Could be enhanced to get actual artist name
                        completed_at: completedAt,
                        account_id: row.account_id,
                        notification_type: 'completion',
                        change_description: null,
                    });
                } catch (err) {
                    console.error('[updateHappeningByStubToken] Failed to create pending notification:', err);
                    // Don't fail the whole operation if notification creation fails
                }
            }

            // Update the returned object with the completion timestamp
            updatedHappening = {
                ...updatedHappening,
                metadata: {
                    ...existingMetadata,
                    stub_completed_at: completedAt,
                    stub_completed_by_name: 'Artist',
                },
            };
        }
    } else {
        const happening = store.happenings.find((h) => h.metadata?.stub_token === normalizedToken);
        if (!happening) {
            throw new Error('Stub token is invalid or expired');
        }
        previousMetadata = happening.metadata;
        updatedHappening = await updateHappening(happening.id, patch);

        // Mark as completed in memory store (only on first completion)
        if (!happening.metadata?.stub_completed_at) {
            const completedAt = new Date().toISOString();
            const happeningIndex = store.happenings.findIndex((h) => h.id === happening.id);
            if (happeningIndex !== -1) {
                store.happenings[happeningIndex] = {
                    ...store.happenings[happeningIndex],
                    metadata: {
                        ...store.happenings[happeningIndex].metadata,
                        stub_completed_at: completedAt,
                        stub_completed_by_name: 'Artist',
                    },
                };
                updatedHappening = store.happenings[happeningIndex];
            }
        }
    }

    // If applyToFuture is requested and happening belongs to a series, propagate changes.
    // The series must have opted into bulk edits: this runs behind an anon stub token, so the
    // flag is enforced here rather than trusting the artist-facing form to have hidden the
    // choice. A series that never opted in silently gets a single-show edit.
    if (
        options?.applyToFuture &&
        updatedHappening.series_id &&
        updatedHappening.start_time &&
        (await seriesAllowsBulkEdits(updatedHappening.series_id))
    ) {
        await applyChangesToFutureInstances({
            seriesId: updatedHappening.series_id,
            patch,
            fromDate: updatedHappening.start_time,
        });
    }

    // Tell the engineers only when the technical asks actually changed — see
    // technicalUpdateChanges.
    {
        const changes = technicalUpdateChanges(previousMetadata, updatedHappening.metadata);

        if (changes.length > 0) {
            const accountId = updatedHappening.account_id;
            if (accountId) {
                const artists = updatedHappening.metadata?.artists;
                const artistName = artists?.[0]?.name || 'Artist';

                try {
                    await createPendingCompletionNotification({
                        entity_type: 'happening',
                        entity_id: updatedHappening.id,
                        entity_name: updatedHappening.title,
                        completed_by_name: artistName,
                        completed_at: new Date().toISOString(),
                        account_id: accountId,
                        notification_type: 'technical_update',
                        change_description: changes.join(' and ') + ' updated',
                    });
                } catch (err) {
                    console.error('[updateHappeningByStubToken] Failed to create technical update notification:', err);
                }
            }
        }
    }

    return updatedHappening;
};

/**
 * Count how many entities reference a given image object URL.
 *
 * Media is shared, not per-row: a series cover applied to every episode (via
 * applyChangesToFutureInstances) or inherited by generated instances leaves many
 * happenings pointing at the SAME object. Deleting one episode must therefore never
 * delete the underlying file while others still reference it. This is the ref-count
 * that makes `deleteMedia` on delete safe.
 *
 * Counts happenings whose `image_url` matches (optionally excluding ids already being
 * deleted) plus happening_series whose `default_metadata.image_url` matches (a series
 * default keeps the image alive for future generated episodes).
 */
export const countImageReferences = async (
    url: string,
    options: { excludeHappeningIds?: string[] } = {},
): Promise<number> => {
    await delay();
    if (!url) return 0;
    const exclude = new Set(options.excludeHappeningIds ?? []);

    if (supabaseContext) {
        let count = 0;

        const happeningParams = new URLSearchParams();
        happeningParams.set('image_url', `eq.${url}`);
        happeningParams.set('select', 'id');
        const happenings = await supabaseRequest<Array<{ id: string }>>(
            'count happenings using image', '/happenings', { searchParams: happeningParams },
        ).catch(() => [] as Array<{ id: string }>);
        count += (happenings ?? []).filter((h) => !exclude.has(h.id)).length;

        const seriesParams = new URLSearchParams();
        seriesParams.set('default_metadata->>image_url', `eq.${url}`);
        seriesParams.set('select', 'id');
        const series = await supabaseRequest<Array<{ id: string }>>(
            'count series using image', '/happening_series', { searchParams: seriesParams },
        ).catch(() => [] as Array<{ id: string }>);
        count += (series ?? []).length;

        return count;
    }

    let count = store.happenings.filter((h) => h.image_url === url && !exclude.has(h.id)).length;
    count += store.happening_series.filter(
        (s) => (s.default_metadata as HappeningMetadata | undefined)?.image_url === url,
    ).length;
    return count;
};

/**
 * Count how many happenings still reference a generated social-card object URL.
 *
 * A card normally belongs to exactly one show, but a save with "apply to future"
 * copies the whole metadata patch — card URLs included — onto the later episodes of
 * a series. Deleting one episode must therefore not pull the file out from under a
 * sibling that still points at it. Call this AFTER the row is gone, or pass the ids
 * being deleted in `excludeHappeningIds`.
 */
export const countSocialCardReferences = async (
    url: string,
    options: { excludeHappeningIds?: string[] } = {},
): Promise<number> => {
    await delay();
    if (!url) return 0;
    const exclude = new Set(options.excludeHappeningIds ?? []);
    const holdsUrl = (metadata: HappeningMetadata | undefined) =>
        Object.values(metadata?.social_cards ?? {}).includes(url);

    if (supabaseContext) {
        // Only rows that have a card map at all — a tiny slice of the table — then
        // match the URL in memory, since PostgREST cannot filter on JSON *values*.
        const params = new URLSearchParams();
        params.set('metadata->social_cards', 'not.is.null');
        params.set('select', 'id,metadata');
        let rows: Array<{ id: string; metadata: unknown }> | null;
        try {
            rows = await supabaseRequest<Array<{ id: string; metadata: unknown }>>(
                'count happenings using social card', '/happenings', { searchParams: params },
            );
        } catch (error) {
            // Fail closed: callers delete a file when this returns 0, so an unanswered
            // question has to read as "still referenced". An orphan file is cheap; a
            // card deleted out from under a live show is not.
            console.warn('[countSocialCardReferences] Lookup failed, treating as referenced:', error);
            return 1;
        }
        return (rows ?? []).filter(
            (row) => !exclude.has(row.id) && holdsUrl(parseHappeningMetadata(row.metadata)),
        ).length;
    }

    return store.happenings.filter((h) => !exclude.has(h.id) && holdsUrl(h.metadata)).length;
};

/** The generated-card object URLs held by a happening's metadata. */
const socialCardUrlsOf = (metadata: HappeningMetadata | undefined): string[] =>
    Object.values(metadata?.social_cards ?? {}).filter((url): url is string => Boolean(url));

/**
 * Delete generated card objects that nothing references any more. Best-effort:
 * a failed delete leaves an orphan file, never a broken show.
 */
const deleteUnreferencedSocialCards = async (
    urls: Iterable<string>,
    excludeHappeningIds: string[],
) => {
    for (const url of new Set(urls)) {
        const remaining = await countSocialCardReferences(url, { excludeHappeningIds });
        if (remaining === 0) {
            await deleteMedia(url);
        } else {
            console.info(
                `[socialCards] Keeping card — still referenced by ${remaining} other happening(s): ${url}`,
            );
        }
    }
};

export interface HappeningImageSharing {
    imageUrl: string | null;
    /** OTHER happenings (excluding the inspected one) that reference the same image object. */
    sharedWith: Array<{ id: string; slug: string | null; title: string | null; start_time: string | null }>;
    /** A series default cover points at this image, so future generated episodes would use it. */
    usedBySeriesDefault: boolean;
    /** True when anything other than this happening references the image. */
    isShared: boolean;
}

/**
 * Report whether a happening's cover image is shared with other shows / a series
 * default. Drives the delete dialog so the user can see, before deleting, whether
 * removing the file would break other episodes.
 */
export const getHappeningImageSharing = async (happeningId: string): Promise<HappeningImageSharing> => {
    await delay();
    const empty: HappeningImageSharing = { imageUrl: null, sharedWith: [], usedBySeriesDefault: false, isShared: false };

    let imageUrl: string | null = null;
    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${happeningId}`);
        params.set('select', 'image_url');
        const rows = await supabaseRequest<Array<{ image_url: string | null }>>(
            'get happening image', '/happenings', { searchParams: params },
        ).catch(() => [] as Array<{ image_url: string | null }>);
        imageUrl = rows?.[0]?.image_url ?? null;
    } else {
        imageUrl = store.happenings.find((h) => h.id === happeningId)?.image_url ?? null;
    }
    if (!imageUrl) return empty;

    let sharedWith: HappeningImageSharing['sharedWith'] = [];
    let usedBySeriesDefault = false;

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('image_url', `eq.${imageUrl}`);
        params.set('select', 'id,slug,title,start_time');
        const rows = await supabaseRequest<Array<{ id: string; slug: string | null; title: string | null; start_time: string | null }>>(
            'get happenings sharing image', '/happenings', { searchParams: params },
        ).catch(() => [] as Array<{ id: string; slug: string | null; title: string | null; start_time: string | null }>);
        sharedWith = (rows ?? []).filter((h) => h.id !== happeningId);

        const seriesParams = new URLSearchParams();
        seriesParams.set('default_metadata->>image_url', `eq.${imageUrl}`);
        seriesParams.set('select', 'id');
        const series = await supabaseRequest<Array<{ id: string }>>(
            'get series sharing image', '/happening_series', { searchParams: seriesParams },
        ).catch(() => [] as Array<{ id: string }>);
        usedBySeriesDefault = (series ?? []).length > 0;
    } else {
        sharedWith = store.happenings
            .filter((h) => h.image_url === imageUrl && h.id !== happeningId)
            .map((h) => ({ id: h.id, slug: h.slug ?? null, title: h.title ?? null, start_time: h.start_time ?? null }));
        usedBySeriesDefault = store.happening_series.some(
            (s) => (s.default_metadata as HappeningMetadata | undefined)?.image_url === imageUrl,
        );
    }

    return {
        imageUrl,
        sharedWith,
        usedBySeriesDefault,
        isShared: sharedWith.length > 0 || usedBySeriesDefault,
    };
};

export interface DeleteHappeningOptions {
    cascadeDeleteTaskIds?: string[];
    cascadeDeletePersonIds?: string[];
    /**
     * Also delete the cover image object from storage. Opt-in (default false): media is
     * shared across episodes, so the safe default is to keep the file and let the orphan
     * sweep reclaim it. Even when true, the object is only removed if no other happening
     * or series default still references it (see countImageReferences).
     */
    deleteMedia?: boolean;
}

export const deleteHappening = async (id: string, options: DeleteHappeningOptions = {}): Promise<void> => {
    await delay();

    // Cascade-delete tasks before deleting the happening. Tasks have a FK to happenings
    // (related_happening_id); deleting tasks first is safe and explicit.
    if (options.cascadeDeleteTaskIds?.length) {
        await Promise.all(
            options.cascadeDeleteTaskIds.map((taskId) =>
                deleteTask(taskId).catch((e) => {
                    console.error('cascade delete task failed', taskId, e);
                }),
            ),
        );
    }

    // Fetch the happening first to get its image_url, slug, and check if it's a public show
    let imageUrl: string | null | undefined = null;
    let isPublicShow = false;
    let showSlug: string | null = null;
    let programId: string | null = null;
    // Generated social cards belong to the show, so they go with it (same opt-in as
    // the cover image, and ref-counted the same way).
    let socialCardUrls: string[] = [];
    if (supabaseContext) {
        const getParams = new URLSearchParams();
        getParams.set('id', `eq.${id}`);
        getParams.set('select', 'image_url,type,is_public,slug,program_id,metadata');
        const happening = await supabaseRequest<Array<{ image_url: string | null; type: string; is_public: boolean; slug: string; program_id: string | null; metadata: unknown }>>('get happening for deletion', '/happenings', {
            searchParams: getParams,
        });
        const h = happening?.[0];
        imageUrl = h?.image_url;
        isPublicShow = h?.type === 'show' && h?.is_public === true;
        showSlug = h?.slug ?? null;
        programId = h?.program_id ?? null;
        socialCardUrls = socialCardUrlsOf(parseHappeningMetadata(h?.metadata));
    } else {
        const happening = store.happenings.find((h) => h.id === id);
        imageUrl = happening?.image_url;
        isPublicShow = happening?.type === 'show' && happening?.is_public === true;
        showSlug = happening?.slug ?? null;
        programId = happening?.program_id ?? null;
        socialCardUrls = socialCardUrlsOf(happening?.metadata);
    }

    // NOTE: media is deleted AFTER the DB row (below), and only when explicitly
    // requested AND unshared — not here. Deleting the file before the row, and
    // without a reference check, is exactly what let a single episode delete wipe
    // a series cover shared by every other episode.

    // Delete the database record
    if (supabaseContext) {
        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `eq.${id}`);

        await supabaseRequest('delete happening', '/happenings', {
            method: 'DELETE',
            searchParams: deleteParams,
        });

        // Trigger webhook if it was a public show
        if (isPublicShow) {
            const { triggerWebsiteUpdate } = await import('./webhook')
            triggerWebsiteUpdate('shows-updated', {
                happeningId: id,
                showSlug,
                deleted: true,
            })
        }

        // Trigger webhook if the deleted happening belonged to a program
        if (programId) {
            const { triggerWebsiteUpdate } = await import('./webhook')
            triggerWebsiteUpdate('program-updated', {
                programId,
                happeningId: id,
                reason: 'happening-deleted',
            })
        }
    } else {
        const happeningIndex = store.happenings.findIndex((h) => h.id === id);
        if (happeningIndex !== -1) {
            store.happenings.splice(happeningIndex, 1);

            // Trigger webhook if it was a public show
            if (isPublicShow) {
                const { triggerWebsiteUpdate } = await import('./webhook')
                triggerWebsiteUpdate('shows-updated', {
                    happeningId: id,
                    showSlug,
                    deleted: true,
                })
            }
        }
    }

    // Delete the cover image ONLY if opted-in and nothing else references it. The row
    // is already gone, so countImageReferences now reflects purely external references.
    if (imageUrl && options.deleteMedia) {
        const remaining = await countImageReferences(imageUrl, { excludeHappeningIds: [id] });
        if (remaining === 0) {
            await deleteMedia(imageUrl);
        } else {
            console.info(
                `[deleteHappening] Keeping cover image — still referenced by ${remaining} other entit${remaining === 1 ? 'y' : 'ies'}: ${imageUrl}`,
            );
        }
    }

    // Generated announcement cards are unlike the cover image: they are rendered FOR
    // this show and exist nowhere else, so they always go with it (no opt-in). The
    // ref-count still guards the applyToFuture case, where a series shares one map.
    if (socialCardUrls.length > 0) {
        await deleteUnreferencedSocialCards(socialCardUrls, [id]);
    }

    // Cascade-delete artist person records *after* the happening is gone, so the
    // happening_participants FK no longer pins them. Collect errors (e.g., person
    // still linked from another happening) and surface them after — the happening
    // itself is already deleted, so we can't unwind, but the UI should know.
    if (options.cascadeDeletePersonIds?.length) {
        const results = await Promise.all(
            options.cascadeDeletePersonIds.map((personId) =>
                deletePerson(personId)
                    .then(() => ({ ok: true as const, personId }))
                    .catch((e) => {
                        console.error('cascade delete person failed', personId, e);
                        return { ok: false as const, personId, error: e };
                    }),
            ),
        );
        const failures = results.filter((r) => !r.ok);
        if (failures.length > 0) {
            throw new Error(
                `Happening deleted, but ${failures.length} artist${failures.length === 1 ? '' : 's'} could not be removed (likely still linked to other happenings). See console for details.`,
            );
        }
    }
};

export interface DeleteHappeningsOptions {
    /** Also delete cover images (opt-in, default false). Each object is removed only if
     *  no surviving happening or series default still references it. */
    deleteMedia?: boolean;
}

export const deleteHappenings = async (ids: string[], options: DeleteHappeningsOptions = {}): Promise<void> => {
    if (ids.length === 0) return;
    await delay();

    // Collect the distinct cover images of the rows being deleted, so we can
    // ref-count them AFTER the rows are gone.
    const imageUrls = new Set<string>();
    const socialCardUrls = new Set<string>();
    if (supabaseContext) {
        const getParams = new URLSearchParams();
        getParams.set('id', `in.(${ids.join(',')})`);
        getParams.set('select', 'image_url,metadata');
        const happenings = await supabaseRequest<Array<{ image_url: string | null; metadata: unknown }>>('get happenings for deletion', '/happenings', {
            searchParams: getParams,
        });
        (happenings ?? []).forEach((h) => {
            if (h.image_url) imageUrls.add(h.image_url);
            socialCardUrlsOf(parseHappeningMetadata(h.metadata)).forEach((url) => socialCardUrls.add(url));
        });
    } else {
        ids.forEach((id) => {
            const happening = store.happenings.find((h) => h.id === id);
            if (happening?.image_url) imageUrls.add(happening.image_url);
            socialCardUrlsOf(happening?.metadata).forEach((url) => socialCardUrls.add(url));
        });
    }

    // Delete the database records first.
    if (supabaseContext) {
        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `in.(${ids.join(',')})`);

        await supabaseRequest('delete happenings', '/happenings', {
            method: 'DELETE',
            searchParams: deleteParams,
        });
    } else {
        ids.forEach((id) => {
            const happeningIndex = store.happenings.findIndex((h) => h.id === id);
            if (happeningIndex !== -1) {
                store.happenings.splice(happeningIndex, 1);
            }
        });
    }

    // Then, if opted-in, delete each cover image that nothing else references. Because
    // the rows are already gone, ref-counting handles images shared within the batch
    // and images shared with shows outside the batch alike.
    if (options.deleteMedia) {
        for (const url of imageUrls) {
            const remaining = await countImageReferences(url);
            if (remaining === 0) {
                await deleteMedia(url);
            } else {
                console.info(
                    `[deleteHappenings] Keeping cover image — still referenced by ${remaining} other entit${remaining === 1 ? 'y' : 'ies'}: ${url}`,
                );
            }
        }
    }

    // Generated announcement cards always go with their show — see deleteHappening.
    await deleteUnreferencedSocialCards(socialCardUrls, ids);
};

export const bulkUpdateHappeningStatus = async (
    ids: string[],
    status: HappeningStatus,
): Promise<void> => {
    if (ids.length === 0) return;
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `in.(${ids.join(',')})`);
        await supabaseRequest('bulk update happening status', '/happenings', {
            method: 'PATCH',
            body: { status, updated_at: now() },
            searchParams: params,
        });
        return;
    }

    const timestamp = now();
    for (const id of ids) {
        const happening = store.happenings.find((h) => h.id === id);
        if (happening) {
            happening.status = status;
            happening.updated_at = timestamp;
        }
    }
};

/**
 * Bulk-toggle is_public on every happening of a program in one round trip.
 * Excludes 'tech' happenings by default since those are internal-only.
 *
 * Returns the number of rows that were updated. Fires a single
 * `program-updated` webhook so the website can revalidate its cached
 * timetable in one shot rather than once per happening.
 */
export const setProgramHappeningsPublic = async (
    programId: string,
    isPublic: boolean,
    options: { includeTech?: boolean } = {},
): Promise<number> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('program_id', `eq.${programId}`);
        if (!options.includeTech) {
            params.set('type', 'neq.tech');
        }

        const rows = await supabaseRequest<Array<{ id: string }>>(
            'bulk publish program happenings',
            '/happenings',
            {
                method: 'PATCH',
                preferReturn: true,
                body: { is_public: isPublic, updated_at: now() },
                searchParams: params,
            },
        );

        const count = rows?.length ?? 0;
        if (count > 0) {
            const { triggerWebsiteUpdate } = await import('./webhook');
            triggerWebsiteUpdate('program-updated', {
                programId,
                reason: isPublic ? 'program-published' : 'program-unpublished',
                count,
            });
        }
        return count;
    }

    // In-memory fallback (tests/examples).
    let count = 0;
    for (const h of store.happenings) {
        if (h.program_id !== programId) continue;
        if (!options.includeTech && h.type === 'tech') continue;
        h.is_public = isPublic;
        h.updated_at = now();
        count++;
    }
    return count;
};

// ============================================================================
// RECURRING HAPPENINGS (SERIES)
// ============================================================================

const DAY_MAP: Record<DayOfWeek, number> = {
    'SU': 0, 'MO': 1, 'TU': 2, 'WE': 3, 'TH': 4, 'FR': 5, 'SA': 6
};

const DAY_ABBREV: Record<number, DayOfWeek> = {
    0: 'SU', 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA'
};

/**
 * Convert a RecurrencePattern to an RRULE string
 */
export const patternToRRule = (pattern: RecurrencePattern): string => {
    if (pattern.frequency === 'weekly') {
        return `FREQ=WEEKLY;BYDAY=${pattern.dayOfWeek}`;
    }
    const weeks = resolveWeeksOfMonth(pattern);
    if (weeks.length === 0) {
        throw new Error('Monthly recurrence requires at least one week of month');
    }
    // Comma-separated BYDAY, e.g. "2WE,4WE,-1WE". Single-week output is byte-identical
    // to the pre-multi-week implementation, so existing series rrules never churn.
    const byDay = weeks.map((w) => `${w}${pattern.dayOfWeek}`).join(',');
    if (pattern.frequency === 'bi-monthly') {
        return `FREQ=MONTHLY;INTERVAL=2;BYDAY=${byDay}`;
    }
    return `FREQ=MONTHLY;BYDAY=${byDay}`;
};

/**
 * Parse an RRULE string to a RecurrencePattern
 */
export const rruleToPattern = (rrule: string): RecurrencePattern => {
    const parts = rrule.split(';').reduce((acc, part) => {
        const [key, value] = part.split('=');
        acc[key] = value;
        return acc;
    }, {} as Record<string, string>);

    const freq = parts['FREQ'];
    const byday = parts['BYDAY'];
    const interval = parts['INTERVAL'];

    if (freq === 'WEEKLY') {
        return { frequency: 'weekly', dayOfWeek: byday as DayOfWeek };
    }

    // Monthly: BYDAY is a comma-separated list of "<n><DD>" tokens, e.g. "2WE,4WE,-1WE".
    const tokens = (byday ?? '').split(',').map((t) => t.trim()).filter(Boolean);
    if (tokens.length === 0) {
        throw new Error(`Invalid BYDAY format: ${byday}`);
    }

    const weeks: WeekOfMonth[] = [];
    let dayOfWeek: DayOfWeek | null = null;
    for (const token of tokens) {
        const match = token.match(/^(-?\d)(MO|TU|WE|TH|FR|SA|SU)$/);
        if (!match) {
            throw new Error(`Invalid BYDAY format: ${byday}`);
        }
        const week = parseInt(match[1], 10) as WeekOfMonth;
        if (!WEEK_ORDER.includes(week)) {
            throw new Error(`Unsupported week of month in BYDAY: ${token}`);
        }
        const day = match[2] as DayOfWeek;
        if (dayOfWeek && day !== dayOfWeek) {
            throw new Error(`Mixed weekdays in BYDAY are not supported: ${byday}`);
        }
        dayOfWeek = day;
        if (!weeks.includes(week)) weeks.push(week);
    }

    const weeksOfMonth = WEEK_ORDER.filter((w) => weeks.includes(w));
    const frequency = interval === '2' ? ('bi-monthly' as const) : ('monthly' as const);

    return {
        frequency,
        dayOfWeek: dayOfWeek as DayOfWeek,
        weeksOfMonth,
        // Mirror the legacy field when there is exactly one week, so any untyped
        // consumer of the old shape keeps working.
        ...(weeksOfMonth.length === 1 ? { weekOfMonth: weeksOfMonth[0] } : {}),
    };
};

/**
 * Calendar-date key (YYYY-MM-DD) in *local* time.
 *
 * Necessary because `new Date('YYYY-MM-DD')` parses as UTC midnight while
 * getNthWeekdayOfMonth() returns a local-midnight Date. Comparing those two directly
 * dropped occurrences landing exactly on the range start in any UTC+N timezone.
 */
const localDateKey = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Get the nth occurrence of a weekday in a given month
 * weekOfMonth: 1-4 for first-fourth, -1 for last
 */
const getNthWeekdayOfMonth = (year: number, month: number, dayOfWeek: DayOfWeek, weekOfMonth: WeekOfMonth): Date | null => {
    const targetDay = DAY_MAP[dayOfWeek];

    if (weekOfMonth === -1) {
        // Last occurrence: start from end of month and go backwards
        const lastDay = new Date(year, month + 1, 0);
        for (let d = lastDay.getDate(); d >= 1; d--) {
            const date = new Date(year, month, d);
            if (date.getDay() === targetDay) {
                return date;
            }
        }
        return null;
    }

    // Find nth occurrence
    let count = 0;
    for (let d = 1; d <= 31; d++) {
        const date = new Date(year, month, d);
        if (date.getMonth() !== month) break; // Went into next month
        if (date.getDay() === targetDay) {
            count++;
            if (count === weekOfMonth) {
                return date;
            }
        }
    }
    return null;
};

/**
 * Compute all occurrence dates for an RRULE within a date range
 */
export const computeOccurrences = (
    rrule: string,
    startDate: string,
    endDate: string,
    startTime: string,
    timezone: string
): Date[] => {
    const pattern = rruleToPattern(rrule);
    const start = new Date(startDate);
    const end = new Date(endDate);
    const occurrences: Date[] = [];

    // Parse time components
    const [hours, minutes, seconds] = startTime.split(':').map(Number);

    if (pattern.frequency === 'weekly') {
        const targetDay = DAY_MAP[pattern.dayOfWeek];
        const current = new Date(start);

        // Find first occurrence on or after start
        while (current.getDay() !== targetDay) {
            current.setDate(current.getDate() + 1);
        }

        while (current <= end) {
            const occurrence = new Date(current);
            occurrence.setHours(hours, minutes, seconds || 0, 0);
            occurrences.push(occurrence);
            current.setDate(current.getDate() + 7);
        }
    } else {
        // Monthly or bi-monthly, over one or more weeks of the month.
        const interval = pattern.frequency === 'bi-monthly' ? 2 : 1;
        const weeks = resolveWeeksOfMonth(pattern);

        // Compare on calendar-date strings rather than Date objects — see localDateKey().
        const startKey = startDate.slice(0, 10);
        const endKey = endDate.slice(0, 10);

        // Keyed by date so a month where the 4th weekday *is* the last one yields a
        // single occurrence (e.g. weeks [2,4,-1] in a four-Wednesday month -> 2 dates).
        const byDate = new Map<string, Date>();

        let currentYear = start.getFullYear();
        let currentMonth = start.getMonth();
        const lastMonthIndex = end.getFullYear() * 12 + end.getMonth();
        let guard = 0;

        // Bound by the end month rather than breaking on the first past-end date: with
        // several weeks selected, one week can be past `end` while an earlier one is not.
        while (currentYear * 12 + currentMonth <= lastMonthIndex && guard++ < 600) {
            for (const week of weeks) {
                const date = getNthWeekdayOfMonth(currentYear, currentMonth, pattern.dayOfWeek, week);
                if (!date) continue;

                const key = localDateKey(date);
                if (key < startKey || key > endKey) continue;
                if (byDate.has(key)) continue; // 4th vs Last collision

                date.setHours(hours, minutes, seconds || 0, 0);
                byDate.set(key, date);
            }

            // Move to next month (or skip months for bi-monthly)
            currentMonth += interval;
            while (currentMonth > 11) {
                currentMonth -= 12;
                currentYear++;
            }
        }

        occurrences.push(...Array.from(byDate.values()).sort((a, b) => a.getTime() - b.getTime()));
    }

    return occurrences;
};

type SupabaseSeriesRow = {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    account_id: string | null;
    template_id: string | null;
    happening_type: HappeningType;
    default_title: string | null;
    default_description: string | null;
    default_duration_minutes: number;
    default_place_id: string | null;
    default_is_public: boolean;
    default_status: HappeningStatus;
    default_metadata: Record<string, unknown> | null;
    default_show_type: RadioShowType | null;
    starting_episode_number: number | null;
    default_participants: SeriesParticipant[] | null;
    default_artist_ids: string[] | null;
    rotating_participants: SeriesRotation[] | null;
    allow_bulk_edits: boolean | null;
    rrule: string;
    recurrence_start_date: string;
    recurrence_end_date: string;
    default_start_time: string;
    timezone: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
};

const mapSupabaseSeries = (row: SupabaseSeriesRow): HappeningSeries => {
    return {
        id: row.id,
        slug: row.slug,
        name: row.name,
        description: row.description,
        account_id: row.account_id,
        template_id: row.template_id,
        happening_type: row.happening_type,
        default_title: row.default_title,
        default_description: row.default_description,
        default_duration_minutes: row.default_duration_minutes,
        default_place_id: row.default_place_id,
        default_is_public: row.default_is_public,
        default_status: row.default_status,
        default_metadata: sanitizeHappeningMetadata(row.default_metadata as HappeningMetadata | null),
        default_show_type: row.default_show_type,
        starting_episode_number: row.starting_episode_number ?? 1,
        default_participants: row.default_participants ?? [],
        default_artist_ids: row.default_artist_ids ?? [],
        rotating_participants: row.rotating_participants ?? [],
        allow_bulk_edits: row.allow_bulk_edits ?? false,
        rrule: row.rrule,
        recurrence_start_date: row.recurrence_start_date,
        recurrence_end_date: row.recurrence_end_date,
        default_start_time: row.default_start_time,
        timezone: row.timezone,
        is_active: row.is_active,
        created_at: row.created_at,
        updated_at: row.updated_at,
    };
};

const ensureUniqueSeriesSlug = async (baseSlug: string, currentId?: string): Promise<string> => {
    let slug = baseSlug;
    let suffix = 1;

    if (supabaseContext) {
        while (true) {
            const params = new URLSearchParams();
            params.set('select', 'id');
            params.set('slug', `eq.${slug}`);
            params.set('limit', '1');
            if (currentId) {
                params.set('id', `neq.${currentId}`);
            }
            const rows = await supabaseRequest<{ id: string }[]>('check series slug', '/happening_series', {
                searchParams: params,
            });
            if (rows.length === 0) break;
            slug = `${baseSlug}-${suffix}`;
            suffix++;
        }
    } else {
        while (store.happening_series.some((s) => s.slug === slug && (!currentId || s.id !== currentId))) {
            slug = `${baseSlug}-${suffix}`;
            suffix++;
        }
    }

    return slug;
};

export const listHappeningSeries = async (filters: HappeningSeriesFilters = {}): Promise<HappeningSeries[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('order', 'name.asc');

        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }
        if (filters.happening_type) {
            params.set('happening_type', `eq.${filters.happening_type}`);
        }
        if (typeof filters.is_active === 'boolean') {
            params.set('is_active', `eq.${filters.is_active}`);
        }
        if (filters.template_id) {
            params.set('template_id', `eq.${filters.template_id}`);
        }

        const rows = await supabaseRequest<SupabaseSeriesRow[]>('list series', '/happening_series', {
            searchParams: params,
        });
        return rows.map(mapSupabaseSeries);
    }

    return store.happening_series
        .filter((series) => {
            if (filters.happening_type && series.happening_type !== filters.happening_type) return false;
            if (typeof filters.is_active === 'boolean' && series.is_active !== filters.is_active) return false;
            if (filters.template_id && series.template_id !== filters.template_id) return false;
            return true;
        })
        .map((series) => clone(series));
};

export const getHappeningSeriesById = async (id: string): Promise<HappeningSeries | null> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('id', `eq.${id}`);
        params.set('limit', '1');

        const rows = await supabaseRequest<SupabaseSeriesRow[]>('get series by id', '/happening_series', {
            searchParams: params,
        });
        return rows.length > 0 ? mapSupabaseSeries(rows[0]) : null;
    }

    const series = store.happening_series.find((s) => s.id === id);
    return series ? clone(series) : null;
};

/**
 * Whether a series opted into "apply to this and all future shows". Off unless explicitly
 * enabled, so the safe answer is also the default for a missing or unreadable series.
 */
export const seriesAllowsBulkEdits = async (seriesId: string | null | undefined): Promise<boolean> => {
    if (!seriesId) return false;
    const series = await getHappeningSeriesById(seriesId);
    return series?.allow_bulk_edits === true;
};

export const createHappeningSeries = async (data: HappeningSeriesCreate): Promise<HappeningSeries> => {
    await delay();

    const rrule = patternToRRule(data.recurrence_pattern);
    const baseSlug = data.slug ? slugify(data.slug) : slugify(data.name);
    const slug = await ensureUniqueSeriesSlug(baseSlug);
    const timestamp = now();
    const metadata = sanitizeHappeningMetadata(data.default_metadata);

    if (supabaseContext) {
        const insertPayload = {
            slug,
            name: data.name,
            description: data.description ?? null,
            account_id: getAccountContextId(),
            template_id: data.template_id ?? null,
            happening_type: data.happening_type,
            default_title: data.default_title ?? null,
            default_description: data.default_description ?? null,
            default_duration_minutes: data.default_duration_minutes ?? 60,
            default_place_id: data.default_place_id ?? null,
            default_is_public: data.default_is_public ?? false,
            default_status: data.default_status ?? 'draft',
            default_metadata: metadata,
            default_show_type: data.default_show_type ?? null,
            starting_episode_number: data.starting_episode_number ?? 1,
            default_participants: data.default_participants ?? [],
            default_artist_ids: data.default_artist_ids ?? [],
            rotating_participants: data.rotating_participants ?? [],
            allow_bulk_edits: data.allow_bulk_edits ?? false,
            rrule,
            recurrence_start_date: data.recurrence_start_date,
            recurrence_end_date: data.recurrence_end_date,
            default_start_time: data.default_start_time,
            timezone: data.timezone ?? 'UTC',
            is_active: true,
            created_at: timestamp,
            updated_at: timestamp,
        };

        const rows = await supabaseRequest<SupabaseSeriesRow[]>('create series', '/happening_series', {
            method: 'POST',
            preferReturn: true,
            body: insertPayload,
        });

        if (!rows || rows.length === 0) {
            throw new Error('Unable to create series: empty response from Supabase');
        }

        const createdSeries = mapSupabaseSeries(rows[0]);

        // Auto-generate instances if requested
        if (data.generate_instances) {
            await generateSeriesInstances(createdSeries.id);
        }

        return createdSeries;
    }

    // In-memory store
    const series: HappeningSeries = {
        id: randomId(),
        slug,
        name: data.name,
        description: data.description ?? null,
        account_id: null,
        template_id: data.template_id ?? null,
        happening_type: data.happening_type,
        default_title: data.default_title ?? null,
        default_description: data.default_description ?? null,
        default_duration_minutes: data.default_duration_minutes ?? 60,
        default_place_id: data.default_place_id ?? null,
        default_is_public: data.default_is_public ?? false,
        default_status: data.default_status ?? 'draft',
        default_metadata: metadata,
        default_show_type: data.default_show_type ?? null,
        starting_episode_number: data.starting_episode_number ?? 1,
        default_participants: data.default_participants ?? [],
        default_artist_ids: data.default_artist_ids ?? [],
        rotating_participants: data.rotating_participants ?? [],
        allow_bulk_edits: data.allow_bulk_edits ?? false,
        rrule,
        recurrence_start_date: data.recurrence_start_date,
        recurrence_end_date: data.recurrence_end_date,
        default_start_time: data.default_start_time,
        timezone: data.timezone ?? 'UTC',
        is_active: true,
        created_at: timestamp,
        updated_at: timestamp,
    };

    store.happening_series.push(series);

    if (data.generate_instances) {
        await generateSeriesInstances(series.id);
    }

    return clone(series);
};

export const updateHappeningSeries = async (id: string, patch: HappeningSeriesUpdate): Promise<HappeningSeries> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);

        // Handle slug uniqueness if changing
        let finalSlug: string | undefined;
        if (patch.slug) {
            finalSlug = await ensureUniqueSeriesSlug(slugify(patch.slug), id);
        }

        const updatePayload: Record<string, unknown> = {
            ...patch,
            updated_at: now(),
        };
        if (finalSlug) {
            updatePayload.slug = finalSlug;
        }
        if (patch.default_metadata) {
            updatePayload.default_metadata = sanitizeHappeningMetadata(patch.default_metadata);
        }

        const rows = await supabaseRequest<SupabaseSeriesRow[]>('update series', '/happening_series', {
            method: 'PATCH',
            preferReturn: true,
            body: updatePayload,
            searchParams: params,
        });

        if (!rows || rows.length === 0) {
            throw new Error(`Series ${id} not found`);
        }

        return mapSupabaseSeries(rows[0]);
    }

    const index = store.happening_series.findIndex((s) => s.id === id);
    if (index === -1) {
        throw new Error(`Series ${id} not found`);
    }

    const existing = store.happening_series[index];
    const updated: HappeningSeries = {
        ...existing,
        ...patch,
        default_metadata: patch.default_metadata
            ? sanitizeHappeningMetadata(patch.default_metadata)
            : existing.default_metadata,
        updated_at: now(),
    };

    if (patch.slug) {
        updated.slug = await ensureUniqueSeriesSlug(slugify(patch.slug), id);
    }

    store.happening_series[index] = updated;
    return clone(updated);
};

export const deleteHappeningSeries = async (
    id: string,
    deleteInstances = false,
    options: DeleteHappeningsOptions = {},
): Promise<void> => {
    await delay();

    if (deleteInstances) {
        // Delete all happenings linked to this series
        const instances = await getSeriesInstances(id);
        if (instances.length > 0) {
            await deleteHappenings(instances.map((h) => h.id), { deleteMedia: options.deleteMedia });
        }
    }

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);

        await supabaseRequest<void>('delete series', '/happening_series', {
            method: 'DELETE',
            searchParams: params,
        });
    } else {
        const index = store.happening_series.findIndex((s) => s.id === id);
        if (index !== -1) {
            store.happening_series.splice(index, 1);
        }
    }
};

export const getSeriesInstances = async (seriesId: string): Promise<Happening[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('series_id', `eq.${seriesId}`);
        params.set('order', 'start_time.asc');

        const rows = await supabaseRequest<SupabaseHappeningsRow[]>('get series instances', '/happenings', {
            searchParams: params,
        });
        return rows.map((row) => mapSupabaseHappening(row));
    }

    return store.happenings
        .filter((h) => h.series_id === seriesId)
        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
        .map((h) => clone(h));
};

/**
 * Describe a recurrence pattern in human-readable format
 * e.g., "Weekly on Fridays", "Every 2 weeks on Saturdays"
 */
export const describeRecurrencePattern = (rrule: string): string => {
    // Parse the RRULE string
    // Format: FREQ=WEEKLY;BYDAY=FR;INTERVAL=1
    const parts = rrule.split(';').reduce((acc, part) => {
        const [key, value] = part.split('=');
        acc[key] = value;
        return acc;
    }, {} as Record<string, string>);

    const freq = parts.FREQ?.toLowerCase() || 'weekly';
    const interval = parseInt(parts.INTERVAL || '1', 10);
    const byDay = parts.BYDAY;

    // Map day codes to names
    const dayNames: Record<string, string> = {
        MO: 'Mondays',
        TU: 'Tuesdays',
        WE: 'Wednesdays',
        TH: 'Thursdays',
        FR: 'Fridays',
        SA: 'Saturdays',
        SU: 'Sundays',
    };

    let freqDesc = freq.charAt(0).toUpperCase() + freq.slice(1);
    if (interval > 1) {
        freqDesc = `Every ${interval} ${freq === 'weekly' ? 'weeks' : freq === 'daily' ? 'days' : freq === 'monthly' ? 'months' : freq}`;
    } else {
        freqDesc = freq === 'weekly' ? 'Weekly' : freq === 'daily' ? 'Daily' : freq === 'monthly' ? 'Monthly' : freqDesc;
    }

    if (byDay && freq === 'weekly') {
        const days = byDay.split(',').map(d => dayNames[d] || d).join(' and ');
        return `${freqDesc} on ${days}`;
    }

    // Monthly BYDAY is a list of "<n><DD>" tokens, e.g. "2WE,4WE,-1WE".
    if (byDay && freq === 'monthly') {
        try {
            const pattern = rruleToPattern(rrule) as MonthlyRecurrencePattern;
            const weeks = formatWeeksOfMonth(resolveWeeksOfMonth(pattern));
            const dayName = (dayNames[pattern.dayOfWeek] ?? pattern.dayOfWeek).replace(/s$/, '');
            return `${freqDesc} on the ${weeks} ${dayName}`;
        } catch {
            return freqDesc;
        }
    }

    return freqDesc;
};

export const generateSeriesInstances = async (
    seriesId: string,
    options: GenerateSeriesInstancesOptions = {}
): Promise<Happening[]> => {
    await delay();

    const series = await getHappeningSeriesById(seriesId);
    if (!series) {
        throw new Error(`Series ${seriesId} not found`);
    }

    const fromDate = options.from_date || series.recurrence_start_date;
    const toDate = options.to_date || series.recurrence_end_date;

    // Get existing instances to avoid duplicates and find max episode number
    const existingInstances = await getSeriesInstances(seriesId);
    const existingDates = new Set(existingInstances.map((h) => h.series_instance_date));

    // Episode numbers only mean anything for resident radio shows: the UI hides them for
    // every other show_type, and the only other consumer (the completion reminder
    // scheduler) is itself resident-gated.
    const usesEpisodeNumbers = series.happening_type !== 'show' || series.default_show_type === 'resident';

    // Find the highest episode number among existing instances
    let maxEpisodeNumber = series.starting_episode_number - 1;
    if (usesEpisodeNumbers) {
        for (const instance of existingInstances) {
            const epNum = instance.metadata?.episode_number;
            if (typeof epNum === 'number' && epNum > maxEpisodeNumber) {
                maxEpisodeNumber = epNum;
            }
        }
    }

    // Round-robin pools. PURE ROTATION over this run's batch: the index restarts at 0
    // every time generation is called, and only advances for instances actually created,
    // so a skipped (already-existing) date does not burn a slot.
    const rotations: SeriesRotation[] = (series.rotating_participants || []).filter(
        (r) => r?.role && Array.isArray(r.members) && r.members.length > 0
    );
    const rotationRoles = new Set(rotations.map((r) => r.role));
    let rotationIndex = 0;

    // Compute occurrences
    const occurrences = computeOccurrences(
        series.rrule,
        fromDate,
        toDate,
        series.default_start_time,
        series.timezone
    );

    const newHappenings: Happening[] = [];

    // Pre-fetch artist data if series has default artists
    let defaultArtistsData: HappeningArtist[] = [];
    if (series.default_artist_ids && series.default_artist_ids.length > 0) {
        const allPeople = await listPeople({});
        const peopleMap = new Map(allPeople.map(p => [p.id, p]));
        defaultArtistsData = series.default_artist_ids
            .map(person_id => {
                const person = peopleMap.get(person_id);
                return {
                    person_id,
                    name: person?.name ?? null,
                    slug: person?.slug ?? '',
                };
            });
    }

    for (const occurrence of occurrences) {
        const instanceDate = occurrence.toISOString().slice(0, 10); // YYYY-MM-DD

        // Skip if we already have an instance for this date
        if (existingDates.has(instanceDate)) {
            continue;
        }

        // Calculate end_time based on duration
        const endTime = new Date(occurrence.getTime() + series.default_duration_minutes * 60 * 1000);

        // Increment episode number for each new instance
        if (usesEpisodeNumbers) {
            maxEpisodeNumber++;
        }

        // Build metadata with episode number and show_type from series
        const instanceMetadata: HappeningMetadata = {
            ...series.default_metadata,
            place_id: series.default_place_id,
            ...(usesEpisodeNumbers ? { episode_number: maxEpisodeNumber } : {}),
        };

        // Add show_type if this is a radio show
        if (series.happening_type === 'show' && series.default_show_type) {
            instanceMetadata.show_type = series.default_show_type;
        }

        // Pick this instance's rotating participants. Array order is rotation order.
        const rotationAssignments: RoleAssignment[] = rotations
            .map((r) => {
                // Offset is normalised here rather than trusted: a stored value could be
                // negative, fractional or larger than the pool from an older client.
                const offset = Number.isFinite(r.start_offset) ? Math.trunc(r.start_offset as number) : 0;
                const slot = (((rotationIndex + offset) % r.members.length) + r.members.length) % r.members.length;
                const member = r.members[slot];
                return {
                    role: r.role,
                    person_id: member.person_id || null,
                    user_id: member.user_id || null,
                };
            })
            .filter((a) => a.person_id || a.user_id);

        // Convert default_participants to RoleAssignment format. A rotation wins over a
        // static default for the same role, so an instance never gets two programmers.
        const roleAssignments: RoleAssignment[] = [
            ...(series.default_participants || [])
                .filter((p) => !rotationRoles.has(p.role))
                .map(p => ({
                    role: p.role,
                    person_id: p.person_id || null,
                    user_id: p.user_id || null,
                })),
            ...rotationAssignments,
        ];

        let happening: Happening;

        // Generate stub link for series instances so artists can edit show details
        const shouldGenerateStubLink = (series.default_artist_ids && series.default_artist_ids.length > 0);

        // If series has a template, use applyTemplate to create the happening with tasks and roles
        if (series.template_id) {
            const result = await applyTemplate(
                series.template_id,
                {
                    // Use default_title if set, otherwise use series name
                    title: series.default_title || series.name,
                    type: series.happening_type,
                    description: series.default_description,
                    start_time: occurrence.toISOString(),
                    end_time: endTime.toISOString(),
                    status: series.default_status,
                    is_public: series.default_is_public,
                    metadata: instanceMetadata,
                    series_id: seriesId,
                    series_instance_date: instanceDate,
                    generateStubLink: shouldGenerateStubLink,
                },
                roleAssignments
            );
            happening = result.happening;
        } else {
            // No template - create basic happening
            happening = await createHappening({
                title: series.default_title || series.name,
                type: series.happening_type,
                description: series.default_description,
                start_time: occurrence.toISOString(),
                end_time: endTime.toISOString(),
                status: series.default_status,
                is_public: series.default_is_public,
                metadata: instanceMetadata,
                series_id: seriesId,
                series_instance_date: instanceDate,
                generateStubLink: shouldGenerateStubLink,
            });

            // Add participants from the series (only when not using a template — the
            // template branch already creates one participant per role assignment).
            // Uses roleAssignments so static defaults and rotation picks are handled
            // identically in both branches.
            for (const assignment of roleAssignments) {
                if (!assignment.person_id && !assignment.user_id) continue;
                await createHappeningParticipant({
                    happening_id: happening.id,
                    person_id: assignment.person_id,
                    user_id: assignment.user_id,
                    role: assignment.role,
                });
            }
        }

        // Add default artists from series to happening metadata (override template artists if series has its own)
        if (defaultArtistsData.length > 0) {
            // Update happening metadata with artists, preserving existing metadata from applyTemplate
            happening = await updateHappening(happening.id, {
                metadata: {
                    ...happening.metadata,
                    artists: defaultArtistsData,
                },
            });

            // Mirror them into happening_participants, as every other create/edit path
            // does. Metadata alone left generated instances without an artist role, so
            // anything reading artists off the participant join saw none.
            try {
                await syncHappeningArtists(
                    happening.id,
                    defaultArtistsData.map((a) => ({ person_id: a.person_id }))
                );
            } catch (error) {
                console.warn(`Failed to sync artists for happening ${happening.id}:`, error);
            }
        }

        // Re-fetch to get the latest metadata (including stub_token if generated)
        const updatedHappening = await getHappeningById(happening.id);
        if (updatedHappening) {
            happening = updatedHappening;
        }

        newHappenings.push(happening);

        // Guard against two occurrences resolving to the same calendar date within a
        // single run (e.g. a hand-edited rrule). computeOccurrences already dedupes, but
        // the Set built above is otherwise never updated inside this loop.
        existingDates.add(instanceDate);
        rotationIndex++;
    }

    // After creating all happenings, handle reminders and emails for series with default artists
    console.log(`[generateSeriesInstances] Created ${newHappenings.length} happenings for series ${series.name}`);
    console.log(`[generateSeriesInstances] Series default_artist_ids:`, series.default_artist_ids);

    if (newHappenings.length > 0 && series.default_artist_ids && series.default_artist_ids.length > 0) {
        console.log(`[generateSeriesInstances] Will create reminders for ${series.default_artist_ids.length} artists`);

        // Load template metadata for reminder config
        let templateMeta: Record<string, unknown> = {};
        if (series.template_id) {
            const template = await getHappeningTemplate(series.template_id);
            if (template?.metadata) {
                templateMeta = template.metadata;
            }
        }

        // Get reminder offsets from template metadata, falling back to defaults
        const happeningReminderOffsets = (templateMeta.default_happening_reminder_offsets as number[]) || [-72];
        const completionReminderOffsets = (templateMeta.default_completion_reminder_offsets as number[]) || [-120];
        const profileReminderOffsets = (templateMeta.default_profile_reminder_offsets as number[]) || [-120];
        const postShowEnabled = templateMeta.default_post_show_enabled !== false; // default true
        const postShowOffsetHours = (templateMeta.default_post_show_offset_hours as number) || 24;

        // Import functions dynamically to avoid forward reference issues
        const { createHappeningReminder: createReminder } = await import('./happening-reminders.js');
        const { createCompletionReminder } = await import('./completion-reminders.js');
        const { getPerson } = await import('./people.js');

        // Pre-fetch person data for all artists (reused across reminder types)
        const artistPersonMap = new Map<string, { email: string; name: string; hasIncompleteProfile: boolean }>();
        for (const artistId of series.default_artist_ids) {
            try {
                const person = await getPerson(artistId);
                if (person && person.email) {
                    const summary = summarizePerson(person);
                    artistPersonMap.set(artistId, {
                        email: person.email,
                        name: person.name || 'Artist',
                        hasIncompleteProfile: summary.missingFields.length > 0,
                    });
                } else {
                    console.warn(`Artist ${artistId} not found or has no email, skipping reminders`);
                }
            } catch (error) {
                console.warn(`Failed to fetch artist ${artistId}:`, error);
            }
        }

        // Auto-create happening reminders for each artist
        for (const happening of newHappenings) {
            for (const artistId of series.default_artist_ids) {
                for (const offsetHours of happeningReminderOffsets) {
                    try {
                        await createReminder({
                            happening_id: happening.id,
                            person_id: artistId,
                            offset_hours: offsetHours,
                        });
                    } catch (error) {
                        console.warn(`Failed to create happening reminder for happening ${happening.id} and artist ${artistId}:`, error);
                    }
                }
            }

            // Create completion reminders for each happening (sent to first artist)
            if (completionReminderOffsets.length > 0 && artistPersonMap.size > 0) {
                const firstArtist = artistPersonMap.values().next().value;
                if (firstArtist) {
                    for (const offsetHours of completionReminderOffsets) {
                        try {
                            await createCompletionReminder({
                                reminder_type: 'happening',
                                entity_id: happening.id,
                                recipient_email: firstArtist.email,
                                recipient_name: firstArtist.name,
                                offset_hours: offsetHours,
                                // Was null, which the completion_reminders RLS policy reads as
                                // "belongs to no account" and rejects. The row is about this
                                // happening, so naming it is both correct and sufficient — no
                                // policy change needed for this type. The scheduler's 'happening'
                                // branch resolves the entity from entity_id, so nothing else moves.
                                happening_id: happening.id,
                            });
                        } catch (error) {
                            console.warn(`Failed to create completion reminder for happening ${happening.id}:`, error);
                        }
                    }
                }
            }

            // Create profile reminders for artists with incomplete profiles
            if (profileReminderOffsets.length > 0) {
                for (const [artistId, artistData] of artistPersonMap) {
                    if (!artistData.hasIncompleteProfile) continue;
                    for (const offsetHours of profileReminderOffsets) {
                        try {
                            await createCompletionReminder({
                                reminder_type: 'profile',
                                entity_id: artistId,
                                recipient_email: artistData.email,
                                recipient_name: artistData.name,
                                offset_hours: offsetHours,
                                happening_id: happening.id,
                            });
                        } catch (error) {
                            console.warn(`Failed to create profile reminder for artist ${artistId}:`, error);
                        }
                    }
                }
            }

            // Create post-show reminders
            if (postShowEnabled) {
                for (const [artistId, artistData] of artistPersonMap) {
                    try {
                        await createCompletionReminder({
                            reminder_type: 'post_show',
                            entity_id: artistId,
                            recipient_email: artistData.email,
                            recipient_name: artistData.name,
                            offset_hours: postShowOffsetHours,
                            happening_id: happening.id,
                        });
                    } catch (error) {
                        console.warn(`Failed to create post-show reminder for artist ${artistId}:`, error);
                    }
                }
            }
        }

        // Create residency creation email reminders (will be sent by scheduler)
        // One reminder per artist
        for (const [artistId, artistData] of artistPersonMap) {
            try {
                await createCompletionReminder({
                    reminder_type: 'residency_creation',
                    entity_id: seriesId,
                    recipient_email: artistData.email,
                    recipient_name: artistData.name,
                    offset_hours: 0, // Immediate - scheduler will pick it up
                    happening_id: null,
                });
                console.log(`Created residency creation reminder for artist ${artistData.name} (${artistData.email})`);
            } catch (error) {
                console.warn(`Failed to create residency reminder for artist ${artistId}:`, error);
            }
        }
    }

    return newHappenings;
};

export const unlinkFromSeries = async (happeningId: string): Promise<Happening> => {
    return updateHappening(happeningId, {
        series_id: null,
        series_instance_date: null,
    });
};

export type PropagateSeriesOptions = {
    /** Which fields to propagate. If not specified, propagates all applicable fields. */
    fields?: ('title' | 'description' | 'artists' | 'genres' | 'show_type' | 'duration' | 'status' | 'is_public')[];
    /** Only update instances starting from this date (defaults to now) */
    from_date?: string;
};

/**
 * Propagate series changes to future instances.
 * Updates happenings linked to the series that start after the specified date.
 */
export const propagateSeriesChanges = async (
    seriesId: string,
    options: PropagateSeriesOptions = {}
): Promise<Happening[]> => {
    await delay();

    const series = await getHappeningSeriesById(seriesId);
    if (!series) {
        throw new Error(`Series ${seriesId} not found`);
    }

    // Get all instances of the series
    const instances = await getSeriesInstances(seriesId);

    // Filter to future instances only (based on from_date or now)
    const fromDate = options.from_date ? new Date(options.from_date) : new Date();
    const futureInstances = instances.filter((h) => {
        if (!h.start_time) return false;
        return new Date(h.start_time) >= fromDate;
    });

    const fields = options.fields || ['title', 'description', 'artists', 'genres', 'show_type', 'duration', 'status', 'is_public'];
    const updatedHappenings: Happening[] = [];

    for (const instance of futureInstances) {
        const patch: HappeningUpdate = {};
        const metadataPatch: Partial<HappeningMetadata> = { ...instance.metadata };
        let hasMetadataChanges = false;

        // Propagate title
        if (fields.includes('title') && series.default_title) {
            patch.title = series.default_title;
        }

        // Propagate description
        if (fields.includes('description')) {
            patch.description = series.default_description;
        }

        // Propagate status
        if (fields.includes('status')) {
            patch.status = series.default_status;
        }

        // Propagate is_public
        if (fields.includes('is_public')) {
            patch.is_public = series.default_is_public;
        }

        // Propagate artists (from default_metadata)
        if (fields.includes('artists') && series.default_metadata?.artists) {
            metadataPatch.artists = series.default_metadata.artists;
            hasMetadataChanges = true;
        }

        // Propagate genres (from default_metadata)
        if (fields.includes('genres') && series.default_metadata?.genres) {
            metadataPatch.genres = series.default_metadata.genres;
            hasMetadataChanges = true;
        }

        // Propagate show_type
        if (fields.includes('show_type') && series.default_show_type) {
            metadataPatch.show_type = series.default_show_type;
            hasMetadataChanges = true;
        }

        // Propagate duration (recalculate end_time)
        if (fields.includes('duration') && instance.start_time) {
            const startTime = new Date(instance.start_time);
            const endTime = new Date(startTime.getTime() + series.default_duration_minutes * 60 * 1000);
            patch.end_time = endTime.toISOString();
        }

        // Apply metadata changes if any
        if (hasMetadataChanges) {
            patch.metadata = metadataPatch;
        }

        // Only update if there's something to change
        if (Object.keys(patch).length > 0) {
            const updated = await updateHappening(instance.id, patch);
            updatedHappenings.push(updated);
        }
    }

    return updatedHappenings;
};

/**
 * Episode renumbering.
 *
 * Episode numbers live in `metadata.episode_number` on each instance and are assigned once,
 * at generation time, as `max(existing) + 1` (see generateSeriesInstances). Deleting an
 * instance from the middle of a series therefore leaves a permanent gap: every later episode
 * keeps its inflated number, and future generations keep counting from that inflated max.
 *
 * These two functions resequence a series gap-free from a pivot point. previewSeriesEpisodeRenumber
 * is a pure read used to show the user exactly what will change; renumberSeriesEpisodes applies
 * the same computation.
 */
export type EpisodeRenumberChange = {
    happening_id: string;
    title: string;
    start_time: string | null;
    series_instance_date: string | null;
    status: HappeningStatus;
    is_public: boolean;
    current_episode_number: number | null;
    new_episode_number: number;
    current_slug: string;
    /** The slug this episode will get, or null when the slug is deliberately left alone. */
    new_slug: string | null;
};

export type RenumberSeriesEpisodesOptions = {
    seriesId: string;
    /**
     * Resequence instances starting at this ISO datetime (inclusive). Instances before it keep
     * their numbers untouched, so already-broadcast history is never rewritten. Omit to
     * resequence the whole series.
     */
    fromDate?: string;
    /**
     * Number to give the first resequenced instance. Defaults to continuing from the instance
     * immediately before the pivot (+1), or the series' starting_episode_number when the pivot
     * is the first instance.
     */
    startAt?: number;
    /**
     * Rewrite slugs that still encode the old episode number. Only applied to instances that are
     * not public yet or are still in the future, and only when the slug is still the
     * auto-generated one — a hand-edited slug is never touched. Defaults to true.
     */
    updateSlugs?: boolean;
};

/** Resident-show instances of a series, oldest first, with unschedulable ones dropped. */
const getRenumberableInstances = async (seriesId: string): Promise<Happening[]> =>
    (await getSeriesInstances(seriesId))
        .filter((h) => h.metadata?.show_type === 'resident' || h.type !== 'show')
        .filter((h) => !!h.start_time)
        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

/**
 * A slug is safe to rewrite only when it is still exactly what createHappening would have
 * produced for the current title / episode number / date. Anything else was hand-edited (or
 * carries a uniqueness suffix we would clobber), so we leave it be.
 */
const isAutoGeneratedEpisodeSlug = (happening: Happening): boolean => {
    const currentEp = happening.metadata?.episode_number;
    if (typeof currentEp !== 'number') return false;
    const expected = buildResidentEpisodeSlug(happening.title, currentEp, happening.start_time);
    return happening.slug === expected;
};

/**
 * Compute the renumbering without writing anything. Returns only the instances whose episode
 * number actually changes, oldest first.
 */
export const previewSeriesEpisodeRenumber = async (
    options: RenumberSeriesEpisodesOptions,
): Promise<EpisodeRenumberChange[]> => {
    await delay();

    const { seriesId, fromDate, startAt, updateSlugs = true } = options;

    const series = await getHappeningSeriesById(seriesId);
    if (!series) {
        throw new Error(`Series ${seriesId} not found`);
    }

    const instances = await getRenumberableInstances(seriesId);
    const pivotIndex = fromDate
        ? instances.findIndex((h) => new Date(h.start_time as string) >= new Date(fromDate))
        : 0;
    if (pivotIndex === -1) return [];

    // Where the sequence resumes: an explicit startAt wins, otherwise continue from the last
    // instance before the pivot, otherwise fall back to the series' configured start.
    let next: number;
    if (typeof startAt === 'number' && startAt > 0) {
        next = Math.trunc(startAt);
    } else {
        const previous = instances[pivotIndex - 1];
        const previousEp = previous?.metadata?.episode_number;
        next = typeof previousEp === 'number' ? previousEp + 1 : series.starting_episode_number ?? 1;
    }

    const now = Date.now();
    const changes: EpisodeRenumberChange[] = [];

    for (const happening of instances.slice(pivotIndex)) {
        const currentEp = typeof happening.metadata?.episode_number === 'number'
            ? happening.metadata.episode_number
            : null;
        const newEp = next;
        next++;

        if (currentEp === newEp) continue;

        // Published-and-past URLs stay put; renumbering must not break a live link.
        const slugIsSafeToRewrite =
            updateSlugs &&
            (!happening.is_public || new Date(happening.start_time as string).getTime() > now) &&
            isAutoGeneratedEpisodeSlug(happening);

        changes.push({
            happening_id: happening.id,
            title: happening.title,
            start_time: happening.start_time,
            series_instance_date: happening.series_instance_date,
            status: happening.status,
            is_public: happening.is_public,
            current_episode_number: currentEp,
            new_episode_number: newEp,
            current_slug: happening.slug,
            new_slug: slugIsSafeToRewrite
                ? buildResidentEpisodeSlug(happening.title, newEp, happening.start_time)
                : null,
        });
    }

    return changes;
};

/**
 * Apply the renumbering computed by previewSeriesEpisodeRenumber. Returns the changes that were
 * written. Slug rewrites go through updateHappening, which records a slug_alias so the old URL
 * still resolves.
 */
export const renumberSeriesEpisodes = async (
    options: RenumberSeriesEpisodesOptions,
): Promise<EpisodeRenumberChange[]> => {
    const changes = await previewSeriesEpisodeRenumber(options);

    const applied: EpisodeRenumberChange[] = [];
    for (const change of changes) {
        try {
            const patch: HappeningUpdate = {
                metadata: { episode_number: change.new_episode_number },
            };
            if (change.new_slug) {
                patch.slug = change.new_slug;
            }
            await updateHappening(change.happening_id, patch);
            applied.push(change);
        } catch (err) {
            console.error(
                `[renumberSeriesEpisodes] Failed to renumber instance ${change.happening_id}:`,
                err,
            );
        }
    }

    return applied;
};

/**
 * Extending a series with more dates.
 *
 * generateSeriesInstances happily generates over a series' whole recurrence range and skips dates
 * that already have an instance, but "skip what exists" is the wrong default when extending: a
 * residency that paused has deliberately deleted instances in the middle, and regenerating over
 * the old range would resurrect them (and re-break the episode numbering). So extension always
 * starts strictly after the last instance the series already has.
 *
 * NOTE: computeOccurrences ignores its timezone argument and uses process-local time, so these
 * must be called from the admin browser, not from the API running on Fly in UTC. See CLAUDE.md.
 */
export type ExtendSeriesOptions = {
    seriesId: string;
    /** New last date for the recurrence (ISO YYYY-MM-DD). */
    toDate: string;
};

export type SeriesExtensionPreview = {
    /** First date generation will consider — the day after the series' last existing instance. */
    from_date: string;
    /** The series' current recurrence end date, which extending will move to toDate. */
    current_end_date: string;
    /** Instance dates (ISO YYYY-MM-DD) that would be created, in order. */
    dates: string[];
};

/** The day after an ISO date, as an ISO date. */
const nextIsoDate = (isoDate: string): string => {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
};

/**
 * computeOccurrences compares occurrences against `new Date(endDate)`, i.e. midnight, so a bare
 * end date silently drops a show scheduled that evening — ask to extend through the 28th and you
 * get shows through the 21st. Widening the bound to end-of-day makes the date the user picked
 * inclusive. The monthly branch compares date strings, so the suffix is harmless there.
 */
const endOfDay = (isoDate: string): string => `${isoDate}T23:59:59`;

/**
 * Work out which dates extending a series to `toDate` would add, without creating anything.
 * Returns an empty `dates` list when the new end date adds nothing.
 */
export const previewSeriesExtension = async (
    options: ExtendSeriesOptions,
): Promise<SeriesExtensionPreview> => {
    await delay();

    const { seriesId, toDate } = options;

    const series = await getHappeningSeriesById(seriesId);
    if (!series) {
        throw new Error(`Series ${seriesId} not found`);
    }

    const instances = await getSeriesInstances(seriesId);
    const lastInstanceDate = instances
        .map((h) => h.series_instance_date)
        .filter((d): d is string => !!d)
        .sort()
        .pop();

    // Start after what already exists so deliberately-deleted dates stay deleted.
    const fromDate = lastInstanceDate
        ? nextIsoDate(lastInstanceDate)
        : series.recurrence_start_date;

    const preview: SeriesExtensionPreview = {
        from_date: fromDate,
        current_end_date: series.recurrence_end_date,
        dates: [],
    };

    if (toDate < fromDate) return preview;

    const existingDates = new Set(instances.map((h) => h.series_instance_date));
    preview.dates = computeOccurrences(
        series.rrule,
        fromDate,
        endOfDay(toDate),
        series.default_start_time,
        series.timezone,
    )
        .map((occurrence) => occurrence.toISOString().slice(0, 10))
        .filter((date) => !existingDates.has(date));

    return preview;
};

/**
 * Extend a series to a new end date: generate the missing instances and move the series'
 * recurrence_end_date so the stored recurrence doesn't go stale. Returns the new instances.
 */
export const extendSeries = async (options: ExtendSeriesOptions): Promise<Happening[]> => {
    const { seriesId, toDate } = options;

    const preview = await previewSeriesExtension(options);
    if (preview.dates.length === 0) return [];

    // Same bounds the preview used, so what was shown is exactly what gets created.
    const created = await generateSeriesInstances(seriesId, {
        from_date: preview.from_date,
        to_date: endOfDay(toDate),
    });

    if (toDate > preview.current_end_date) {
        await updateHappeningSeries(seriesId, { recurrence_end_date: toDate });
    }

    return created;
};

export type ApplyToFutureOptions = {
    /** The series ID */
    seriesId: string;
    /** The happening patch to apply (same shape as a normal update) */
    patch: HappeningUpdate;
    /** Only update instances on or after this date (ISO string). The instance matching this exact start_time is excluded (already saved). */
    fromDate: string;
    /** If artists changed, sync participants for each future instance */
    artistPersonIds?: string[];
};

/**
 * Apply an edit patch directly to all future instances in a series.
 * Unlike propagateSeriesChanges() which reads from series defaults,
 * this applies the actual edit payload to each future instance.
 * Also updates series defaults so newly generated instances inherit the changes.
 */
export const applyChangesToFutureInstances = async (options: ApplyToFutureOptions): Promise<number> => {
    await delay();

    const { seriesId, patch, fromDate, artistPersonIds } = options;

    // Get all instances of the series
    const instances = await getSeriesInstances(seriesId);

    // Filter to instances strictly after fromDate (the current show is already saved)
    const cutoff = new Date(fromDate);
    const futureInstances = instances.filter((h) => {
        if (!h.start_time) return false;
        return new Date(h.start_time) > cutoff;
    });

    // Apply the patch to each future instance
    let updatedCount = 0;
    for (const instance of futureInstances) {
        try {
            await updateHappening(instance.id, patch);

            // Sync artists if provided
            if (artistPersonIds) {
                await syncHappeningArtists(
                    instance.id,
                    artistPersonIds.map((pid) => ({ person_id: pid }))
                );
            }
            updatedCount++;
        } catch (err) {
            console.error(`[applyChangesToFutureInstances] Failed to update instance ${instance.id}:`, err);
        }
    }

    // Update series defaults so newly generated instances inherit the changes
    const seriesUpdate: HappeningSeriesUpdate = {};
    if (patch.description !== undefined) {
        seriesUpdate.default_description = patch.description;
    }
    if (patch.image_url !== undefined) {
        const currentSeries = await getHappeningSeriesById(seriesId);
        if (currentSeries) {
            seriesUpdate.default_metadata = {
                ...currentSeries.default_metadata,
                image_url: patch.image_url,
            };
        }
    }
    if (patch.title !== undefined) {
        seriesUpdate.default_title = patch.title ?? null;
    }
    if (patch.status !== undefined) {
        seriesUpdate.default_status = patch.status;
    }
    if (patch.is_public !== undefined) {
        seriesUpdate.default_is_public = patch.is_public;
    }
    if (patch.metadata?.genres) {
        const currentSeries = await getHappeningSeriesById(seriesId);
        if (currentSeries) {
            seriesUpdate.default_metadata = {
                ...(seriesUpdate.default_metadata || currentSeries.default_metadata),
                genres: patch.metadata.genres,
            };
        }
    }
    if (artistPersonIds) {
        seriesUpdate.default_artist_ids = artistPersonIds;
    }

    if (Object.keys(seriesUpdate).length > 0) {
        await updateHappeningSeries(seriesId, seriesUpdate);
    }

    return updatedCount;
};

export const batchGenerateHappenings = async (options: BatchGenerateOptions): Promise<Happening[]> => {
    await delay();

    const rrule = patternToRRule(options.recurrence_pattern);
    const occurrences = computeOccurrences(
        rrule,
        options.start_date,
        options.end_date,
        options.start_time,
        options.timezone || 'UTC'
    );

    const happenings: Happening[] = [];
    const templateId = options.happening_data.metadata?.template_id as string | undefined;

    // If using a template, apply it for each occurrence
    if (templateId) {
        for (const occurrence of occurrences) {
            const result = await applyTemplate(
                templateId,
                {
                    ...options.happening_data,
                    start_time: occurrence.toISOString(),
                    series_id: null,
                    series_instance_date: null,
                },
                options.role_assignments || []
            );
            happenings.push(result.happening);
        }
    } else {
        // No template - just create happenings directly
        for (const occurrence of occurrences) {
            const happening = await createHappening({
                ...options.happening_data,
                start_time: occurrence.toISOString(),
                // Don't link to series - these are independent
                series_id: null,
                series_instance_date: null,
            });
            happenings.push(happening);
        }
    }

    return happenings;
};

export type ListPlacesFilters = {
    type?: string;
    is_active?: boolean;
    /** Case-insensitive substring match against name and slug. */
    query?: string;
    limit?: number;
};

export const listPlaces = async (filters: ListPlacesFilters = {}): Promise<Place[]> => {
    await delay();
    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('order', 'name.asc');
        if (filters.query && filters.query.trim()) {
            params.set('or', buildTextSearchFilter(['name', 'slug'], filters.query.trim()));
        }
        if (filters.limit !== undefined) {
            params.set('limit', String(filters.limit));
        }
        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }
        if (filters.type) {
            params.set('type', `eq.${filters.type}`);
        }
        if (typeof filters.is_active === 'boolean') {
            params.set('is_active', `eq.${filters.is_active}`);
        }
        return await supabaseRequest<Place[]>('list places', '/places', {
            searchParams: params,
        });
    }
    const lowerPlaceQuery = filters.query?.trim().toLowerCase();
    const matchedPlaces = store.places
        .filter((place) => {
            if (filters.type && place.type !== filters.type) {
                return false;
            }
            if (typeof filters.is_active === 'boolean' && place.is_active !== filters.is_active) {
                return false;
            }
            if (lowerPlaceQuery) {
                const haystack = `${place.name ?? ''} ${place.slug ?? ''}`.toLowerCase();
                if (!haystack.includes(lowerPlaceQuery)) return false;
            }
            return true;
        })
        .map((place) => clone(place));
    return filters.limit !== undefined ? matchedPlaces.slice(0, filters.limit) : matchedPlaces;
};

export const getPlace = async (id: string): Promise<Place | null> => {
    await delay();
    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        const result = await supabaseRequest<Place[]>(`/places/${id}`, `/places?id=eq.${id}`, {
            searchParams: params,
        });
        return result.length > 0 ? result[0] : null;
    }
    return store.places.find((place) => place.id === id) || null;
};

// Re-export types from types.ts
export type { PlaceCreate, PlaceUpdate } from './types.js';
export type { ThingCreate, ThingUpdate } from './types.js';

export const createPlace = async (data: PlaceCreate): Promise<Place> => {
    await delay();
    const timestamp = now();
    const slug = data.slug || slugify(data.name || 'place');

    if (supabaseContext) {
        const insertPayload = {
            slug,
            name: data.name,
            type: data.type,
            account_id: getAccountContextId(),
            parent_id: data.parent_id ?? null,
            address: data.address ?? null,
            emergency_contact_phone: data.emergency_contact_phone ?? null,
            is_active: data.is_active ?? true,
            metadata: data.metadata ?? {},
            created_at: timestamp,
            updated_at: timestamp,
        };

        const rows = await supabaseRequest<Place[]>('create place', '/places', {
            method: 'POST',
            preferReturn: true,
            body: insertPayload,
        });

        if (!rows || rows.length === 0) {
            throw new Error('Unable to create place: empty response from Supabase');
        }

        return rows[0];
    }

    const place: Place = {
        id: randomId(),
        slug,
        name: data.name,
        type: data.type,
        parent_id: data.parent_id ?? null,
        address: data.address ?? null,
        is_active: data.is_active ?? true,
        metadata: data.metadata ?? {},
        created_at: timestamp,
        updated_at: timestamp,
    };
    store.places.push(place);
    return clone(place);
};

export const updatePlace = async (id: string, patch: PlaceUpdate): Promise<Place> => {
    await delay();

    if (supabaseContext) {
        const updatePayload: Record<string, unknown> = {
            updated_at: now(),
        };

        if (patch.name !== undefined) updatePayload.name = patch.name;
        if (patch.slug !== undefined) updatePayload.slug = patch.slug;
        if (patch.type !== undefined) updatePayload.type = patch.type;
        if (patch.parent_id !== undefined) updatePayload.parent_id = patch.parent_id;
        if (patch.address !== undefined) updatePayload.address = patch.address;
        if (patch.emergency_contact_phone !== undefined) updatePayload.emergency_contact_phone = patch.emergency_contact_phone;
        if (patch.is_active !== undefined) updatePayload.is_active = patch.is_active;
        if (patch.metadata !== undefined) updatePayload.metadata = patch.metadata;

        const updateParams = new URLSearchParams();
        updateParams.set('id', `eq.${id}`);

        const rows = await supabaseRequest<Place[]>('update place', '/places', {
            method: 'PATCH',
            preferReturn: true,
            searchParams: updateParams,
            body: updatePayload,
        });

        if (!rows || rows.length === 0) {
            throw new Error(`Place ${id} not found`);
        }

        return rows[0];
    }

    const placeIndex = store.places.findIndex((p) => p.id === id);
    if (placeIndex === -1) {
        throw new Error(`Place ${id} not found`);
    }

    const existing = store.places[placeIndex];
    const updated: Place = {
        ...existing,
        ...patch,
        updated_at: now(),
    };
    store.places[placeIndex] = updated;
    return clone(updated);
};

export type ListThingsFilters = {
    category?: Thing['category'];
    status?: Thing['status'];
    limit?: number;
    /** Case-insensitive substring match against name and slug. */
    query?: string;
};

export const listThings = async (filters: ListThingsFilters = {}): Promise<Thing[]> => {
    await delay();
    const { category, status, limit, query } = filters;

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        if (query && query.trim()) {
            params.set('or', buildTextSearchFilter(['name', 'slug'], query.trim(), ['code']));
        }
        params.set('order', 'name.asc');

        // Filter by current account (multi-tenant isolation)
        const accountId = getAccountContextId();
        if (accountId) {
            params.set('account_id', `eq.${accountId}`);
        }

        if (category) {
            params.set('category', `eq.${category}`);
        }

        if (status) {
            params.set('status', `eq.${status}`);
        }

        if (limit) {
            params.set('limit', String(limit));
        }

        return await supabaseRequest<Thing[]>('list things', '/things', {
            searchParams: params,
        });
    }

    let result = store.things;

    if (category) {
        result = result.filter(t => t.category === category);
    }
    if (status) {
        result = result.filter(t => t.status === status);
    }
    const lowerThingQuery = query?.trim().toLowerCase();
    if (lowerThingQuery) {
        result = result.filter(t =>
            `${t.name ?? ''} ${t.slug ?? ''}`.toLowerCase().includes(lowerThingQuery) ||
            (t.code?.toLowerCase().startsWith(lowerThingQuery) ?? false)
        );
    }
    if (limit) {
        result = result.slice(0, limit);
    }

    return result.map((thing) => clone(thing));
};

export const createThing = async (data: ThingCreate): Promise<Thing> => {
    await delay();
    const timestamp = now();
    const slug = data.slug || slugify(data.name || 'thing');

    if (supabaseContext) {
        const basePayload = {
            slug,
            name: data.name,
            type: data.type,
            account_id: getAccountContextId(),
            status: data.status ?? 'available',
            tracking_mode: data.tracking_mode ?? 'lendable',
            quantity: data.quantity ?? 1,
            image_url: data.image_url ?? null,
            owner_id: data.owner_id ?? null,
            metadata: data.metadata ?? {},
            created_at: timestamp,
            updated_at: timestamp,
        };

        // Generate a unique code, retrying on the rare collision. The DB unique
        // index (account_id, code) is the real guarantee; widen the code after a
        // few clashes so a saturated account can never get stuck. A caller-
        // supplied code (e.g. the create form's previewed code) is tried first,
        // but if it collides we fall back to generating a fresh one rather than
        // failing the create — so the preview is what-you-see in the common case
        // and the user never hits a dead end on the astronomically rare clash.
        // Any non-code conflict surfaces as a real error.
        let codeLength = 5;
        let preferredCode = data.code;
        for (let attempt = 0; ; attempt += 1) {
            const code = preferredCode ?? generateThingCode(codeLength);
            try {
                const rows = await supabaseRequest<Thing[]>('create thing', '/things', {
                    method: 'POST',
                    preferReturn: true,
                    body: { ...basePayload, code },
                });
                if (!rows || rows.length === 0) {
                    throw new Error('Unable to create thing: empty response from Supabase');
                }
                return rows[0];
            } catch (err) {
                if (attempt >= 10 || !isDuplicateThingCodeError(err)) {
                    throw err;
                }
                // The colliding code (caller-supplied or generated) is dropped so
                // the next attempt generates a fresh one.
                preferredCode = undefined;
                if (attempt >= 3) codeLength += 1;
            }
        }
    }

    const thing: Thing = {
        id: randomId(),
        slug,
        code: data.code ?? generateUniqueThingCodeLocal(),
        name: data.name,
        type: data.type,
        status: data.status ?? 'available',
        tracking_mode: data.tracking_mode ?? 'lendable',
        quantity: data.quantity ?? 1,
        image_url: data.image_url ?? null,
        owner_id: data.owner_id ?? null,
        metadata: data.metadata ?? {},
        created_at: timestamp,
        updated_at: timestamp,
    };
    store.things.push(thing);
    return clone(thing);
};

export const updateThing = async (id: string, patch: ThingUpdate): Promise<Thing> => {
    await delay();

    if (supabaseContext) {
        const updatePayload: Record<string, unknown> = {
            updated_at: now(),
        };

        if (patch.name !== undefined) updatePayload.name = patch.name;
        if (patch.slug !== undefined) updatePayload.slug = patch.slug;
        if (patch.type !== undefined) updatePayload.type = patch.type;
        if (patch.status !== undefined) updatePayload.status = patch.status;
        if (patch.quantity !== undefined) updatePayload.quantity = patch.quantity;
        if (patch.tracking_mode !== undefined) updatePayload.tracking_mode = patch.tracking_mode;
        if (patch.image_url !== undefined) updatePayload.image_url = patch.image_url;
        if (patch.owner_id !== undefined) updatePayload.owner_id = patch.owner_id;
        if (patch.metadata !== undefined) updatePayload.metadata = patch.metadata;

        const updateParams = new URLSearchParams();
        updateParams.set('id', `eq.${id}`);

        const rows = await supabaseRequest<Thing[]>('update thing', '/things', {
            method: 'PATCH',
            preferReturn: true,
            searchParams: updateParams,
            body: updatePayload,
        });

        if (!rows || rows.length === 0) {
            throw new Error(`Thing ${id} not found`);
        }

        return rows[0];
    }

    const thingIndex = store.things.findIndex((t) => t.id === id);
    if (thingIndex === -1) {
        throw new Error(`Thing ${id} not found`);
    }

    const existing = store.things[thingIndex];
    const updated: Thing = {
        ...existing,
        ...patch,
        updated_at: now(),
    };
    store.things[thingIndex] = updated;
    return clone(updated);
};

export const deletePlace = async (id: string): Promise<void> => {
    await delay();

    if (supabaseContext) {
        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `eq.${id}`);

        await supabaseRequest('delete place', '/places', {
            method: 'DELETE',
            searchParams: deleteParams,
        });
    } else {
        const placeIndex = store.places.findIndex((p) => p.id === id);
        if (placeIndex !== -1) {
            store.places.splice(placeIndex, 1);
        }
    }
};

export const deletePlaces = async (ids: string[]): Promise<void> => {
    await delay();
    if (ids.length === 0) return;

    if (supabaseContext) {
        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `in.(${ids.join(',')})`);

        await supabaseRequest('delete places', '/places', {
            method: 'DELETE',
            searchParams: deleteParams,
        });
    } else {
        ids.forEach((id) => {
            const placeIndex = store.places.findIndex((p) => p.id === id);
            if (placeIndex !== -1) {
                store.places.splice(placeIndex, 1);
            }
        });
    }
};

export const deleteThing = async (id: string): Promise<void> => {
    await delay();

    if (supabaseContext) {
        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `eq.${id}`);

        await supabaseRequest('delete thing', '/things', {
            method: 'DELETE',
            searchParams: deleteParams,
        });
    } else {
        const thingIndex = store.things.findIndex((t) => t.id === id);
        if (thingIndex !== -1) {
            store.things.splice(thingIndex, 1);
        }
    }
};

export const deleteThings = async (ids: string[]): Promise<void> => {
    await delay();
    if (ids.length === 0) return;

    if (supabaseContext) {
        const deleteParams = new URLSearchParams();
        deleteParams.set('id', `in.(${ids.join(',')})`);

        await supabaseRequest('delete things', '/things', {
            method: 'DELETE',
            searchParams: deleteParams,
        });
    } else {
        ids.forEach((id) => {
            const thingIndex = store.things.findIndex((t) => t.id === id);
            if (thingIndex !== -1) {
                store.things.splice(thingIndex, 1);
            }
        });
    }
};

// ============================================================================
// THING INVENTORY TRANSACTIONS
// ============================================================================

export const createThingTransaction = async (data: ThingTransactionCreate): Promise<ThingTransaction> => {
    await delay();
    const timestamp = now();

    if (supabaseContext) {
        const insertPayload = {
            thing_id: data.thing_id,
            person_id: data.person_id,
            place_id: data.place_id ?? null,
            account_id: getAccountContextId(),
            type: data.type,
            quantity: data.quantity,
            notes: data.notes ?? null,
            created_at: timestamp,
        };

        const rows = await supabaseRequest<ThingTransaction[]>('create thing transaction', '/thing_transactions', {
            method: 'POST',
            preferReturn: true,
            body: insertPayload,
        });

        if (!rows || rows.length === 0) {
            throw new Error('Unable to create thing transaction: empty response from Supabase');
        }

        return rows[0];
    }

    const transaction: ThingTransaction = {
        id: randomId(),
        thing_id: data.thing_id,
        person_id: data.person_id,
        place_id: data.place_id ?? null,
        account_id: null,
        type: data.type,
        quantity: data.quantity,
        notes: data.notes ?? null,
        created_at: timestamp,
    };
    store.thing_transactions.push(transaction);
    return clone(transaction);
};

export const listThingTransactions = async (thingId: string, limit = 10): Promise<ThingTransaction[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('thing_id', `eq.${thingId}`);
        params.set('order', 'created_at.desc');
        params.set('limit', String(limit));

        const rows = await supabaseRequest<ThingTransaction[]>('list thing transactions', '/thing_transactions', {
            method: 'GET',
            searchParams: params,
        });

        return rows ?? [];
    }

    return clone(
        store.thing_transactions
            .filter((t) => t.thing_id === thingId)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, limit)
    );
};

export const getThingInventoryState = async (thingId: string): Promise<ThingInventoryState> => {
    await delay();

    // Fetch the thing itself
    let thing: Thing | undefined;
    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${thingId}`);
        const rows = await supabaseRequest<Thing[]>('get thing for inventory', '/things', {
            method: 'GET',
            searchParams: params,
        });
        thing = rows?.[0];
    } else {
        thing = store.things.find((t) => t.id === thingId);
    }

    if (!thing) {
        throw new Error(`Thing ${thingId} not found`);
    }

    // Fetch all transactions
    let transactions: ThingTransaction[];
    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('thing_id', `eq.${thingId}`);
        params.set('order', 'created_at.desc');
        transactions = (await supabaseRequest<ThingTransaction[]>('list thing transactions for inventory', '/thing_transactions', {
            method: 'GET',
            searchParams: params,
        })) ?? [];
    } else {
        transactions = store.thing_transactions
            .filter((t) => t.thing_id === thingId)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    // Fetch people for names
    const personIds = [...new Set(transactions.map((t) => t.person_id).filter(Boolean))] as string[];
    let peopleMap = new Map<string, string>();
    if (personIds.length > 0) {
        if (supabaseContext) {
            const params = new URLSearchParams();
            params.set('id', `in.(${personIds.join(',')})`);
            params.set('select', 'id,name');
            const people = await supabaseRequest<Array<{ id: string; name: string }>>('get people for inventory', '/people', {
                method: 'GET',
                searchParams: params,
            });
            (people ?? []).forEach((p) => peopleMap.set(p.id, p.name));
        } else {
            store.people.forEach((p) => {
                if (personIds.includes(p.id)) {
                    peopleMap.set(p.id, p.name);
                }
            });
        }
    }

    // Fetch places for names (optional "where" on checkouts)
    const placeIds = [...new Set(transactions.map((t) => t.place_id).filter(Boolean))] as string[];
    const placesMap = new Map<string, string>();
    if (placeIds.length > 0) {
        if (supabaseContext) {
            const params = new URLSearchParams();
            params.set('id', `in.(${placeIds.join(',')})`);
            params.set('select', 'id,name');
            const places = await supabaseRequest<Array<{ id: string; name: string }>>('get places for inventory', '/places', {
                method: 'GET',
                searchParams: params,
            });
            (places ?? []).forEach((p) => placesMap.set(p.id, p.name));
        } else {
            store.places.forEach((p) => {
                if (placeIds.includes(p.id)) {
                    placesMap.set(p.id, p.name);
                }
            });
        }
    }

    const recentTransactions = transactions.slice(0, 15).map((t) => ({
        ...t,
        person_name: t.person_id ? peopleMap.get(t.person_id) ?? 'Unknown' : undefined,
        place_name: t.place_id ? placesMap.get(t.place_id) ?? 'Unknown' : undefined,
    }));

    if (thing.tracking_mode === 'consumable') {
        const consumed = transactions
            .filter((t) => t.type === 'consume')
            .reduce((sum, t) => sum + t.quantity, 0);

        return {
            total_quantity: thing.quantity,
            available: Math.max(0, thing.quantity - consumed),
            checked_out: 0,
            consumed,
            active_checkouts: [],
            recent_transactions: recentTransactions,
        };
    }

    // Lendable: compute active checkouts per person
    const checkoutsByPerson = new Map<string, number>();
    const placesByPerson = new Map<string, Set<string>>();
    // transactions are ordered newest-first, so the first checkout seen per
    // person is their most recent one.
    const lastCheckoutAtByPerson = new Map<string, string>();
    for (const t of transactions) {
        if (!t.person_id) continue;
        const current = checkoutsByPerson.get(t.person_id) ?? 0;
        if (t.type === 'checkout') {
            checkoutsByPerson.set(t.person_id, current + t.quantity);
            if (!lastCheckoutAtByPerson.has(t.person_id)) {
                lastCheckoutAtByPerson.set(t.person_id, t.created_at);
            }
            if (t.place_id) {
                const name = placesMap.get(t.place_id) ?? 'Unknown';
                const set = placesByPerson.get(t.person_id) ?? new Set<string>();
                set.add(name);
                placesByPerson.set(t.person_id, set);
            }
        } else if (t.type === 'return') {
            checkoutsByPerson.set(t.person_id, current - t.quantity);
        }
    }

    const activeCheckouts: ThingInventoryState['active_checkouts'] = [];
    let totalCheckedOut = 0;
    for (const [personId, qty] of checkoutsByPerson) {
        if (qty > 0) {
            const places = placesByPerson.get(personId);
            activeCheckouts.push({
                person_id: personId,
                person_name: peopleMap.get(personId) ?? 'Unknown',
                quantity: qty,
                place_name: places && places.size > 0 ? [...places].join(', ') : undefined,
                checked_out_at: lastCheckoutAtByPerson.get(personId),
            });
            totalCheckedOut += qty;
        }
    }

    return {
        total_quantity: thing.quantity,
        available: Math.max(0, thing.quantity - totalCheckedOut),
        checked_out: totalCheckedOut,
        consumed: 0,
        active_checkouts: activeCheckouts,
        recent_transactions: recentTransactions,
    };
};

export type PersonSummary = {
    missingFields: string[];
    hasInviteLink: boolean;
};

export const summarizePerson = (person: Person): PersonSummary => {
    const missingFields: string[] = [];
    if (person.type === 'artist') {
        if (!person.name) missingFields.push('Name');
        if (!person.email) missingFields.push('Email');
        if (!person.phone) missingFields.push('Phone');
        if (!person.bio) missingFields.push('Bio');
        if (!person.picture_url) missingFields.push('Picture');
    }
    return {
        missingFields,
        hasInviteLink: Boolean(person.invite_token),
    };
};

const DEFAULT_INVITE_BASE_URL = 'http://localhost:5173';
const INVITE_BASE_ENV_KEYS = [
    'ADMIN_BASE_URL',
    'VITE_ADMIN_BASE_URL',
    'ARTIST_PORTAL_URL',
    'ARTIST_INVITE_BASE_URL',
    'VITE_ARTIST_PORTAL_URL',
    'VITE_ARTIST_INVITE_BASE_URL',
    'NEXT_PUBLIC_ARTIST_PORTAL_URL',
    'PUBLIC_ARTIST_PORTAL_URL',
    'REACT_APP_ARTIST_PORTAL_URL',
];

const resolveInviteBaseUrl = (origin?: string) => {
    const clean = (value: string) => value.replace(/\/$/, '');
    if (origin?.trim()) {
        return clean(origin.trim());
    }

    const source = resolveEnvObject();
    for (const key of INVITE_BASE_ENV_KEYS) {
        const candidate = source[key];
        if (candidate && candidate.trim()) {
            return clean(candidate.trim());
        }
    }

    return DEFAULT_INVITE_BASE_URL;
};

export const generateInviteLink = (person: Person, origin?: string) => {
    if (!person.invite_token) {
        return null;
    }
    const base = resolveInviteBaseUrl(origin);
    return `${base}/people/${person.slug}?invite=${person.invite_token}`;
};

/**
 * Build a staff member's personal timetable link. Reuses the person's invite
 * token as the no-auth credential, but points at the read-only staff schedule
 * view rather than the artist profile form.
 */
export const generateStaffTimetableLink = (person: Person, origin?: string) => {
    if (!person.invite_token) {
        return null;
    }
    const base = resolveInviteBaseUrl(origin);
    return `${base}/staff/${person.slug}?token=${person.invite_token}`;
};

// Happenings helpers
export type HappeningSummary = {
    missingFields: string[];
    hasStubLink: boolean;
    isReadyToPublish: boolean;
};

export const summarizeHappening = (happening: Happening): HappeningSummary => {
    const missingFields: string[] = [];
    const metadata = happening.metadata || {};
    const isShow = happening.type === 'show';
    const isResidentShow = isShow && metadata.show_type === 'resident';

    // Description is always mandatory
    if (!happening.description?.trim()) {
        missingFields.push('Description');
    }

    // Title is mandatory for resident shows
    if (isResidentShow && !happening.title?.trim()) {
        missingFields.push('Show title');
    }

    // Episode number is mandatory for resident shows
    if (isResidentShow && !metadata.episode_number) {
        missingFields.push('Episode number');
    }

    // For shows, at least one artist is required
    if (isShow && (!metadata.artists || metadata.artists.length === 0)) {
        missingFields.push('Artists');
    }

    // Shows must have artwork. Artists reliably fill in their artist photo and forget
    // the show photo, so it is tracked as a hard requirement and chased by reminders.
    if (isShow && !happening.image_url?.trim()) {
        missingFields.push('Show photo');
    }

    // Start time and duration are required for shows
    if (isShow) {
        if (!happening.start_time) {
            missingFields.push('Start time');
        }
        const metadata = happening.metadata || {};
        if (!metadata.duration_minutes) {
            missingFields.push('Duration');
        }
    }

    return {
        missingFields,
        hasStubLink: Boolean(metadata.stub_token),
        isReadyToPublish: missingFields.length === 0,
    };
};

const DEFAULT_SHOW_BASE_URL = 'http://localhost:5173';
const SHOW_BASE_ENV_KEYS = [
    'ADMIN_BASE_URL',
    'VITE_ADMIN_BASE_URL',
    'SHOW_PORTAL_URL',
    'SHOW_INVITE_BASE_URL',
    'VITE_SHOW_PORTAL_URL',
    'VITE_SHOW_INVITE_BASE_URL',
    'NEXT_PUBLIC_SHOW_PORTAL_URL',
    'PUBLIC_SHOW_PORTAL_URL',
    'REACT_APP_SHOW_PORTAL_URL',
];

const resolveShowBaseUrl = (origin?: string) => {
    const clean = (value: string) => value.replace(/\/$/, '');
    if (origin?.trim()) {
        return clean(origin.trim());
    }

    const source = resolveEnvObject();
    for (const key of SHOW_BASE_ENV_KEYS) {
        const candidate = source[key];
        if (candidate && candidate.trim()) {
            return clean(candidate.trim());
        }
    }

    return DEFAULT_SHOW_BASE_URL;
};

export const generateHappeningStubLink = (happening: Happening, origin?: string) => {
    const stubToken = happening.metadata?.stub_token;
    if (!stubToken) {
        return null;
    }
    const base = resolveShowBaseUrl(origin);
    return `${base}/shows/${happening.slug}?stub=${stubToken}`;
};

/**
 * The combined invite: one link that carries both credentials, so the artist
 * completes their profile and fills in the show details in a single sitting.
 * This is the link programmers should be handing out by default — the
 * single-purpose builders above are the fallback for when only one half applies.
 *
 * Uses `resolveShowBaseUrl` rather than `resolveInviteBaseUrl` because
 * `/invite/:personSlug/:showSlug` is a route of the admin SPA, the same app that
 * serves `/shows/:slug`. Both resolvers try ADMIN_BASE_URL/VITE_ADMIN_BASE_URL
 * first so they agree in every real deployment; they can only diverge if someone
 * sets ARTIST_PORTAL_URL and SHOW_PORTAL_URL to different hosts, and in that case
 * the show origin is the right one — the stub token is the show-scoped credential.
 *
 * Guards on slug as well as token: the route is addressed by slug but validated by
 * token, so a missing slug yields a dead URL rather than an honest error page.
 */
export const generateCombinedInviteLink = (
    person: Person,
    happening: Happening,
    origin?: string,
) => {
    const stubToken = happening.metadata?.stub_token;
    if (!person.invite_token || !stubToken || !person.slug || !happening.slug) {
        return null;
    }
    const base = resolveShowBaseUrl(origin);
    return `${base}${buildCombinedInvitePath(person.slug, happening.slug, person.invite_token, stubToken)}`;
};

// Search helpers for autocomplete
export const searchGenres = async (query: string): Promise<string[]> => {
    await delay();
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) return [];

    // Get all unique genres from existing happenings
    const allGenres = new Set<string>();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', 'metadata');
        const rows = await supabaseRequest<{ metadata: Record<string, unknown> | null }[]>(
            'search genres',
            '/happenings',
            { searchParams: params }
        );
        rows.forEach((row) => {
            const genres = row.metadata?.genres;
            if (Array.isArray(genres)) {
                genres.forEach((g) => {
                    if (typeof g === 'string') allGenres.add(g);
                });
            }
        });
    } else {
        store.happenings.forEach((h) => {
            h.metadata?.genres?.forEach((g) => allGenres.add(g));
        });
    }

    // Filter by query
    return Array.from(allGenres)
        .filter((genre) => genre.toLowerCase().includes(normalizedQuery))
        .slice(0, 10);
};

// ============================================================================
// TEMPLATES & WORKFLOWS FUNCTIONS
// ============================================================================

// In-memory stores for new entities
const templateStore: {
    templates: HappeningTemplate[];
    taskDefinitions: TaskDefinition[];
    submissions: ParticipantSubmission[];
    participants: HappeningParticipant[];
    requirements: HappeningRequirement[];
} = {
    templates: [],
    taskDefinitions: [],
    submissions: [],
    participants: [],
    requirements: [],
};

// --- Happening Templates ---

export const listHappeningTemplates = async (
    filters: HappeningTemplateFilters = {}
): Promise<HappeningTemplate[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', '*');
        params.set('order', 'name.asc');

        if (filters.happening_type) {
            params.set('happening_type', `eq.${filters.happening_type}`);
        }
        if (filters.is_active !== undefined) {
            params.set('is_active', `eq.${filters.is_active}`);
        }
        if (!filters.include_system) {
            // By default, show both system and custom templates
            // If include_system is explicitly false, filter them out
            if (filters.include_system === false) {
                params.set('is_system', 'eq.false');
            }
        }

        const rows = await supabaseRequest<HappeningTemplate[]>(
            'list templates',
            '/happening_templates',
            { searchParams: params }
        );
        return rows;
    }

    // In-memory fallback
    let result = [...templateStore.templates];
    if (filters.happening_type) {
        result = result.filter((t) => t.happening_type === filters.happening_type);
    }
    if (filters.is_active !== undefined) {
        result = result.filter((t) => t.is_active === filters.is_active);
    }
    if (filters.include_system === false) {
        result = result.filter((t) => !t.is_system);
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
};

export const getHappeningTemplate = async (id: string): Promise<HappeningTemplate | null> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        const rows = await supabaseRequest<HappeningTemplate[]>(
            'get template',
            '/happening_templates',
            { searchParams: params }
        );
        return rows[0] || null;
    }

    return templateStore.templates.find((t) => t.id === id) || null;
};

export const getHappeningTemplateBySlug = async (slug: string): Promise<HappeningTemplate | null> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('slug', `eq.${slug}`);
        const rows = await supabaseRequest<HappeningTemplate[]>(
            'get template by slug',
            '/happening_templates',
            { searchParams: params }
        );
        return rows[0] || null;
    }

    return templateStore.templates.find((t) => t.slug === slug) || null;
};

export const createHappeningTemplate = async (
    data: HappeningTemplateCreate
): Promise<HappeningTemplate> => {
    await delay();

    const template: HappeningTemplate = {
        id: randomId(),
        slug: data.slug,
        name: data.name,
        description: data.description || null,
        happening_type: data.happening_type,
        account_id: getAccountContextId(),
        required_roles: data.required_roles || [],
        default_duration_minutes: data.default_duration_minutes || 60,
        default_status: data.default_status || 'draft',
        default_is_public: data.default_is_public || false,
        default_requirements: data.default_requirements || [],
        is_active: true,
        is_system: false,
        metadata: data.metadata || {},
        created_at: now(),
        updated_at: now(),
    };

    if (supabaseContext) {
        const [created] = await supabaseRequest<HappeningTemplate[]>(
            'create template',
            '/happening_templates',
            { method: 'POST', body: template, preferReturn: true }
        );
        return created;
    }

    templateStore.templates.push(template);
    return clone(template);
};

export const updateHappeningTemplate = async (
    id: string,
    data: HappeningTemplateUpdate
): Promise<HappeningTemplate> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        const [updated] = await supabaseRequest<HappeningTemplate[]>(
            'update template',
            '/happening_templates',
            { method: 'PATCH', body: { ...data, updated_at: now() }, searchParams: params, preferReturn: true }
        );
        return updated;
    }

    const idx = templateStore.templates.findIndex((t) => t.id === id);
    if (idx === -1) throw new Error('Template not found');
    templateStore.templates[idx] = { ...templateStore.templates[idx], ...data, updated_at: now() };
    return clone(templateStore.templates[idx]);
};

export const deleteHappeningTemplate = async (id: string): Promise<void> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        await supabaseRequest<void>('delete template', '/happening_templates', {
            method: 'DELETE',
            searchParams: params,
        });
        return;
    }

    const idx = templateStore.templates.findIndex((t) => t.id === id);
    if (idx !== -1) templateStore.templates.splice(idx, 1);
};

// --- Task Definitions ---

export const listTaskDefinitions = async (templateId: string): Promise<TaskDefinition[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('template_id', `eq.${templateId}`);
        params.set('order', 'sort_order.asc');
        return await supabaseRequest<TaskDefinition[]>('list task definitions', '/task_definitions', {
            searchParams: params,
        });
    }

    return templateStore.taskDefinitions
        .filter((td) => td.template_id === templateId)
        .sort((a, b) => a.sort_order - b.sort_order);
};

export const createTaskDefinition = async (data: TaskDefinitionCreate): Promise<TaskDefinition> => {
    await delay();

    const definition: TaskDefinition = {
        id: randomId(),
        template_id: data.template_id,
        task_key: data.task_key,
        title: data.title,
        description: data.description || null,
        type: data.type || 'preparation',
        priority: data.priority || 'medium',
        due_offset_days: data.due_offset_days || 0,
        due_offset_hours: data.due_offset_hours || 0,
        assignee_role: data.assignee_role || null,
        depends_on: data.depends_on || [],
        auto_complete_on_happening_status: data.auto_complete_on_happening_status || null,
        auto_create_on_happening_status: data.auto_create_on_happening_status || 'draft',
        subtask_keys: data.subtask_keys || [],
        sort_order: data.sort_order || 0,
        is_required: data.is_required !== false,
        reminder_enabled: data.reminder_enabled ?? false,
        reminder_offset_hours: data.reminder_offset_hours ?? 0,
        metadata: data.metadata || {},
        created_at: now(),
        updated_at: now(),
    };

    if (supabaseContext) {
        const [created] = await supabaseRequest<TaskDefinition[]>(
            'create task definition',
            '/task_definitions',
            { method: 'POST', body: definition, preferReturn: true }
        );
        return created;
    }

    templateStore.taskDefinitions.push(definition);
    return clone(definition);
};

export const updateTaskDefinition = async (
    id: string,
    data: TaskDefinitionUpdate
): Promise<TaskDefinition> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        const [updated] = await supabaseRequest<TaskDefinition[]>(
            'update task definition',
            '/task_definitions',
            { method: 'PATCH', body: { ...data, updated_at: now() }, searchParams: params, preferReturn: true }
        );
        return updated;
    }

    const idx = templateStore.taskDefinitions.findIndex((td) => td.id === id);
    if (idx === -1) throw new Error('Task definition not found');
    templateStore.taskDefinitions[idx] = { ...templateStore.taskDefinitions[idx], ...data, updated_at: now() };
    return clone(templateStore.taskDefinitions[idx]);
};

export const deleteTaskDefinition = async (id: string): Promise<void> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        await supabaseRequest<void>('delete task definition', '/task_definitions', {
            method: 'DELETE',
            searchParams: params,
        });
        return;
    }

    const idx = templateStore.taskDefinitions.findIndex((td) => td.id === id);
    if (idx !== -1) templateStore.taskDefinitions.splice(idx, 1);
};

// --- Participant Submissions ---

export const getParticipantSubmission = async (
    happeningParticipantId: string
): Promise<ParticipantSubmission | null> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('happening_participant_id', `eq.${happeningParticipantId}`);
        const rows = await supabaseRequest<ParticipantSubmission[]>(
            'get participant submission',
            '/participant_submissions',
            { searchParams: params }
        );
        return rows[0] || null;
    }

    return templateStore.submissions.find((s) => s.happening_participant_id === happeningParticipantId) || null;
};

export const createParticipantSubmission = async (
    data: ParticipantSubmissionCreate
): Promise<ParticipantSubmission> => {
    await delay();

    const submission: ParticipantSubmission = {
        id: randomId(),
        happening_participant_id: data.happening_participant_id,
        account_id: getAccountContextId(),
        gear_requests: data.gear_requests || [],
        rider_notes: data.rider_notes || null,
        dietary_restrictions: data.dietary_restrictions || null,
        arrival_time: data.arrival_time || null,
        departure_time: data.departure_time || null,
        submitted_at: now(),
        metadata: data.metadata || {},
        created_at: now(),
        updated_at: now(),
    };

    if (supabaseContext) {
        const [created] = await supabaseRequest<ParticipantSubmission[]>(
            'create participant submission',
            '/participant_submissions',
            { method: 'POST', body: submission, preferReturn: true }
        );
        return created;
    }

    templateStore.submissions.push(submission);
    return clone(submission);
};

export const updateParticipantSubmission = async (
    id: string,
    data: ParticipantSubmissionUpdate
): Promise<ParticipantSubmission> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        const [updated] = await supabaseRequest<ParticipantSubmission[]>(
            'update participant submission',
            '/participant_submissions',
            { method: 'PATCH', body: { ...data, updated_at: now() }, searchParams: params, preferReturn: true }
        );
        return updated;
    }

    const idx = templateStore.submissions.findIndex((s) => s.id === id);
    if (idx === -1) throw new Error('Submission not found');
    templateStore.submissions[idx] = { ...templateStore.submissions[idx], ...data, updated_at: now() };
    return clone(templateStore.submissions[idx]);
};

// --- Happening Participants ---

// Type for Supabase response with joined data
type SupabaseParticipantRow = {
    id: string;
    happening_id: string;
    person_id: string | null;
    user_id: string | null;
    role: string;
    sort_order: number;
    metadata: Record<string, unknown>;
    created_at: string;
    people: { name: string } | null;
    users: { name: string } | null;
};

export const listHappeningParticipants = async (happeningId: string): Promise<HappeningParticipant[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('happening_id', `eq.${happeningId}`);
        // Join to get person/user names
        params.set('select', '*,people(name),users(name)');
        params.set('order', 'sort_order.asc,created_at.asc');

        const rows = await supabaseRequest<SupabaseParticipantRow[]>(
            'list happening participants',
            '/happening_participants',
            { searchParams: params }
        );

        // Map to include names at top level
        return rows.map(row => ({
            id: row.id,
            happening_id: row.happening_id,
            person_id: row.person_id,
            user_id: row.user_id,
            role: row.role,
            sort_order: row.sort_order ?? 0,
            metadata: row.metadata,
            created_at: row.created_at,
            person_name: row.people?.name ?? null,
            user_name: row.users?.name ?? null,
        }));
    }

    return templateStore.participants.filter((p) => p.happening_id === happeningId);
};

export const listHappeningParticipantsBatch = async (
    happeningIds: string[]
): Promise<HappeningParticipant[]> => {
    await delay();

    if (happeningIds.length === 0) return [];

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('happening_id', `in.(${happeningIds.join(',')})`);
        // Only the columns the UI actually consumes for the participant filter
        // dropdown. Avoid `select=*` so the response stays small and parsing
        // doesn't dominate the main thread on big festivals.
        params.set('select', 'id,happening_id,person_id,user_id,role,sort_order,people(name),users(name)');

        const rows = await supabaseRequest<SupabaseParticipantRow[]>(
            'list happening participants batch',
            '/happening_participants',
            { searchParams: params }
        );

        return rows.map(row => ({
            id: row.id,
            happening_id: row.happening_id,
            person_id: row.person_id,
            user_id: row.user_id,
            role: row.role,
            sort_order: row.sort_order ?? 0,
            metadata: row.metadata ?? null,
            created_at: row.created_at ?? '',
            person_name: row.people?.name ?? null,
            user_name: row.users?.name ?? null,
        }));
    }

    return templateStore.participants.filter((p) => happeningIds.includes(p.happening_id));
};

/**
 * Every participant holding `role` across the account, with no happening_id
 * filter. Used to populate the happenings participant-filter dropdown.
 *
 * The previous approach passed the currently visible happening IDs to
 * listHappeningParticipantsBatch under a fixed React Query key, so the cached
 * answer belonged to whichever page happened to load first and went stale
 * against every later page. It also built a multi-kilobyte `in.(...)` URL that
 * tripped CORS preflights. Scoping by role instead keeps the URL short, the key
 * honest, and the dropdown complete. RLS scopes the rows to the account via the
 * parent happening.
 */
/**
 * The distinct participant roles in use across the account.
 *
 * Deriving these from the happenings on screen would make the role filter
 * options come and go with the page, which is the same trap the programmer
 * dropdown fell into.
 */
export const listParticipantRoles = async (): Promise<string[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', 'role');

        const rows = await supabaseRequest<Array<{ role: string }>>(
            'list participant roles',
            '/happening_participants',
            { searchParams: params }
        );

        return Array.from(new Set(rows.map((r) => r.role).filter(Boolean))).sort();
    }

    return Array.from(
        new Set(templateStore.participants.map((p) => p.role).filter(Boolean))
    ).sort();
};

export const listParticipantsByRole = async (role: string): Promise<HappeningParticipant[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('role', `eq.${role}`);
        params.set('select', 'id,happening_id,person_id,user_id,role,sort_order,people(name),users(name)');

        const rows = await supabaseRequest<SupabaseParticipantRow[]>(
            'list participants by role',
            '/happening_participants',
            { searchParams: params }
        );

        return rows.map(row => ({
            id: row.id,
            happening_id: row.happening_id,
            person_id: row.person_id,
            user_id: row.user_id,
            role: row.role,
            sort_order: row.sort_order ?? 0,
            metadata: row.metadata ?? {},
            created_at: row.created_at ?? '',
            person_name: row.people?.name ?? null,
            user_name: row.users?.name ?? null,
        }));
    }

    return templateStore.participants.filter((p) => p.role === role);
};

export const createHappeningParticipant = async (
    data: HappeningParticipantCreate
): Promise<HappeningParticipant> => {
    await delay();

    // Validate that at most one of person_id or user_id is provided
    // Allow creating unassigned role slots (both null) to represent open requirements
    if (data.person_id && data.user_id) {
        throw new Error('Cannot provide both person_id and user_id');
    }

    const participant: HappeningParticipant = {
        id: randomId(),
        happening_id: data.happening_id,
        person_id: data.person_id || null,
        user_id: data.user_id || null,
        role: data.role,
        sort_order: data.sort_order ?? 0,
        metadata: data.metadata || {},
        created_at: now(),
    };

    if (supabaseContext) {
        const [created] = await supabaseRequest<HappeningParticipant[]>(
            'create happening participant',
            '/happening_participants',
            { method: 'POST', body: participant, preferReturn: true }
        );
        return created;
    }

    templateStore.participants.push(participant);
    return clone(participant);
};

export type HappeningParticipantUpdate = Partial<Pick<HappeningParticipant, 'person_id' | 'user_id' | 'role' | 'metadata' | 'sort_order'>>;

export const updateHappeningParticipant = async (
    id: string,
    patch: HappeningParticipantUpdate
): Promise<HappeningParticipant> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        const [updated] = await supabaseRequest<HappeningParticipant[]>(
            'update happening participant',
            '/happening_participants',
            { method: 'PATCH', searchParams: params, body: patch, preferReturn: true }
        );
        return updated;
    }

    const idx = templateStore.participants.findIndex((p) => p.id === id);
    if (idx === -1) {
        throw new Error('Participant not found');
    }
    const current = templateStore.participants[idx];
    const updated: HappeningParticipant = {
        ...current,
        person_id: patch.person_id ?? current.person_id,
        user_id: patch.user_id ?? current.user_id,
        role: patch.role ?? current.role,
        sort_order: patch.sort_order ?? current.sort_order,
        metadata: patch.metadata ?? current.metadata,
    };
    templateStore.participants[idx] = updated;
    return clone(updated);
};

export const deleteHappeningParticipant = async (id: string): Promise<void> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        await supabaseRequest<void>('delete happening participant', '/happening_participants', {
            method: 'DELETE',
            searchParams: params,
        });
        return;
    }

    const idx = templateStore.participants.findIndex((p) => p.id === id);
    if (idx !== -1) templateStore.participants.splice(idx, 1);
};

/**
 * Sync artists to the happening_participants table.
 * This ensures artist data is normalized and kept fresh via joins to the people table.
 *
 * @param happeningId - The happening ID to sync artists for
 * @param artists - Array of artists with person_id to sync
 */
export const syncHappeningArtists = async (
    happeningId: string,
    artists: { person_id: string }[]
): Promise<void> => {
    await delay();

    // Get existing participants for this happening
    const existingParticipants = await listHappeningParticipants(happeningId);
    const existingArtistParticipants = existingParticipants.filter(p => p.role === 'artist');

    // Create sets for comparison
    const existingPersonIds = new Set(
        existingArtistParticipants
            .filter(p => p.person_id !== null)
            .map(p => p.person_id as string)
    );
    const newPersonIds = new Set(artists.map(a => a.person_id));

    // Find artists to add (in new list but not in existing)
    const artistsToAdd = artists.filter(a => !existingPersonIds.has(a.person_id));

    // Find participants to remove (in existing but not in new list)
    const participantsToRemove = existingArtistParticipants.filter(
        p => p.person_id && !newPersonIds.has(p.person_id)
    );

    // Add missing artists
    for (const artist of artistsToAdd) {
        await createHappeningParticipant({
            happening_id: happeningId,
            person_id: artist.person_id,
            role: 'artist',
        });
    }

    // Remove artists no longer in the list
    for (const participant of participantsToRemove) {
        await deleteHappeningParticipant(participant.id);
    }
};

// --- Happening Requirements ---

export const listHappeningRequirements = async (happeningId: string): Promise<HappeningRequirement[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('happening_id', `eq.${happeningId}`);
        return await supabaseRequest<HappeningRequirement[]>(
            'list happening requirements',
            '/happening_requirements',
            { searchParams: params }
        );
    }

    return templateStore.requirements.filter((r) => r.happening_id === happeningId);
};

export const createHappeningRequirement = async (
    data: HappeningRequirementCreate
): Promise<HappeningRequirement> => {
    await delay();

    const requirement: HappeningRequirement = {
        id: randomId(),
        happening_id: data.happening_id,
        thing_id: data.thing_id,
        quantity: data.quantity || 1,
        notes: data.notes || null,
        requested_by_participant_id: data.requested_by_participant_id || null,
        request_source: data.request_source || 'manual',
        status: data.status || 'requested',
        created_at: now(),
    };

    if (supabaseContext) {
        const [created] = await supabaseRequest<HappeningRequirement[]>(
            'create happening requirement',
            '/happening_requirements',
            { method: 'POST', body: requirement, preferReturn: true }
        );
        return created;
    }

    templateStore.requirements.push(requirement);
    return clone(requirement);
};

export const updateHappeningRequirement = async (
    id: string,
    data: HappeningRequirementUpdate
): Promise<HappeningRequirement> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        const [updated] = await supabaseRequest<HappeningRequirement[]>(
            'update happening requirement',
            '/happening_requirements',
            { method: 'PATCH', body: data, searchParams: params, preferReturn: true }
        );
        return updated;
    }

    const idx = templateStore.requirements.findIndex((r) => r.id === id);
    if (idx === -1) throw new Error('Requirement not found');
    templateStore.requirements[idx] = { ...templateStore.requirements[idx], ...data };
    return clone(templateStore.requirements[idx]);
};

export const deleteHappeningRequirement = async (id: string): Promise<void> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${id}`);
        await supabaseRequest<void>('delete happening requirement', '/happening_requirements', {
            method: 'DELETE',
            searchParams: params,
        });
        return;
    }

    const idx = templateStore.requirements.findIndex((r) => r.id === id);
    if (idx !== -1) templateStore.requirements.splice(idx, 1);
};

// --- Place Availability ---

export const checkPlaceAvailability = async (
    placeId: string,
    startTime: string,
    endTime: string,
    excludeHappeningId?: string
): Promise<PlaceAvailabilityResult> => {
    await delay();

    // Validate inputs
    if (!placeId || !startTime || !endTime) {
        throw new Error('Place ID, start time, and end time are required for availability check');
    }

    console.log('[checkPlaceAvailability] Checking availability:', {
        placeId,
        startTime,
        endTime,
        excludeHappeningId,
    });

    // Get place to check capacity
    let capacity: number | null = null;
    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('id', `eq.${placeId}`);
        params.set('select', 'metadata');
        const places = await supabaseRequest<{ metadata: Record<string, unknown> | null }[]>(
            'get place',
            '/places',
            { searchParams: params }
        );
        if (places[0]?.metadata?.concurrent_happenings) {
            capacity = places[0].metadata.concurrent_happenings as number;
        }
    }

    // Find overlapping happenings
    let overlapping: { id: string; title: string; start_time: string; end_time: string }[] = [];

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('select', 'id,title,start_time,end_time');
        params.set('place_id', `eq.${placeId}`);
        params.set('status', 'not.eq.cancelled');
        // Filter to ensure both start and end times exist, and check for overlap
        // Overlap condition: start_time < endTime AND end_time > startTime
        params.set('start_time', `lt.${endTime}`);
        params.set('end_time', `gt.${startTime}`);
        if (excludeHappeningId) {
            params.set('id', `not.eq.${excludeHappeningId}`);
        }

        console.log('[checkPlaceAvailability] Supabase query params:', {
            select: 'id,title,start_time,end_time',
            place_id: `eq.${placeId}`,
            status: 'not.eq.cancelled',
            start_time: `lt.${endTime}`,
            end_time: `gt.${startTime}`,
            id: excludeHappeningId ? `not.eq.${excludeHappeningId}` : 'N/A',
        });

        overlapping = await supabaseRequest<typeof overlapping>(
            'check place availability',
            '/happenings',
            { searchParams: params }
        );

        console.log('[checkPlaceAvailability] Found overlapping happenings:', overlapping);

        // Filter results to exclude those with missing start or end times
        // (shouldn't happen but ensures consistency with in-memory filter)
        overlapping = overlapping.filter(h => h.start_time && h.end_time);
    } else {
        overlapping = store.happenings
            .filter((h) => {
                if (h.place_id !== placeId) return false;
                if (h.status === 'cancelled') return false;
                if (excludeHappeningId && h.id === excludeHappeningId) return false;
                if (!h.start_time || !h.end_time) return false;
                // Check overlap
                return h.start_time < endTime && h.end_time > startTime;
            })
            .map((h) => ({
                id: h.id,
                title: h.title,
                start_time: h.start_time!,
                end_time: h.end_time!,
            }));
    }

    const concurrent_count = overlapping.length;
    const available = capacity === null || concurrent_count < capacity;

    console.log('[checkPlaceAvailability] Result:', {
        available,
        concurrent_count,
        capacity,
        conflicts: overlapping,
    });

    return {
        available,
        conflicts: overlapping.map((h) => ({
            happening_id: h.id,
            happening_title: h.title,
            start_time: h.start_time,
            end_time: h.end_time,
        })),
        capacity,
        concurrent_count,
    };
};

// --- Apply Template ---

export type RoleAssignment = {
    role: string;
    person_id?: string | null; // For external people (artists)
    user_id?: string | null;   // For internal users (staff)
};

export const applyTemplate = async (
    templateId: string,
    happeningData: HappeningCreate & { start_time: string },
    roleAssignments?: RoleAssignment[]
): Promise<ApplyTemplateResult> => {
    await delay();

    // Get template
    const template = await getHappeningTemplate(templateId);
    if (!template) {
        throw new Error('Template not found');
    }

    // Get task definitions
    const taskDefinitions = await listTaskDefinitions(templateId);

    // Calculate end_time from duration
    const startDate = new Date(happeningData.start_time);
    const endDate = new Date(startDate.getTime() + template.default_duration_minutes * 60 * 1000);

    // Use template name as default title if not provided
    const title = happeningData.title?.trim() || template.name;

    // Get default_show_type from template metadata (for show templates)
    const defaultShowType = template.happening_type === 'show' && template.metadata?.default_show_type
        ? (template.metadata.default_show_type as RadioShowType)
        : undefined;

    // Get default_artists from template metadata (for resident shows)
    const defaultArtists = template.happening_type === 'show' && template.metadata?.default_artists
        ? (template.metadata.default_artists as HappeningArtist[])
        : undefined;

    // Get default place_id from template metadata
    const templatePlaceId = template.metadata?.place_id as string | undefined;

    // Merge metadata, ensuring show_type is set from template if not already provided
    const mergedMetadata: HappeningMetadata = {
        ...happeningData.metadata,
        duration_minutes: template.default_duration_minutes,
        template_id: templateId,
        required_roles: template.required_roles, // Store for later editing
    };

    // Set place_id from template if not already set (check for null/undefined/empty string)
    if (templatePlaceId && (!mergedMetadata.place_id || mergedMetadata.place_id === '')) {
        mergedMetadata.place_id = templatePlaceId;
    }

    // Set show_type from template if it's a show and show_type isn't already set
    if (template.happening_type === 'show' && defaultShowType && !mergedMetadata.show_type) {
        mergedMetadata.show_type = defaultShowType;
    }

    // Set default artists from template if not already provided (or if empty array)
    if (defaultArtists && defaultArtists.length > 0 && (!mergedMetadata.artists || mergedMetadata.artists.length === 0)) {
        mergedMetadata.artists = defaultArtists;
    }

    // Create the happening
    const happening = await createHappening({
        ...happeningData,
        title,
        type: template.happening_type,
        status: happeningData.status || template.default_status,
        is_public: happeningData.is_public ?? template.default_is_public,
        end_time: happeningData.end_time || endDate.toISOString(),
        metadata: mergedMetadata,
    });

    // Create tasks from definitions FIRST (before participants, so trigger can find them)
    const taskMap = new Map<string, Task>(); // task_key -> created task
    const tasks: Task[] = [];

    // Sort definitions by dependencies (simple topological sort)
    const sortedDefinitions = [...taskDefinitions].sort((a, b) => {
        if (a.depends_on.includes(b.task_key)) return 1;
        if (b.depends_on.includes(a.task_key)) return -1;
        return a.sort_order - b.sort_order;
    });

    if (typeof window !== 'undefined') {
        console.log('📋 Task definitions found:', taskDefinitions.length);
        console.log('📋 Happening status:', happening.status);
    }

    for (const def of sortedDefinitions) {
        // Create tasks - for new happenings, create all tasks that match the status
        // or if auto_create_on_happening_status is 'draft' (default)
        const shouldCreate = def.auto_create_on_happening_status === happening.status ||
            def.auto_create_on_happening_status === 'draft';

        if (typeof window !== 'undefined') {
            console.log(`📋 Task def "${def.title}": auto_create_on=${def.auto_create_on_happening_status}, shouldCreate=${shouldCreate}, assignee_role=${def.assignee_role}`);
        }

        if (!shouldCreate) {
            continue;
        }

        // Calculate due date
        const dueDate = new Date(startDate);
        dueDate.setDate(dueDate.getDate() + def.due_offset_days);
        dueDate.setHours(dueDate.getHours() + def.due_offset_hours);

        // Find parent task if this is a subtask
        let parentTaskId: string | null = null;
        for (const [key, task] of taskMap) {
            const parentDef = taskDefinitions.find((td) => td.task_key === key);
            if (parentDef?.subtask_keys.includes(def.task_key)) {
                parentTaskId = task.id;
                break;
            }
        }

        const task = await createTask({
            title: def.title,
            type: def.type,
            description: def.description,
            priority: def.priority,
            status: 'todo',
            due_date: dueDate.toISOString(),
            parent_task_id: parentTaskId,
            template_task_key: def.task_key,
            related_happening_id: happening.id,
            sort_order: def.sort_order,
            assignee_role: def.assignee_role, // Copy role for auto-assignment
            reminder_enabled: def.reminder_enabled ?? false,
            reminder_offset_hours: def.reminder_offset_hours ?? 0,
        });

        taskMap.set(def.task_key, task);
        tasks.push(task);
    }

    // Create participants from role assignments AFTER tasks exist
    // This allows the trigger to auto-assign tasks
    const participants: HappeningParticipant[] = [];
    if (roleAssignments && roleAssignments.length > 0) {
        for (const assignment of roleAssignments) {
            // Skip if no person or user assigned
            if (!assignment.person_id && !assignment.user_id) continue;

            const participant = await createHappeningParticipant({
                happening_id: happening.id,
                person_id: assignment.person_id || null,
                user_id: assignment.user_id || null,
                role: assignment.role,
            });
            participants.push(participant);
        }
    }

    // Create placeholder participants for required roles (unassigned slots)
    if (template.required_roles && template.required_roles.length > 0) {
        const assignedCountByRole = new Map<string, number>();
        for (const ra of roleAssignments ?? []) {
            assignedCountByRole.set(ra.role, (assignedCountByRole.get(ra.role) ?? 0) + 1);
        }
        for (const roleSpec of template.required_roles) {
            const min = roleSpec.min_count ?? 0;
            if (min <= 0) continue;
            const alreadyAssigned = assignedCountByRole.get(roleSpec.role) ?? 0;
            const needed = Math.max(min - alreadyAssigned, 0);
            for (let i = 0; i < needed; i++) {
                const placeholder = await createHappeningParticipant({
                    happening_id: happening.id,
                    role: roleSpec.role,
                    person_id: null,
                    user_id: null,
                    metadata: {},
                });
                participants.push(placeholder);
            }
        }
    }

    // Create default requirements from template
    const requirements: HappeningRequirement[] = [];
    // Note: In a real implementation, we'd look up things by type
    // For now, default_requirements are informational only

    return {
        happening,
        tasks,
        requirements,
        participants,
    };
};

// --- Subtask Helpers ---

export const listSubtasks = async (parentTaskId: string): Promise<Task[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('parent_task_id', `eq.${parentTaskId}`);
        params.set('order', 'sort_order.asc');
        return await supabaseRequest<Task[]>('list subtasks', '/tasks', { searchParams: params });
    }

    return store.tasks
        .filter((t) => t.parent_task_id === parentTaskId)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

export const getTaskWithSubtasks = async (taskId: string): Promise<Task & { subtasks: Task[] }> => {
    await delay();

    // Get parent task
    const tasks = await listTasks({});
    const task = tasks.find((t) => t.id === taskId);
    if (!task) {
        throw new Error('Task not found');
    }

    // Get subtasks
    const subtasks = await listSubtasks(taskId);

    return { ...task, subtasks };
};

// --- Backlinks ---

export const getHappeningsForPerson = async (personId: string): Promise<Happening[]> => {
    await delay();

    if (supabaseContext) {
        // First get all participant records for this person
        const params = new URLSearchParams();
        params.set('person_id', `eq.${personId}`);
        params.set('select', 'happening_id');
        const participants = await supabaseRequest<{ happening_id: string }[]>(
            'get participations',
            '/happening_participants',
            { searchParams: params }
        );

        if (participants.length === 0) return [];

        // Then get the happenings
        const happeningIds = participants.map((p) => p.happening_id);
        const happeningParams = new URLSearchParams();
        happeningParams.set('id', `in.(${happeningIds.join(',')})`);
        happeningParams.set('order', 'start_time.desc');

        const rows = await supabaseRequest<SupabaseHappeningsRow[]>(
            'get happenings for person',
            '/happenings',
            { searchParams: happeningParams }
        );
        return rows.map(mapSupabaseRowToHappening);
    }

    // In-memory: find all happenings where person is a participant
    const participantHappeningIds = templateStore.participants
        .filter((p) => p.person_id === personId)
        .map((p) => p.happening_id);

    return store.happenings
        .filter((h) => participantHappeningIds.includes(h.id))
        .sort((a, b) => (b.start_time || '').localeCompare(a.start_time || ''));
};

export const getTasksForHappening = async (happeningId: string): Promise<Task[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('related_happening_id', `eq.${happeningId}`);
        params.set('order', 'sort_order.asc,due_date.asc');
        return await supabaseRequest<Task[]>('get tasks for happening', '/tasks', { searchParams: params });
    }

    return store.tasks
        .filter((t) => t.related_happening_id === happeningId)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

export const getTasksForPerson = async (personId: string): Promise<Task[]> => {
    await delay();

    if (supabaseContext) {
        const params = new URLSearchParams();
        params.set('assigned_to_id', `eq.${personId}`);
        params.set('order', 'due_date.asc.nullslast');
        return await supabaseRequest<Task[]>('get tasks for person', '/tasks', { searchParams: params });
    }

    return store.tasks
        .filter((t) => t.assigned_to_id === personId)
        .sort((a, b) => {
            if (!a.due_date && !b.due_date) return 0;
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return a.due_date.localeCompare(b.due_date);
        });
};

// Re-export user types and functions
export type { User as UserAdmin, UserRole as UserRoleAdmin, UserCreate, UserUpdate, UserInvite, UserInviteAccept, UserSession } from './types.js';
export {
    getUser,
    getUserByEmail,
    // listUsers is now defined in this file with proper multi-tenant support
    createUser,
    updateUser,
    getUserPasswordChangedAt,
    updateUserLastLogin,
    changeUserPassword,
    deleteUser,
    inviteUser,
    getUserByInviteToken,
    acceptInvite,
    listPendingInvites,
    cancelInvite,
    validateUserLogin,
    validateUserLoginByEmail,
    getUserSession,
    canUserCreateUsers,
    createOwnerUser,
    adminListUsers,
    adminCreateUser,
    adminDeleteUser,
} from './users.js';

// Re-export password utilities for frontend use
export {
    generateSalt,
    hashPassword,
    hashNewPassword,
    verifyPassword,
    validatePasswordStrength,
} from './password.js';

// Dependency checking types
export type EntityDependency = {
    type: string;
    count: number;
    items: Array<{ id: string; name: string }>;
};

export type DependencyCheckResult = {
    hasDependencies: boolean;
    dependencies: EntityDependency[];
};

// Dependency checking functions (using supabaseRequest pattern)
export const checkHappeningDependencies = async (happeningId: string): Promise<DependencyCheckResult> => {
    await delay();
    const dependencies: EntityDependency[] = [];

    if (supabaseContext) {
        // Check tasks
        const taskParams = new URLSearchParams();
        taskParams.set('related_happening_id', `eq.${happeningId}`);
        taskParams.set('select', 'id,title');
        try {
            const tasks = await supabaseRequest<Array<{ id: string; title: string }>>('check happening tasks', '/tasks', {
                searchParams: taskParams,
            });
            if (tasks && tasks.length > 0) {
                dependencies.push({
                    type: 'tasks',
                    count: tasks.length,
                    items: tasks.map((t) => ({ id: t.id, name: t.title })),
                });
            }
        } catch (error) {
            // Ignore errors, just means no tasks
        }

        // Participants: relationship rows; the people remain by default. We surface them
        // here so the UI can offer optional cascade deletion of the artist records themselves.
        const participantParams = new URLSearchParams();
        participantParams.set('happening_id', `eq.${happeningId}`);
        participantParams.set('select', 'person_id,people(id,name)');
        try {
            const rows = await supabaseRequest<Array<{ person_id: string; people: { id: string; name: string } | null }>>(
                'check happening participants',
                '/happening_participants',
                { searchParams: participantParams },
            );
            if (rows && rows.length > 0) {
                const items = rows
                    .map((r) => r.people)
                    .filter((p): p is { id: string; name: string } => !!p);
                if (items.length > 0) {
                    dependencies.push({
                        type: 'participants',
                        count: items.length,
                        items,
                    });
                }
            }
        } catch {
            // ignore
        }

        // Note: Requirements are just relationship records, not entities that get deleted.
        // The requirement links will be removed (CASCADE), but the things themselves remain.
        // So we don't show requirements as dependencies.
    } else {
        // In-memory fallback
        const tasks = store.tasks.filter((t) => t.related_happening_id === happeningId);
        if (tasks.length > 0) {
            dependencies.push({
                type: 'tasks',
                count: tasks.length,
                items: tasks.map((t) => ({ id: t.id, name: t.title })),
            });
        }
    }

    return {
        hasDependencies: dependencies.length > 0,
        dependencies,
    };
};

export const checkTaskDependencies = async (taskId: string): Promise<DependencyCheckResult> => {
    await delay();
    const dependencies: EntityDependency[] = [];

    if (supabaseContext) {
        // Check subtasks
        const subtaskParams = new URLSearchParams();
        subtaskParams.set('parent_task_id', `eq.${taskId}`);
        subtaskParams.set('select', 'id,title');
        try {
            const subtasks = await supabaseRequest<Array<{ id: string; title: string }>>('check task subtasks', '/tasks', {
                searchParams: subtaskParams,
            });
            if (subtasks && subtasks.length > 0) {
                dependencies.push({
                    type: 'subtasks',
                    count: subtasks.length,
                    items: subtasks.map((t) => ({ id: t.id, name: t.title })),
                });
            }
        } catch (error) {
            // Ignore errors
        }

        // Check comments
        const commentParams = new URLSearchParams();
        commentParams.set('task_id', `eq.${taskId}`);
        commentParams.set('select', 'id');
        try {
            const comments = await supabaseRequest<Array<{ id: string }>>('check task comments', '/task_comments', {
                searchParams: commentParams,
            });
            if (comments && comments.length > 0) {
                dependencies.push({
                    type: 'comments',
                    count: comments.length,
                    items: comments.map((c) => ({ id: c.id, name: `Comment #${c.id.slice(0, 8)}` })),
                });
            }
        } catch (error) {
            // Ignore errors
        }
    } else {
        // In-memory fallback
        const subtasks = store.tasks.filter((t) => t.parent_task_id === taskId);
        if (subtasks.length > 0) {
            dependencies.push({
                type: 'subtasks',
                count: subtasks.length,
                items: subtasks.map((t) => ({ id: t.id, name: t.title })),
            });
        }
    }

    return {
        hasDependencies: dependencies.length > 0,
        dependencies,
    };
};

export const checkPersonDependencies = async (personId: string): Promise<DependencyCheckResult> => {
    await delay();
    const dependencies: EntityDependency[] = [];

    if (supabaseContext) {
        // Check happening participants
        const participantParams = new URLSearchParams();
        participantParams.set('person_id', `eq.${personId}`);
        participantParams.set('select', 'id,happening_id');
        try {
            const participants = await supabaseRequest<Array<{ id: string; happening_id: string }>>('check person participants', '/happening_participants', {
                searchParams: participantParams,
            });
            if (participants && participants.length > 0) {
                const happeningIds = participants.map((p) => p.happening_id).filter((id): id is string => !!id && id.trim() !== '');
                if (happeningIds.length > 0) {
                    const happeningParams = new URLSearchParams();
                    happeningParams.set('id', `in.(${happeningIds.join(',')})`);
                    happeningParams.set('select', 'id,title');
                    const happenings = await supabaseRequest<Array<{ id: string; title: string }>>('get happenings for person', '/happenings', {
                        searchParams: happeningParams,
                    });
                    dependencies.push({
                        type: 'happening_participants',
                        count: participants.length,
                        items: (happenings || []).map((h) => ({ id: h.id, name: h.title })),
                    });
                }
            }
        } catch (error) {
            // Ignore errors
        }

        // Check assigned tasks
        const taskParams = new URLSearchParams();
        taskParams.set('assigned_to_id', `eq.${personId}`);
        taskParams.set('select', 'id,title');
        try {
            const tasks = await supabaseRequest<Array<{ id: string; title: string }>>('check person tasks', '/tasks', {
                searchParams: taskParams,
            });
            if (tasks && tasks.length > 0) {
                dependencies.push({
                    type: 'assigned_tasks',
                    count: tasks.length,
                    items: tasks.map((t) => ({ id: t.id, name: t.title })),
                });
            }
        } catch (error) {
            // Ignore errors
        }
    } else {
        // In-memory fallback
        const tasks = store.tasks.filter((t) => t.assigned_to_id === personId);
        if (tasks.length > 0) {
            dependencies.push({
                type: 'assigned_tasks',
                count: tasks.length,
                items: tasks.map((t) => ({ id: t.id, name: t.title })),
            });
        }
    }

    return {
        hasDependencies: dependencies.length > 0,
        dependencies,
    };
};

export const checkPlaceDependencies = async (placeId: string): Promise<DependencyCheckResult> => {
    await delay();
    const dependencies: EntityDependency[] = [];

    if (supabaseContext) {
        // Check child places
        const childParams = new URLSearchParams();
        childParams.set('parent_id', `eq.${placeId}`);
        childParams.set('select', 'id,name');
        try {
            const childPlaces = await supabaseRequest<Array<{ id: string; name: string }>>('check child places', '/places', {
                searchParams: childParams,
            });
            if (childPlaces && childPlaces.length > 0) {
                dependencies.push({
                    type: 'child_places',
                    count: childPlaces.length,
                    items: childPlaces.map((p) => ({ id: p.id, name: p.name })),
                });
            }
        } catch (error) {
            // Ignore errors
        }

        // Check happenings
        const happeningParams = new URLSearchParams();
        happeningParams.set('place_id', `eq.${placeId}`);
        happeningParams.set('select', 'id,title');
        try {
            const happenings = await supabaseRequest<Array<{ id: string; title: string }>>('check place happenings', '/happenings', {
                searchParams: happeningParams,
            });
            if (happenings && happenings.length > 0) {
                dependencies.push({
                    type: 'happenings',
                    count: happenings.length,
                    items: happenings.map((h) => ({ id: h.id, name: h.title })),
                });
            }
        } catch (error) {
            // Ignore errors
        }

        // Check related tasks
        const taskParams = new URLSearchParams();
        taskParams.set('related_place_id', `eq.${placeId}`);
        taskParams.set('select', 'id,title');
        try {
            const tasks = await supabaseRequest<Array<{ id: string; title: string }>>('check place tasks', '/tasks', {
                searchParams: taskParams,
            });
            if (tasks && tasks.length > 0) {
                dependencies.push({
                    type: 'tasks',
                    count: tasks.length,
                    items: tasks.map((t) => ({ id: t.id, name: t.title })),
                });
            }
        } catch (error) {
            // Ignore errors
        }
    } else {
        // In-memory fallback
        const childPlaces = store.places.filter((p) => p.parent_id === placeId);
        if (childPlaces.length > 0) {
            dependencies.push({
                type: 'child_places',
                count: childPlaces.length,
                items: childPlaces.map((p) => ({ id: p.id, name: p.name })),
            });
        }
    }

    return {
        hasDependencies: dependencies.length > 0,
        dependencies,
    };
};

export const checkThingDependencies = async (thingId: string): Promise<DependencyCheckResult> => {
    await delay();
    const dependencies: EntityDependency[] = [];

    if (supabaseContext) {
        // Check happening requirements
        const requirementParams = new URLSearchParams();
        requirementParams.set('thing_id', `eq.${thingId}`);
        requirementParams.set('select', 'id,happening_id');
        try {
            const requirements = await supabaseRequest<Array<{ id: string; happening_id: string }>>('check thing requirements', '/happening_requirements', {
                searchParams: requirementParams,
            });
            if (requirements && requirements.length > 0) {
                const happeningIds = requirements.map((r) => r.happening_id).filter((id): id is string => !!id && id.trim() !== '');
                if (happeningIds.length > 0) {
                    const happeningParams = new URLSearchParams();
                    happeningParams.set('id', `in.(${happeningIds.join(',')})`);
                    happeningParams.set('select', 'id,title');
                    const happenings = await supabaseRequest<Array<{ id: string; title: string }>>('get happenings for thing', '/happenings', {
                        searchParams: happeningParams,
                    });
                    dependencies.push({
                        type: 'happening_requirements',
                        count: requirements.length,
                        items: (happenings || []).map((h) => ({ id: h.id, name: h.title })),
                    });
                }
            }
        } catch (error) {
            // Ignore errors
        }

        // Check related tasks
        const taskParams = new URLSearchParams();
        taskParams.set('related_thing_id', `eq.${thingId}`);
        taskParams.set('select', 'id,title');
        try {
            const tasks = await supabaseRequest<Array<{ id: string; title: string }>>('check thing tasks', '/tasks', {
                searchParams: taskParams,
            });
            if (tasks && tasks.length > 0) {
                dependencies.push({
                    type: 'tasks',
                    count: tasks.length,
                    items: tasks.map((t) => ({ id: t.id, name: t.title })),
                });
            }
        } catch (error) {
            // Ignore errors
        }
    }

    return {
        hasDependencies: dependencies.length > 0,
        dependencies,
    };
};

export const checkBulkDependencies = async (
    entityType: 'happening' | 'task' | 'person' | 'place' | 'thing',
    entityIds: string[]
): Promise<Map<string, DependencyCheckResult>> => {
    await delay();
    const results = new Map<string, DependencyCheckResult>();

    // Check dependencies for each entity
    for (const id of entityIds) {
        let result: DependencyCheckResult;
        switch (entityType) {
            case 'happening':
                result = await checkHappeningDependencies(id);
                break;
            case 'task':
                result = await checkTaskDependencies(id);
                break;
            case 'person':
                result = await checkPersonDependencies(id);
                break;
            case 'place':
                result = await checkPlaceDependencies(id);
                break;
            case 'thing':
                result = await checkThingDependencies(id);
                break;
            default:
                result = { hasDependencies: false, dependencies: [] };
        }
        results.set(id, result);
    }

    return results;
};

// Bulk delete functions are now defined above in this file

// Re-export client functions from client.ts (these are used by newer modules like tasks.ts and task-reminders.ts)
export { initClient as initDalClient, getClient, setAccountTokenGetter } from './client.js';
export type { DalConfig, DalConfigWithAdmin } from './client.js';
export { loadConfigFromEnv as loadDalConfigFromEnv } from './config.js';

// Import getter registrars to wire request context into the client
import { setAccountTokenGetter, setServiceRolePreferenceGetter } from './client.js';

// Register the account token getter with the client after getAccountToken is defined
// This allows the client to add x-account-token header to requests
setAccountTokenGetter(getAccountToken);
// Let getClient() consult the request's service-role preference
setServiceRolePreferenceGetter(getServiceRolePreference);

// Re-export task reminder functions
export {
    getTaskReminders,
    createTaskReminder,
    updateTaskReminder,
    deleteTaskReminder,
    deleteTaskReminders
} from './task-reminders.js';
export type { TaskReminder, TaskReminderCreate, TaskReminderUpdate } from './types.js';

// Re-export happening reminder functions
export {
    getHappeningReminders,
    createHappeningReminder,
    updateHappeningReminder,
    deleteHappeningReminder,
    deleteHappeningReminders,
} from './happening-reminders.js';
export type { HappeningReminder, HappeningReminderCreate, HappeningReminderUpdate } from './types.js';

// Re-export completion reminder functions
export {
    getCompletionReminders,
    createCompletionReminder,
    updateCompletionReminder,
    deleteCompletionReminder,
    deleteCompletionReminders,
    getProfileRemindersForHappening,
    deleteProfileRemindersForHappening,
    getCombinedRemindersForHappening,
    deleteCombinedRemindersForHappening,
    getPostShowRemindersForHappening,
    deletePostShowRemindersForHappening,
    queueImmediateCompletionReminder,
} from './completion-reminders.js';
export type { DeleteRemindersOptions } from './completion-reminders.js';
export type { CompletionReminder, CompletionReminderCreate, CompletionReminderUpdate } from './types.js';

// Re-export people functions
export { getPerson, getPersonByProfileToken } from './people.js';

// Re-export account functions
export { getAccount, updateAccount, validateAccountToken } from './accounts.js';
export type { Account, AccountUpdate } from './types.js';

// Re-export program functions
export {
    getProgram,
    getProgramBySlug,
    listPrograms,
    createProgram,
    updateProgram,
    deleteProgram,
    listProgramPlaces,
    addProgramPlace,
    removeProgramPlace,
    setProgramPlaces,
} from './programs.js';
export type { Program, ProgramCreate, ProgramUpdate, ProgramPlace, ProgramPlaceCreate } from './types.js';

// Re-export tag functions
export {
    getTag,
    listTags,
    createTag,
    updateTag,
    deleteTag,
    listHappeningTags,
    setHappeningTags,
    countHappeningsPerTag,
} from './tags.js';
export { TAG_COLORS } from './types.js';
export {
    parseTimecode,
    formatTimecode,
    sanitizeShowSets,
    showSetName,
    withSetEnds,
    findSetForPerson,
    parseRunningOrder,
    buildRunningOrderText,
    soundCloudUrlAt,
    showPageUrlForSet,
} from './show-sets.js';
export type { ShowSet, ParsedRunningOrder } from './show-sets.js';
export type { Tag, TagColor, TagCreate, TagUpdate, HappeningTag } from './types.js';

// Re-export staffing functions
export {
    getStaffRole,
    listStaffRoles,
    createStaffRole,
    updateStaffRole,
    deleteStaffRole,
    listPersonRoles,
    listPersonRolesBatch,
    setPersonRoles,
} from './staff-roles.js';
export {
    listAvailability,
    createAvailability,
    deleteAvailability,
    setAvailability,
} from './staff-availability.js';
export {
    listHappeningRoleRequirements,
    listHappeningRoleRequirementsBatch,
    setHappeningRoleRequirements,
} from './happening-role-requirements.js';
export {
    listPlaceRoleDefaults,
    setPlaceRoleDefaults,
    listPlaceRoleDayDefaults,
    setPlaceRoleDayDefaults,
} from './places.js';
export {
    listProgramShifts,
    createShift,
    deleteShift,
    updateShift,
    deleteProgramShifts,
    assignPersonToShift,
    unassignShift,
    confirmAssignments,
    reopenShiftAssignment,
    reopenAssignments,
    clearProgramAssignments,
    generateShifts,
    autoAssignStaff,
    getStaffScheduleByToken,
    getProgramTimetableByToken,
    ensureProgramTimetableToken,
} from './staff-shifts.js';
export type {
    GenerateShiftsResult,
    AutoAssignResult,
    StaffSchedule,
    StaffScheduleShift,
    StaffScheduleProgramme,
    StaffingContact,
    ProgramTimetable,
    TimetablePerformance,
} from './staff-shifts.js';
export type {
    StaffRole,
    StaffRoleCreate,
    StaffRoleUpdate,
    PersonRole,
    StaffAvailability,
    StaffAvailabilityCreate,
    PlaceRoleDefault,
    PlaceRoleDayDefault,
    HappeningRoleRequirement,
    HappeningRoleRequirementInput,
    StaffShift,
    StaffShiftSource,
    StaffAssignment,
    StaffAssignmentStatus,
    StaffSchedulingConstraints,
    StaffShiftWithAssignment,
} from './types.js';

// Re-export Collective Selection functions
export {
    computeSuggestedMaxPicks,
    getSelection,
    listSelections,
    createSelection,
    updateSelection,
    deleteSelection,
    listSelectionVoters,
    setSelectionVoters,
    listRecusals,
    addRecusal,
    removeRecusal,
    recusedHappeningIds,
    listRounds,
    getRound,
    createRound,
    updateRound,
    transitionRound,
    getVoterBallot,
    ensureBallot,
    saveBallotEntries,
    validateBallotEntries,
    submitBallot,
    getEffectiveShortlist,
    listRoundBallots,
    getRoundProgress,
    listIndividualPicks,
    setIndividualPick,
    removeIndividualPick,
    logSelectionAudit,
    listSelectionAudit,
    tallyNominations,
    cutoffPreview,
    revealNominateRound,
    recutShortlist,
    revealAllocateRound,
    tallyAllocateRound,
    SelectionValidationError,
} from './selections.js';
export {
    upsertListen,
    deleteListen,
    listListens,
    getCoverage,
    countOrphans,
} from './listens.js';
export type {
    Selection,
    SelectionState,
    SelectionScope,
    SelectionInput,
    SelectionCreate,
    SelectionUpdate,
    SelectionVoter,
    SelectionRecusal,
    SelectionRecusalCreate,
    SelectionRound,
    RoundKind,
    RoundState,
    RoundConfig,
    NominateRoundConfig,
    AllocateRoundConfig,
    SelectionRoundInput,
    SelectionRoundCreate,
    SelectionRoundUpdate,
    CutoffRule,
    TallyRow,
    SelectionBallot,
    BallotStatus,
    SelectionBallotEntry,
    BallotEntryInput,
    SelectionBallotWithEntries,
    SelectionIndividualPick,
    SelectionIndividualPickCreate,
    SelectionAuditEvent,
    SelectionAuditEventCreate,
    Listen,
    ListenSource,
    ListenUpsert,
    CoverageRow,
} from './types.js';

// Re-export API key functions
export {
    listApiKeys,
    getApiKey,
    generateApiKey,
    validateApiKey,
    revokeApiKey,
    updateApiKey,
} from './api-keys.js';
export type { ApiKey, ApiKeyCreate, ApiKeyWithPlainText, ApiKeyValidationResult } from './types.js';

// Re-export email functions for admin use
export {
    initEmailService,
    updateEmailOrganizationName,
    sendHappeningCompletionReminder,
    sendProfileCompletionReminder,
    sendHappeningStubLinkEmail,
    sendHappeningReminder,
    sendTaskReminder,
    sendCombinedArtistShowInvite,
    sendPostShowEmail,
    getEmailBrandingFromAccount,
} from './email.js';
export type { EmailBranding } from './email.js';

// Re-export email template functions and types
export {
    renderEmailTemplate,
    validateTemplate,
    getEmailBrandingFromAccount as getExtendedEmailBranding,
} from './email-renderer.js';
export type { EmailTemplateOverride, RenderedEmail } from './email-renderer.js';

export {
    getEmailTemplateSchema,
    getAllEmailTemplateTypes,
    getAllEmailTemplateSchemas,
    EMAIL_TEMPLATE_REGISTRY,
} from './email-templates.js';
export type {
    EmailType,
    EmailTemplateSchema,
    TemplateVariable,
    VariableDataType,
} from './email-templates.js';

// Re-export place equipment functions
export {
    listPlaceEquipment,
    getPlaceEquipmentSummary,
    listPlacesForThing,
    addPlaceEquipment,
    updatePlaceEquipment,
    removePlaceEquipment,
    bulkAddPlaceEquipment,
    getAvailableEquipmentForPlace,
} from './place-equipment.js';
export type {
    PlaceEquipment,
    PlaceEquipmentCreate,
    PlaceEquipmentUpdate,
    PlaceEquipmentAvailabilityType,
    PlaceEquipmentSummary,
    ThingWithPlaceContext,
} from './types.js';

// Re-export people participants functions
export {
    listPeopleParticipants,
    listPeopleParticipantsBatch,
    addPeopleParticipant,
    removePeopleParticipant,
    removeAllPeopleParticipants,
    getPeopleParticipantsByRole,
    getPeopleParticipantsByRoles,
} from './people-participants.js';
export type {
    PeopleParticipant,
    PeopleParticipantCreate,
} from './types.js';

// Re-export completion notification roles functions
export {
    getCompletionNotificationRoles,
    setCompletionNotificationRoles,
    addCompletionNotificationRole,
    removeCompletionNotificationRole,
    deleteCompletionNotificationRoles,
    hasCompletionNotificationRoles,
    getCompletionNotificationRoleNames,
} from './completion-notification-roles.js';
export type {
    CompletionNotificationRole,
    CompletionNotificationRoleCreate,
    CompletionNotificationEntityType,
    PendingCompletionNotification,
    PendingCompletionNotificationCreate,
} from './types.js';

// Re-export completion notification sender functions
export {
    sendCompletionNotifications,
    sendPersonCompletionNotifications,
    sendHappeningCompletionNotifications,
} from './completion-notification-sender.js';

// Re-export completion notification email function
export { sendCompletionNotificationEmail } from './email.js';

// Re-export pending completion notifications functions
export {
    createPendingCompletionNotification,
    getPendingCompletionNotifications,
    markPendingNotificationSent,
    markPendingNotificationFailed,
} from './pending-completion-notifications.js';

// Re-export login session functions
export {
    createUserSession,
    getUserSessionByTokenHash,
    getUserSessionByPreviousTokenHash,
    rotateUserSessionToken,
    touchUserSession,
    revokeUserSession,
    revokeAllUserSessions,
} from './user-sessions.js';
export type { UserSession as LoginSession, UserSessionCreate as LoginSessionCreate } from './user-sessions.js';

// Re-export auth events functions
export {
    createAuthEvent,
    listAuthEvents,
    getRecentFailedAttempts,
    getRecentFailedAttemptsByIp,
    deleteOldAuthEvents,
} from './auth-events.js';
export type {
    AuthEvent,
    AuthEventCreate,
    AuthEventType,
    AuthFailureReason,
    LoginResult,
} from './types.js';
